var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};

// src/rich/kind.ts
function createNumericKind(def) {
  return {
    dataType: "number",
    align: "right",
    isNumeric: true,
    ...def
  };
}
function createStringKind(def) {
  return {
    dataType: "string",
    align: "left",
    isNumeric: false,
    ...def
  };
}
var LB = 0.45359237;
var IN = 25.4;
var FT = 304.8;
var KindDefinitions = {
  boolean: {
    title: "Boolean",
    dataType: "boolean",
    align: "left",
    isNumeric: false
  },
  integer: createNumericKind({
    title: "Integer"
  }),
  unitless: createNumericKind({
    title: "Unitless"
  }),
  percent: createNumericKind({
    title: "Percent"
  }),
  money: createNumericKind({
    title: "Amount"
  }),
  money_per_mass: createNumericKind({
    // _ is placeholder for actual currency symbol
    title: "Money per Mass",
    defaultUnit: (metric) => metric ? "_/kg" : "_/lb",
    unitOptions: {
      "_/g": 1 / 1e-3,
      "_/kg": 1,
      "_/lb": 1 / LB,
      "_/oz": 1 / (LB / 16)
    }
  }),
  money_per_hour: createNumericKind({
    // _ is placeholder for actual currency symbol
    title: "Money per Mass",
    defaultUnit: () => "_/h",
    unitOptions: { "_/h": 1 }
  }),
  length: createNumericKind({
    title: "Length",
    defaultUnit: (metric) => metric ? "mm" : "in",
    unitOptions: { mm: 1, cm: 10, m: 1e3, in: IN, ft: FT }
  }),
  volume: createNumericKind({
    title: "Volume",
    defaultUnit: (metric) => metric ? "cm\xB3" : "in\xB3",
    unitOptions: {
      "mm\xB3": 1,
      "cm\xB3": 10 ** 3,
      l: 100 ** 3,
      "m\xB3": 1e3 ** 3,
      "in\xB3": IN ** 3,
      "ft\xB3": FT ** 3
      // 'gal': IN**3 * 231, // US gallon = 231 in³ ~ 3.785 l
      // 'gal': 4.54609e6, // Imperial gallon exactly 4.54609 liters
    }
  }),
  mass: createNumericKind({
    title: "Mass",
    defaultUnit: (metric) => metric ? "kg" : "lb",
    unitOptions: { kg: 1, g: 1e-3, lb: LB, oz: LB / 16 }
  }),
  angle: createNumericKind({
    title: "Angle",
    defaultUnit: () => "deg",
    unitOptions: { deg: 1, rad: 180 / Math.PI }
  }),
  current: createNumericKind({
    title: "Current",
    defaultUnit: () => "A",
    unitOptions: { kA: 1e3, A: 1, mA: 1e-3, \u00B5A: 1e-6 }
  }),
  watts: createNumericKind({
    title: "Watts",
    defaultUnit: () => "W",
    unitOptions: { W: 1, kW: 1e3, MW: 1e6 }
  }),
  volts: createNumericKind({
    title: "Volts",
    defaultUnit: () => "V",
    unitOptions: { V: 1, kV: 1e3 }
  }),
  gas_flow: createNumericKind({
    title: "Gas Flow Rate",
    defaultUnit: (metric) => metric ? "m\xB3/h" : "ft\xB3/min",
    unitOptions: { "m\xB3/h": 1, "ft\xB3/min": 1.69901, "l/s": 3.6 }
  }),
  feedrate: createNumericKind({
    title: "Feedrate",
    defaultUnit: (metric) => metric ? "mm/min" : "in/min",
    unitOptions: { "mm/min": 1, "in/min": IN }
  }),
  acceleration: createNumericKind({
    title: "Acceleration",
    defaultUnit: (metric) => metric ? "mm/s\xB2" : "in/s\xB2",
    unitOptions: {
      "m/s\xB2": 1,
      "mm/s\xB2": 1e-3,
      "ft/s\xB2": FT / 1e3,
      "in/s\xB2": IN / 1e3
    }
  }),
  rotation_speed: createNumericKind({
    title: "Rotation Speed",
    defaultUnit: () => "rpm",
    unitOptions: { rpm: 1, rps: 1 / 60 }
  }),
  surface_speed: createNumericKind({
    title: "Surface Speed",
    defaultUnit: (metric) => metric ? "m/min" : "ft/min",
    unitOptions: { "m/min": 1, "ft/min": FT / 1e3 }
  }),
  chip_load: createNumericKind({
    title: "Chip Load",
    defaultUnit: (metric) => metric ? "mm/tooth" : "in/tooth",
    unitOptions: { "mm/tooth": 1, "in/tooth": IN }
  }),
  hardness: createNumericKind({
    title: "Hardness",
    defaultUnit: () => "hb",
    // Only one hardness scale is provided as conversion between different hardness scales is non-linear.
    unitOptions: { hb: 1 }
  }),
  density: createNumericKind({
    title: "Density",
    defaultUnit: (metric) => metric ? "kg/m\xB3" : "lb/ft\xB3",
    unitOptions: {
      "kg/m\xB3": 1,
      "g/cm\xB3": 1e3,
      "lb/ft\xB3": LB / (FT / 1e3) ** 3,
      "lb/in\xB3": LB / (IN / 1e3) ** 3
    }
  }),
  duration: createNumericKind({
    title: "Duration",
    defaultUnit: () => "s",
    unitOptions: { s: 1, min: 60, h: 3600 }
  }),
  text: createStringKind({
    title: "Text"
  }),
  enum: createStringKind({
    title: "Enumeration"
  }),
  asset: createStringKind({
    title: "Asset"
  }),
  object: createStringKind({
    title: "Object"
  }),
  array: createStringKind({
    title: "Array"
  }),
  datetime: createStringKind({
    title: "Datetime"
  }),
  guid: createStringKind({
    title: "GUID"
  }),
  url: createStringKind({
    title: "URL"
  }),
  segments: createStringKind({
    title: "Segments"
  }),
  group: {
    title: "Group",
    dataType: "boolean",
    // expanded or not
    align: "left",
    isNumeric: false
  }
};
function getKindDataType(kind) {
  return KindDefinitions[kind].dataType;
}
function getKindAlign(kind) {
  return KindDefinitions[kind].align;
}
function isKindNumeric(kind) {
  return KindDefinitions[kind].isNumeric;
}
function getDefaultUnit(kind, metric) {
  if (!isKindNumeric(kind)) {
    return null;
  }
  const def = KindDefinitions[kind];
  return def.defaultUnit ? def.defaultUnit(metric) : null;
}
function getUnitOptions(kind) {
  return KindDefinitions[kind].unitOptions;
}
function getUnitConversionFactor(kind, fromUnit, toUnit) {
  if (!isKindNumeric(kind)) {
    return void 0;
  }
  const options = getUnitOptions(kind);
  if (!options) {
    return void 0;
  }
  if (fromUnit == toUnit) {
    return void 0;
  }
  if (!(fromUnit in options) || !(toUnit in options)) {
    throw new Error(`Unsupported conversion units for ${kind}: ${fromUnit} or ${toUnit}`);
  }
  return options[fromUnit] / options[toUnit];
}
function getDefaultConversionFactor(kind, toMetric) {
  if (!isKindNumeric(kind)) {
    return void 0;
  }
  const fromUnit = getDefaultUnit(kind, !toMetric);
  const toUnit = getDefaultUnit(kind, toMetric);
  if (!fromUnit || !toUnit) {
    return void 0;
  }
  return getUnitConversionFactor(kind, fromUnit, toUnit);
}
function kindEqual(kind, a, b, eps = 1e-9) {
  if (isKindNumeric(kind)) {
    return Math.abs(a - b) <= eps;
  }
  return a === b;
}

// src/rich/field-spec.ts
function getEnumValuesFromMap(map) {
  return Object.entries(map).map(([value, title]) => ({
    value,
    title
  }));
}
function getEnumTitle(spec, value) {
  return spec.enumValues?.find((entry) => entry.value == value)?.title || value;
}
function groupFieldSpecs(items) {
  const result = [];
  let currentGroup = null;
  let groupAdded = false;
  for (const item of items) {
    if (item.kind === "group") {
      currentGroup = { ...item, children: [] };
      groupAdded = false;
    } else {
      if (item.inGroup !== false && currentGroup) {
        if (!groupAdded) {
          result.push(currentGroup);
          groupAdded = true;
        }
        currentGroup.children.push(item);
      } else {
        result.push(item);
        currentGroup = null;
        groupAdded = false;
      }
    }
  }
  return result;
}
function makeFieldSpecMap(specs) {
  return specs.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
}
function matchesCondition(value, condition) {
  if (condition.in !== void 0) {
    if (!condition.in.includes(value)) {
      return false;
    }
  }
  if (condition.notIn !== void 0) {
    if (condition.notIn.includes(value)) {
      return false;
    }
  }
  if (condition.eq !== void 0) {
    if (value !== condition.eq) {
      return false;
    }
  }
  if (condition.notEq !== void 0) {
    if (value === condition.notEq) {
      return false;
    }
  }
  return true;
}
function isFieldEnabled(spec, data) {
  if (!spec.enabledWhen) {
    return true;
  }
  for (const [dependencyField, condition] of Object.entries(spec.enabledWhen)) {
    const dependencyValue = data[dependencyField];
    if (!matchesCondition(dependencyValue, condition)) {
      return false;
    }
  }
  return true;
}
function filterEnabledFieldSpecs(data, specs) {
  return specs.filter((m) => isFieldEnabled(m, data));
}
function buildUsedByMap(specs) {
  const map = {};
  for (const { id, dependencies } of specs) {
    for (const dep of dependencies || []) {
      if (!map[dep]) {
        map[dep] = [];
      }
      map[dep].push(id);
    }
  }
  return map;
}
function prepareAssetData(source, specs) {
  const enabledSpecs = filterEnabledFieldSpecs(source, specs);
  const data = {};
  for (const spec of enabledSpecs) {
    data[spec.id] = source[spec.id];
  }
  return data;
}
function getDefaultValue(spec, metric) {
  let value = spec.defaultValue;
  if (value === void 0) {
    value = metric ? spec.defaultValueMM : spec.defaultValueIN;
  }
  if (value === void 0 && isKindNumeric(spec.kind)) {
    value = metric ? spec.defaultValueIN : spec.defaultValueMM;
    if (value !== void 0) {
      value = (getDefaultConversionFactor(spec.kind, metric) || 1) * value;
    }
  }
  if (value === void 0) {
    if (spec.required && !spec.compute) {
      throw new Error(`No default value for required property '${spec.title}'`);
    }
  }
  return value;
}
function getDefaults(specs, metric, initial = {}) {
  const data = { ...initial };
  for (const spec of specs) {
    if (!spec.id) {
      continue;
    }
    if (data[spec.id] !== void 0) {
      continue;
    }
    data[spec.id] = getDefaultValue(spec, metric);
  }
  for (const spec of specs) {
    if (spec.compute) {
      if (data[spec.id] !== void 0) {
        continue;
      }
      data[spec.id] = spec.compute(data);
    }
  }
  return data;
}
function convertToUnitSystem(data, specs, toMetric) {
  const specmap = makeFieldSpecMap(filterEnabledFieldSpecs(data, specs));
  for (const key of Object.keys(data)) {
    const spec = specmap[key];
    if (!spec) {
      continue;
    }
    const factor = getDefaultConversionFactor(spec.kind, toMetric);
    if (factor) {
      data[key] *= factor;
    }
  }
  if ("unit" in data) {
    data.unit = toMetric ? "mm" : "in";
  }
}

// src/rich/rich-type.ts
var RichType = class {
  static getFieldSpec() {
    return Object.keys(this).filter((key) => key.endsWith("_spec")).map((specsKey) => {
      const id = specsKey.replace("_spec", "");
      const specs = this[specsKey];
      return { id, ...specs };
    });
  }
  static getFieldSpecMap() {
    return Object.keys(this).filter((key) => key.endsWith("_spec")).reduce((map, key) => {
      const property = key.replace("_spec", "");
      map[property] = this[key];
      return map;
    }, {});
  }
  static getDefaults(metric) {
    return getDefaults(this.getFieldSpec(), metric);
  }
};

// src/fusion/type_groups.js
var groups = {
  AssemblyItem: [
    "ball end mill",
    "bull nose end mill",
    "flat end mill",
    "face mill",
    "tapered mill",
    "radius mill",
    "chamfer mill",
    "dovetail mill",
    "lollipop mill",
    "slot mill",
    "thread mill",
    "circle segment barrel",
    "circle segment lens",
    "circle segment oval",
    "circle segment taper",
    "form mill",
    "boring bar",
    "counter bore",
    "drill",
    "center drill",
    "spot drill",
    "reamer",
    "counter sink",
    "tap left hand",
    "tap right hand",
    "turning general",
    "turning boring",
    "turning grooving",
    "turning threading",
    "waterjet",
    "laser cutter",
    "plasma cutter",
    "probe",
    "holder",
    "electric arc wire",
    "laser powder",
    "laser wire"
  ],
  Tool: [
    "ball end mill",
    "bull nose end mill",
    "flat end mill",
    "face mill",
    "tapered mill",
    "radius mill",
    "chamfer mill",
    "dovetail mill",
    "lollipop mill",
    "slot mill",
    "thread mill",
    "circle segment barrel",
    "circle segment lens",
    "circle segment oval",
    "circle segment taper",
    "form mill",
    "boring bar",
    "counter bore",
    "drill",
    "center drill",
    "spot drill",
    "reamer",
    "counter sink",
    "tap left hand",
    "tap right hand",
    "turning general",
    "turning boring",
    "turning grooving",
    "turning threading",
    "waterjet",
    "laser cutter",
    "plasma cutter",
    "probe",
    "electric arc wire",
    "laser powder",
    "laser wire"
  ],
  ToolMilling: [
    "flat end mill",
    "ball end mill",
    "bull nose end mill",
    "face mill",
    "tapered mill",
    "radius mill",
    "chamfer mill",
    "dovetail mill",
    "lollipop mill",
    "slot mill",
    "thread mill",
    "circle segment barrel",
    "circle segment lens",
    "circle segment oval",
    "circle segment taper",
    "form mill"
  ],
  ToolEndMill: [
    "ball end mill",
    "bull nose end mill",
    "flat end mill",
    "face mill",
    "tapered mill",
    "radius mill",
    "chamfer mill",
    "dovetail mill",
    "lollipop mill",
    "slot mill",
    "thread mill"
  ],
  ToolCircleSegmentMill: ["circle segment barrel", "circle segment lens", "circle segment oval", "circle segment taper"],
  ToolHoleMaking: [
    "boring bar",
    "counter bore",
    "drill",
    "center drill",
    "spot drill",
    "reamer",
    "counter sink",
    "tap left hand",
    "tap right hand"
  ],
  ToolDrill: ["drill", "center drill", "spot drill"],
  ToolTap: ["tap left hand", "tap right hand"],
  ToolTurning: ["turning general", "turning boring", "turning grooving", "turning threading"],
  ToolCutting: ["waterjet", "laser cutter", "plasma cutter"],
  /*
    "probe": [
      "probe",
    ],
    "holder": [
      "holder",
    ],
  */
  ToolDepositing: ["electric arc wire", "laser powder", "laser wire"]
};
var OtherMills = groups.ToolMilling.filter((t) => !groups.ToolEndMill.includes(t));
var OtherTypes = ["probe", "holder", ...groups.ToolTurning, ...groups.ToolDepositing];
var type_names = {
  "ball end mill": "Ball end mill",
  "bull nose end mill": "Bull nose end mill",
  "flat end mill": "Flat end mill",
  "face mill": "Face mill",
  "tapered mill": "Tapered mill",
  "radius mill": "Radius mill",
  "chamfer mill": "Engrave/Chamfer mill",
  "dovetail mill": "Dovetail mill",
  "lollipop mill": "Lollipop mill",
  "slot mill": "Slot mill",
  "thread mill": "Thread mill",
  "circle segment barrel": "Circle segment barrel",
  "circle segment lens": "Circle segment lens",
  "circle segment oval": "Circle segment oval",
  "circle segment taper": "Circle segment taper",
  "form mill": "Form mill",
  "boring bar": "Boring bar",
  "counter bore": "Counter bore",
  "drill": "Drill",
  "center drill": "Center drill",
  "spot drill": "Spot drill",
  "reamer": "Reamer",
  "counter sink": "Counter sink",
  "tap left hand": "Tap left hand",
  "tap right hand": "Tap right hand",
  "turning general": "Turning general",
  "turning boring": "Turning boring",
  "turning grooving": "Turning grooving",
  "turning threading": "Turning threading",
  "waterjet": "Waterjet",
  "laser cutter": "Laser cutter",
  "plasma cutter": "Plasma cutter",
  "probe": "Probe",
  "holder": "Holder",
  "electric arc wire": "Electric arc wire",
  "laser powder": "Laser powder",
  "laser wire": "Laser wire"
};
var main_groups = [
  groups.ToolMilling,
  groups.ToolHoleMaking,
  groups.ToolTurning,
  ["probe"],
  ["holder"],
  groups.ToolDepositing
];

// src/fusion/fusion_type_fields.json
var fusion_type_fields_default = {
  BMC: [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolTurning"
  ],
  description: [
    "AssemblyItem"
  ],
  "geometry.CSP": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "geometry.DC": [
    "ToolMilling",
    "ToolHoleMaking",
    "probe"
  ],
  "geometry.HAND": [
    "ToolMilling",
    "ToolDrill",
    "boring bar",
    "counter bore",
    "reamer",
    "counter sink"
  ],
  "geometry.LB": [
    "ToolMilling",
    "ToolHoleMaking",
    "probe"
  ],
  "geometry.LCF": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "geometry.NOF": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "geometry.OAL": [
    "ToolMilling",
    "ToolHoleMaking",
    "turning grooving",
    "turning threading"
  ],
  "geometry.SFDM": [
    "ToolEndMill",
    "ToolHoleMaking",
    "circle segment barrel",
    "probe"
  ],
  "geometry.assemblyGaugeLength": [
    "ToolMilling",
    "ToolHoleMaking",
    "probe"
  ],
  "geometry.shoulder-diameter": [
    "ToolEndMill",
    "ToolCircleSegmentMill"
  ],
  "geometry.shoulder-length": [
    "ToolEndMill",
    "ToolHoleMaking"
  ],
  guid: [
    "AssemblyItem"
  ],
  "holder.description": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.gaugeLength": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.guid": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.product-id": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.product-link": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.segments.height": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.segments.lower-diameter": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.segments.upper-diameter": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.type": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.unit": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "holder.vendor": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "post-process.break-control": [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolTurning",
    "probe"
  ],
  "post-process.comment": [
    "Tool"
  ],
  "post-process.diameter-offset": [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolCutting",
    "probe"
  ],
  "post-process.length-offset": [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolCutting",
    "ToolDepositing",
    "probe"
  ],
  "post-process.live": [
    "ToolMilling",
    "ToolHoleMaking",
    "probe"
  ],
  "post-process.manual-tool-change": [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolTurning",
    "ToolCutting",
    "probe"
  ],
  "post-process.number": [
    "Tool"
  ],
  "post-process.turret": [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolTurning",
    "probe"
  ],
  "product-id": [
    "AssemblyItem"
  ],
  "product-link": [
    "AssemblyItem"
  ],
  "shaft.segments.height": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "shaft.segments.lower-diameter": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "shaft.segments.upper-diameter": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "shaft.type": [
    "ToolMilling",
    "ToolHoleMaking"
  ],
  "start-values.presets.f_n": [
    "ToolMilling",
    "ToolDrill",
    "boring bar",
    "counter bore",
    "reamer",
    "counter sink",
    "turning general",
    "turning boring",
    "turning grooving"
  ],
  "start-values.presets.f_z": [
    "ToolMilling",
    "boring bar",
    "counter bore",
    "center drill",
    "spot drill",
    "counter sink"
  ],
  "start-values.presets.guid": [
    "Tool"
  ],
  "start-values.presets.material.category": [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolTurning",
    "ToolCutting",
    "ToolDepositing"
  ],
  "start-values.presets.material.query": [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolTurning",
    "ToolCutting",
    "ToolDepositing"
  ],
  "start-values.presets.material.use-hardness": [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolTurning",
    "ToolCutting",
    "ToolDepositing"
  ],
  "start-values.presets.n": [
    "ToolMilling",
    "ToolHoleMaking",
    "turning general",
    "turning boring",
    "turning threading"
  ],
  "start-values.presets.n_ramp": [
    "ToolMilling"
  ],
  "start-values.presets.name": [
    "Tool"
  ],
  "start-values.presets.ramp-angle": [
    "ToolMilling"
  ],
  "start-values.presets.stepdown": [
    "ToolMilling"
  ],
  "start-values.presets.stepover": [
    "ToolMilling",
    "ToolDepositing"
  ],
  "start-values.presets.tool-coolant": [
    "ToolMilling",
    "ToolHoleMaking",
    "ToolTurning"
  ],
  "start-values.presets.use-stepdown": [
    "ToolMilling"
  ],
  "start-values.presets.use-stepover": [
    "ToolMilling",
    "ToolDepositing"
  ],
  "start-values.presets.v_c": [
    "ToolMilling",
    "ToolHoleMaking",
    "turning general",
    "turning boring",
    "turning grooving"
  ],
  "start-values.presets.v_f": [
    "ToolMilling",
    "ToolCutting",
    "ToolDepositing",
    "boring bar",
    "counter bore",
    "center drill",
    "spot drill",
    "counter sink",
    "turning general",
    "turning boring"
  ],
  "start-values.presets.v_f_leadIn": [
    "ToolMilling",
    "ToolCutting",
    "ToolDepositing",
    "boring bar",
    "counter bore",
    "center drill",
    "spot drill",
    "counter sink",
    "turning general",
    "turning boring",
    "probe"
  ],
  "start-values.presets.v_f_leadOut": [
    "ToolMilling",
    "ToolCutting",
    "ToolDepositing",
    "boring bar",
    "counter bore",
    "center drill",
    "spot drill",
    "counter sink",
    "turning general",
    "turning boring"
  ],
  "start-values.presets.v_f_plunge": [
    "ToolMilling",
    "ToolDrill",
    "ToolDepositing",
    "boring bar",
    "counter bore",
    "reamer",
    "counter sink"
  ],
  "start-values.presets.v_f_ramp": [
    "ToolMilling",
    "boring bar",
    "counter bore",
    "center drill",
    "spot drill",
    "counter sink"
  ],
  "start-values.presets.v_f_transition": [
    "ToolMilling",
    "boring bar",
    "counter bore",
    "center drill",
    "spot drill",
    "counter sink"
  ],
  type: [
    "AssemblyItem"
  ],
  unit: [
    "AssemblyItem"
  ],
  vendor: [
    "AssemblyItem"
  ],
  "geometry.RE": [
    "bull nose end mill",
    "face mill",
    "tapered mill",
    "radius mill",
    "dovetail mill",
    "slot mill",
    "circle segment lens",
    "turning general",
    "turning boring",
    "turning grooving"
  ],
  "geometry.DCX": [
    "face mill"
  ],
  "geometry.TA": [
    "face mill",
    "tapered mill",
    "chamfer mill",
    "dovetail mill",
    "circle segment taper",
    "center drill"
  ],
  "geometry.upper-radius": [
    "face mill",
    "circle segment barrel",
    "circle segment taper"
  ],
  "tapered-type": [
    "tapered mill"
  ],
  "geometry.tip-length": [
    "radius mill",
    "center drill"
  ],
  "geometry.tip-diameter": [
    "chamfer mill",
    "circle segment taper",
    "center drill",
    "spot drill",
    "counter sink"
  ],
  "geometry.NT": [
    "thread mill"
  ],
  "geometry.TP": [
    "ToolTap",
    "thread mill",
    "turning threading"
  ],
  "geometry.thread-profile-angle": [
    "thread mill",
    "turning threading"
  ],
  "geometry.axial-distance": [
    "circle segment barrel"
  ],
  "geometry.lower-radius": [
    "ToolCircleSegmentMill"
  ],
  "geometry.profile-radius": [
    "circle segment barrel",
    "circle segment oval",
    "circle segment taper"
  ],
  "geometry.SIG": [
    "ToolDrill",
    "counter sink"
  ],
  "start-values.presets.use-feed-per-revolution": [
    "ToolDrill",
    "reamer",
    "counter sink",
    "turning general",
    "turning boring",
    "turning grooving"
  ],
  "start-values.presets.v_f_retract": [
    "ToolDrill",
    "reamer",
    "counter sink"
  ],
  "start-values.presets.f_n_retract": [
    "ToolDrill",
    "reamer",
    "counter sink"
  ],
  "geometry.INSD": [
    "turning general",
    "turning boring"
  ],
  "geometry.RA": [
    "turning general",
    "turning boring"
  ],
  "geometry.S": [
    "ToolTurning"
  ],
  "geometry.SC": [
    "ToolTurning"
  ],
  "geometry.SCTY": [
    "ToolTurning"
  ],
  "geometry.SIZE_SPECIFICATION_MODE": [
    "turning general",
    "turning boring"
  ],
  "geometry.TC": [
    "ToolTurning"
  ],
  "holder.CW": [
    "turning general",
    "turning boring"
  ],
  "holder.H": [
    "turning general",
    "turning grooving",
    "turning threading"
  ],
  "holder.HAND": [
    "ToolTurning"
  ],
  "holder.LH": [
    "ToolTurning"
  ],
  "holder.MTP": [
    "ToolTurning"
  ],
  "holder.OAL": [
    "ToolTurning"
  ],
  "holder.THSC": [
    "ToolTurning"
  ],
  "holder.W": [
    "ToolTurning"
  ],
  "holder.tool_roundShank": [
    "turning general",
    "turning grooving",
    "turning threading"
  ],
  "post-process.compensation-offset": [
    "ToolTurning"
  ],
  "setup.HAND": [
    "ToolTurning"
  ],
  "setup.angle": [
    "ToolTurning"
  ],
  "setup.compensation": [
    "ToolTurning"
  ],
  "start-values.presets.depth-of-cut": [
    "turning general",
    "turning boring"
  ],
  "start-values.presets.f_n_leadIn": [
    "turning general",
    "turning boring",
    "turning grooving"
  ],
  "start-values.presets.f_n_leadOut": [
    "turning general",
    "turning boring",
    "turning grooving"
  ],
  "start-values.presets.use-constant-surface-speed": [
    "turning general",
    "turning boring",
    "turning grooving"
  ],
  "start-values.presets.use-depth-of-cut": [
    "turning general",
    "turning boring"
  ],
  "geometry.LH": [
    "turning grooving"
  ],
  "geometry.tool_grooveWidth": [
    "turning grooving"
  ],
  "geometry.tool_insertWidth": [
    "turning grooving",
    "turning threading"
  ],
  "setup.tool_grooveCompOppositeEdge": [
    "turning grooving"
  ],
  "geometry.maximum-thread-pitch": [
    "turning threading"
  ],
  "geometry.minimum-thread-pitch": [
    "turning threading"
  ],
  "geometry.thread-tip-radius": [
    "turning threading"
  ],
  "geometry.thread-tip-type": [
    "turning threading"
  ],
  "geometry.tool_internalThread": [
    "turning threading"
  ],
  "geometry.CW": [
    "ToolCutting"
  ],
  "geometry.JET_HEAD_CLEARANCE": [
    "ToolCutting"
  ],
  "geometry.JET_NOZZLE_DIAMETER": [
    "ToolCutting"
  ],
  "start-values.presets.abrasiveFlowRate": [
    "waterjet"
  ],
  "start-values.presets.cutHeight": [
    "ToolCutting"
  ],
  "start-values.presets.machineQualityControl": [
    "ToolCutting"
  ],
  "start-values.presets.pierceTime": [
    "ToolCutting"
  ],
  "start-values.presets.pressure": [
    "ToolCutting"
  ],
  "start-values.presets.assistGas": [
    "laser cutter",
    "plasma cutter"
  ],
  "start-values.presets.cutPower": [
    "laser cutter",
    "plasma cutter"
  ],
  "start-values.presets.pierceHeight": [
    "laser cutter",
    "plasma cutter"
  ],
  "start-values.presets.piercePower": [
    "laser cutter",
    "plasma cutter"
  ],
  "start-values.presets.v_f_link": [
    "probe"
  ],
  "start-values.presets.v_f_measure": [
    "probe"
  ],
  gaugeLength: [
    "holder"
  ],
  "segments.height": [
    "holder"
  ],
  "segments.lower-diameter": [
    "holder"
  ],
  "segments.upper-diameter": [
    "holder"
  ],
  "start-values.presets.BEAD_WIDTH": [
    "ToolDepositing"
  ],
  "start-values.presets.DEPOSITING_CURRENT": [
    "electric arc wire"
  ],
  "start-values.presets.DEPOSITING_SHIELD_GAS_FLOW_RATE": [
    "ToolDepositing"
  ],
  "start-values.presets.DEPOSITING_VOLTAGE": [
    "electric arc wire"
  ],
  "start-values.presets.LAYER_THICKNESS": [
    "ToolDepositing"
  ],
  "start-values.presets.STAND_OFF_DISTANCE": [
    "ToolDepositing"
  ],
  "start-values.presets.USE_PRESET_PROGRAM": [
    "electric arc wire"
  ],
  "start-values.presets.WIRE_FEED": [
    "electric arc wire",
    "laser wire"
  ],
  "start-values.presets.DEPOSITING_AUXILIARY_GAS_FLOW_RATE": [
    "laser powder",
    "laser wire"
  ],
  "start-values.presets.DEPOSITING_CARRIER_GAS_FLOW_RATE": [
    "laser powder"
  ],
  "start-values.presets.DEPOSITING_POWER": [
    "laser powder",
    "laser wire"
  ],
  "start-values.presets.POWDER_FLOW_RATE": [
    "laser powder"
  ],
  GRADE: [
    "form mill"
  ],
  "geometry.profile.arc": [
    "form mill"
  ],
  "geometry.profile.ccw": [
    "form mill"
  ],
  "geometry.tip-offset": [
    "form mill"
  ],
  "start-values.presets.description": [
    "form mill"
  ],
  "geometry.thread-tip-width": [
    "turning threading"
  ],
  "geometry.EPSR": [
    "turning general"
  ],
  "geometry.NON_CUTTING_EDGE_LENGTH": [
    "turning general"
  ]
};

// src/fusion/fusion_fields.ts
var fusion_library_version = 26;
var unitEnumValues = getEnumValuesFromMap({ millimeters: "Millimeters", inches: "Inches" });
var fusion_base_fields = [
  // General
  {
    group: "general",
    id: "type",
    csv_id: "tool_type",
    json_id: "type",
    hsm_id: "type",
    tp_id: "typeId",
    // TODO: needs mapping
    title: "Type",
    kind: "enum",
    required: true,
    defaultValue: "unknown",
    enumValues: getEnumValuesFromMap(type_names)
  },
  {
    group: "general",
    id: "unit",
    csv_id: "tool_unit",
    json_id: "unit",
    hsm_id: "unit",
    tp_id: "unit",
    title: "Unit",
    kind: "enum",
    required: true,
    defaultValue: "millimeters",
    enumValues: unitEnumValues
  },
  {
    group: "general",
    id: "description",
    csv_id: "tool_description",
    json_id: "description",
    hsm_id: "description",
    hsm_iselem: true,
    title: "Description",
    kind: "text",
    required: true,
    defaultValue: ""
  },
  {
    group: "general",
    id: "vendor",
    csv_id: "tool_vendor",
    json_id: "vendor",
    hsm_id: "manufacturer",
    hsm_iselem: true,
    title: "Vendor",
    kind: "text",
    required: true,
    defaultValue: ""
  },
  {
    group: "general",
    id: "productId",
    csv_id: "tool_productId",
    json_id: "product-id",
    hsm_id: "product-id",
    hsm_iselem: true,
    title: "Product ID",
    kind: "text",
    required: true,
    defaultValue: ""
  },
  {
    group: "general",
    id: "productLink",
    csv_id: "tool_productLink",
    json_id: "product-link",
    hsm_id: "product-link",
    hsm_iselem: true,
    title: "Product Link",
    kind: "text",
    required: true,
    defaultValue: ""
  },
  {
    group: "general",
    id: "guid",
    comment: "not in CSV",
    title: "GUID",
    json_id: "guid",
    hsm_id: "guid",
    tp_id: "guid",
    kind: "guid",
    hidden: true
  },
  {
    group: "general",
    id: "sourceSystem",
    title: "Source System",
    kind: "enum",
    required: true,
    defaultValue: "fusion",
    hidden: true,
    enumValues: [{ value: "fusion", title: "Autodesk Fusion" }]
  }
];
var fusion_tool_fields = [
  // Cutter
  {
    group: "cutter",
    id: "geometry_clockwise",
    csv_id: "tool_clockwise",
    json_id: "geometry.HAND",
    hsm_id: "motion.clockwise",
    tp_id: "clockwiseRotation",
    title: "Clockwise Spindle Rotation",
    kind: "boolean",
    defaultValue: true
  },
  {
    group: "cutter",
    id: "geometry_numberOfFlutes",
    csv_id: "tool_numberOfFlutes",
    json_id: "geometry.NOF",
    hsm_id: "body.number-of-flutes",
    tp_id: "fluteCount",
    title: "Number of Flutes",
    kind: "integer",
    defaultValue: 3
  },
  {
    group: "cutter",
    id: "material",
    csv_id: "tool_material",
    json_id: "BMC",
    hsm_id: "material.name",
    title: "Material",
    kind: "enum",
    defaultValue: "carbide",
    enumValues: getEnumValuesFromMap({
      unspecified: "Unspecified",
      carbide: "Carbide",
      "ti coated": "Ti Coated",
      ceramics: "Ceramic",
      hss: "HSS"
    })
  },
  // Geometry
  {
    group: "cutter_geometry",
    mdes_id: "CuttingDiameter",
    iso_id: "DC",
    id: "geometry_cuttingDiameter",
    csv_id: "tool_diameter",
    json_id: "geometry.DC",
    hsm_id: "body.diameter",
    tp_id: "nominalDiameter",
    title: "Diameter",
    kind: "length",
    defaultValue: 6
    // mm
  },
  /* From v33
  {
    group: "cutter_geometry",
    mdes_id: "CuttingDiameterMaximum",
    iso_id: "DCX",
    id: "geometry_cuttingDiameterMaximum",
    csv_id: "tool_maximumCuttingDiameter",
    hsm_key: "tool_maximumCuttingDiameter",
    json_id: "geometry.DCX",
    //tp_id: ,
    title: "Maximum Diameter",
    kind: "length",
    defaultValue: 6.0, // mm
  },
  */
  {
    group: "cutter_geometry",
    id: "geometry_cornerRadius",
    //id: "insert_cornerRadius", // for turning case
    csv_id: "tool_cornerRadius",
    json_id: "geometry.RE",
    hsm_id: "body.corner-radius",
    tp_id: "cornerRadius",
    // diameter/2 for ball
    title: "Corner Radius",
    kind: "length",
    defaultValue: 0.75
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_shaftDiameter",
    csv_id: "tool_shaftDiameter",
    json_id: "geometry.SFDM",
    hsm_id: "body.shaft-diameter",
    tp_id: "shankDiameter",
    //
    title: "Shaft Diameter",
    kind: "length",
    defaultValue: 6
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_overallLength",
    //id: "holder.overallLength", // for turning case
    csv_id: "tool_overallLength",
    json_id: "geometry.OAL",
    hsm_id: "body.overall-length",
    title: "Overall Length",
    kind: "length",
    defaultValue: 36
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_protrudingLength",
    csv_id: "tool_bodyLength",
    json_id: "geometry.LB",
    hsm_id: "body.body-length",
    mdes_id: "ProtrudingLength",
    iso_id: "LPR",
    tp_id: "stickout",
    title: "Length below holder",
    kind: "length",
    defaultValue: 24
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_shoulderLength",
    csv_id: "tool_shoulderLength",
    json_id: "geometry.shoulder-length",
    hsm_id: "body.shoulder-length",
    iso_id: "LH",
    mdes_id: "HeadLength",
    title: "Shoulder Length",
    kind: "length",
    defaultValue: 21
    // mm
  },
  {
    group: "cutter_geometry",
    comment: "Present in some Fusion version. Unclear what it's used for.",
    title: "Shoulder Diameter",
    csv_id: "tool_shoulderDiameter",
    hsm_key: "tool_shoulderDiameter",
    id: "geometry_shoulderDiameter",
    json_id: "geometry.shoulder-diameter",
    kind: "length",
    hidden: true
  },
  {
    group: "cutter_geometry",
    id: "geometry_fluteLength",
    csv_id: "tool_fluteLength",
    json_id: "geometry.LCF",
    hsm_id: "body.flute-length",
    tp_id: "fluteLength",
    //
    title: "Flute Length",
    kind: "length",
    defaultValue: 18
    // mm
  },
  {
    group: "cutter_geometry",
    csv_id: "tool_coolantSupport",
    id: "geometry_coolantSupport",
    json_id: "geometry.CSP",
    hsm_id: "body.coolant-support",
    title: "Coolant Support",
    kind: "boolean",
    // "yes"/"no" in CSV produced by Fusion
    defaultValue: false
    // Not sure if this is currently visible in Fusion anywhere
  },
  {
    group: "cutter_geometry",
    id: "geometry_taperedType",
    csv_id: "tool_taperedType",
    json_id: "tapered-type",
    title: "Tapered Type",
    kind: "enum",
    defaultValue: "tapered_bull_nose"
  },
  {
    group: "cutter_geometry",
    id: "geometry_taperAngle",
    csv_id: "tool_taperAngle",
    json_id: "geometry.TA",
    tp_id: "tipAngle",
    // for tool other than drill, spot drill, counter sink
    title: "Taper Angle",
    kind: "angle",
    defaultValue: 10
    // deg
  },
  {
    group: "cutter_geometry",
    csv_id: "tool_numberOfTeeth",
    json_id: "geometry.NT",
    id: "geometry_numberOfTeeth",
    title: "Number of Teeth",
    kind: "integer",
    defaultValue: 1
  },
  {
    group: "cutter_geometry",
    id: "geometry_threadPitch",
    //id: "insert_threadPitch", // for turning case
    csv_id: "tool_threadPitch",
    json_id: "geometry.TP",
    hsm_id: "body.thread-pitch",
    tp_id: "threadPitch",
    title: "Thread Pitch",
    kind: "length",
    defaultValue: 1
    // mm
  },
  {
    group: "cutter_geometry",
    comment: "Used on both thread mill and turning threading",
    id: "geometry_threadProfileAngle",
    //id: "insert_threadProfileAngle", // for turning case
    json_id: "geometry.thread-profile-angle",
    csv_id: "tool_threadProfileAngle",
    hsm_id: "body.thread-profile-angle",
    title: "Thread Profile Angle",
    kind: "angle",
    defaultValue: 60
    // deg
  },
  {
    group: "cutter_geometry",
    id: "geometry_tipAngle",
    csv_id: "tool_tipAngle",
    json_id: "geometry.SIG",
    tp_id: "tipAngle",
    // for drill, spot drill, counter-sink
    title: "Tip Angle",
    kind: "angle",
    defaultValue: 120
    // deg
  },
  {
    group: "cutter_geometry",
    id: "geometry_tipDiameter",
    csv_id: "tool_tipDiameter",
    json_id: "geometry.tip-diameter",
    tp_id: "bottomDiameter",
    // 'slot mill' has special handling via getBottomDiameter function
    title: "Tip Diameter",
    kind: "length",
    defaultValue: 21
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_tipLength",
    csv_id: "tool_tipLength",
    json_id: "geometry.tip-length",
    title: "Tip Length",
    kind: "length",
    defaultValue: 2
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_tipOffset",
    csv_id: "tool_tipOffset",
    json_id: "geometry.tip-offset",
    title: "Tip Offset",
    kind: "length",
    defaultValue: 0
    // mm
  },
  {
    group: "cutter_geometry",
    csv_id: "tool_axialDistance",
    json_id: "geometry.axial-distance",
    id: "geometry_axialDistance",
    title: "Axial Distance",
    kind: "length",
    defaultValue: 4
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_profileRadius",
    csv_id: "tool_profileRadius",
    json_id: "geometry.profile-radius",
    title: "Profile Radius",
    kind: "length",
    defaultValue: 8
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_upperRadius",
    csv_id: "tool_upperRadius",
    json_id: "geometry.upper-radius",
    title: "Upper Radius",
    kind: "length",
    defaultValue: 1
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_lowerRadius",
    csv_id: "tool_lowerRadius",
    json_id: "geometry.lower-radius",
    title: "Lower Radius",
    kind: "length",
    defaultValue: 1
    // mm
  },
  // Jet tools
  {
    group: "cutter_geometry",
    id: "geometry_nozzleDiameter",
    json_id: "geometry.JET_NOZZLE_DIAMETER",
    csv_id: "tool_nozzleDiameter",
    title: "Nozzle Diameter",
    kind: "length",
    defaultValue: 1
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_kerfWidth",
    json_id: "geometry.CW",
    csv_id: "tool_kerfWidth",
    title: "Kerf Width",
    kind: "length",
    defaultValue: 1
    // mm
  },
  {
    group: "cutter_geometry",
    id: "geometry_headClearance",
    json_id: "geometry.JET_HEAD_CLEARANCE",
    csv_id: "tool_headClearance",
    title: "Head Clearance",
    kind: "length",
    comment: "Not currently shown in Fusion UI. Appears hardcoded to 1.0mm",
    defaultValue: 1,
    // mm
    hidden: true
  },
  // Tool Assembly
  {
    group: "cutter_assembly",
    id: "geometry_assemblyGaugeLength",
    csv_id: "tool_assemblyGaugeLength",
    json_id: "geometry.assemblyGaugeLength",
    hsm_id: "body.assembly-gauge-length",
    title: "Tool Assembly Gauge Length",
    kind: "length"
  },
  // Post
  {
    group: "post",
    id: "post_number",
    csv_id: "tool_number",
    json_id: "post-process.number",
    hsm_id: "nc.number",
    title: "Number",
    kind: "integer",
    defaultValue: 1
  },
  {
    group: "post",
    id: "post_lengthOffset",
    csv_id: "tool_lengthOffset",
    json_id: "post-process.length-offset",
    hsm_id: "nc.length-offset",
    title: "Length Offset",
    kind: "integer",
    defaultValue: 1
  },
  {
    group: "post",
    id: "post_diameterOffset",
    csv_id: "tool_diameterOffset",
    json_id: "post-process.diameter-offset",
    hsm_id: "nc.diameter-offset",
    title: "Diameter Offset",
    kind: "integer",
    defaultValue: 1
  },
  {
    group: "post",
    id: "post_compensationOffset",
    csv_id: "tool_compensationOffset",
    json_id: "post-process.compensation-offset",
    hsm_id: "nc.compensation-offset",
    title: "Compensation Offset",
    kind: "integer",
    defaultValue: 1
  },
  {
    group: "post",
    id: "post_turret",
    csv_id: "tool_turret",
    json_id: "post-process.turret",
    hsm_id: "nc.turret",
    title: "Turret",
    kind: "integer",
    defaultValue: 0
  },
  {
    group: "post",
    id: "post_comment",
    csv_id: "tool_comment",
    json_id: "post-process.comment",
    hsm_id: "comment",
    hsm_iselem: true,
    title: "Comment",
    kind: "text",
    //required: true,
    defaultValue: ""
  },
  {
    group: "post",
    id: "post_manualToolChange",
    csv_id: "tool_manualToolChange",
    json_id: "post-process.manual-tool-change",
    hsm_id: "nc.manual-tool-change",
    title: "Manual Tool Change",
    kind: "boolean",
    defaultValue: false
  },
  {
    group: "post",
    id: "post_live",
    csv_id: "tool_live",
    json_id: "post-process.live",
    hsm_id: "nc.live-tool",
    title: "Live Tool",
    kind: "boolean",
    defaultValue: true
  },
  {
    group: "post",
    id: "post_breakControl",
    csv_id: "tool_breakControl",
    json_id: "post-process.break-control",
    hsm_id: "nc.break-control",
    title: "Break Control",
    kind: "boolean",
    defaultValue: false
  },
  // Shaft
  {
    group: "shaft",
    id: "shaft.segments",
    csv_id: "shaft_segments",
    json_id: "shaft.segments",
    title: "Shaft Segments",
    kind: "segments",
    for_types: ["ToolMilling", "ToolHoleMaking", "probe"]
  },
  {
    group: "shaft",
    title: "",
    comment: "always 'shaft'",
    json_id: "shaft.type",
    defaultValue: "shaft",
    kind: "enum",
    for_types: ["ToolMilling", "ToolHoleMaking", "probe"]
  },
  // Holder
  {
    group: "holder",
    id: "holder.unit",
    comment: "not in CSV - for CSV always same unit as tool",
    json_id: "holder.unit",
    title: "Unit",
    kind: "enum",
    defaultValue: "millimeters",
    enumValues: unitEnumValues,
    for_types: ["ToolMilling", "ToolHoleMaking", "holder", "probe"]
  },
  {
    group: "holder",
    title: "Holder GUID",
    comment: "not in CSV",
    json_id: "holder.guid",
    id: "holder.guid",
    kind: "guid",
    hidden: true,
    for_types: ["ToolMilling", "ToolHoleMaking", "holder", "probe"]
  },
  {
    group: "holder",
    id: "holder.description",
    csv_id: "holder_description",
    json_id: "holder.description",
    hsm_id: "holder.description",
    title: "Holder Description",
    kind: "text",
    defaultValue: "",
    for_types: ["ToolMilling", "ToolHoleMaking", "holder", "probe"]
  },
  {
    group: "holder",
    id: "holder.productId",
    csv_id: "holder_productId",
    json_id: "holder.product-id",
    hsm_id: "holder.product-id",
    title: "Holder Product ID",
    kind: "text",
    defaultValue: "",
    for_types: ["ToolMilling", "ToolHoleMaking", "holder", "probe"]
  },
  {
    group: "holder",
    id: "holder.productLink",
    csv_id: "holder_productLink",
    json_id: "holder.product-link",
    hsm_id: "holder.product-link",
    title: "Holder Product Link",
    kind: "text",
    defaultValue: "",
    for_types: ["ToolMilling", "ToolHoleMaking", "holder", "probe"]
  },
  {
    group: "holder",
    id: "holder.vendor",
    csv_id: "holder_vendor",
    json_id: "holder.vendor",
    hsm_id: "holder.vendor",
    title: "Holder Vendor",
    kind: "text",
    defaultValue: "",
    for_types: ["ToolMilling", "ToolHoleMaking", "holder", "probe"]
  },
  {
    group: "holder",
    id: "holder.gaugeLength",
    csv_id: "tool_holderGaugeLength",
    json_id: "holder.gaugeLength",
    hsm_id: "holder.gauge-length",
    title: "Holder Gauge Length",
    kind: "length",
    for_types: ["ToolMilling", "ToolHoleMaking", "holder", "probe"]
  },
  {
    group: "holder",
    id: "holder.segments",
    csv_id: "holder_segments",
    json_id: "holder.segments",
    title: "Holder Segments",
    kind: "segments",
    comment: "Holder on a tool",
    for_types: ["ToolMilling", "ToolHoleMaking", "holder", "probe"]
  },
  {
    title: "",
    comment: "always 'holder'",
    json_id: "holder.type",
    required: true,
    kind: "enum",
    for_types: ["ToolMilling", "ToolHoleMaking", "holder", "probe"],
    defaultValue: "holder"
  }
];
var fusion_turning_insert_fields = [
  // Turning Insert
  {
    group: "turning_insert",
    id: "insert_type",
    json_id: "geometry.SC",
    csv_id: "tool_insertType",
    title: "Shape",
    kind: "enum",
    defaultValue: void 0
  },
  {
    group: "turning_insert",
    id: "insert_reliefAngle",
    json_id: "geometry.RA",
    csv_id: "tool_reliefAngle",
    title: "Relief Angle",
    kind: "angle"
  },
  {
    group: "turning_insert",
    id: "insert_toleranceClass",
    json_id: "geometry.TC",
    csv_id: "tool_tolerance",
    title: "Tolerance",
    kind: "enum",
    defaultValue: "M"
  },
  {
    group: "turning_insert",
    id: "insert_crossSection",
    csv_id: "tool_crossSection",
    json_id: "geometry.SCTY",
    title: "Cross Section",
    kind: "enum"
  },
  {
    group: "turning_insert",
    id: "insert_sizeSpecificationMode",
    json_id: "geometry.SIZE_SPECIFICATION_MODE",
    csv_id: "tool_insertSizeSpecificationMode",
    title: "Size specified by",
    kind: "enum",
    defaultValue: "CE"
  },
  {
    group: "turning_insert",
    id: "insert_diameter",
    json_id: "geometry.INSD",
    csv_id: "tool_insertSize",
    title: "Insert size",
    kind: "length",
    comment: "Alternatively geometry.INSL/insertLength depending on insertSizeSpecificationMode"
  },
  {
    group: "turning_insert",
    id: "insert_thickness",
    json_id: "geometry.S",
    csv_id: "tool_thickness",
    title: "Thickness",
    kind: "length"
  },
  {
    group: "turning_insert",
    id: "insert_headLength",
    json_id: "geometry.LH",
    csv_id: "tool_headLength",
    title: "Head Length",
    kind: "length",
    comment: "Grooving tools only"
  },
  {
    group: "turning_insert",
    id: "insert_grooveWidth",
    json_id: "geometry.tool_grooveWidth",
    csv_id: "tool_grooveWidth",
    title: "Groove Width",
    kind: "length"
  },
  {
    group: "turning_insert",
    id: "insert_angle",
    json_id: "geometry.EPSR",
    csv_id: "tool_insertAngle",
    title: "Angle",
    kind: "angle"
  },
  {
    group: "turning_insert",
    id: "insert_leadingAngle",
    json_id: "geometry.EPSR",
    csv_id: "tool_leadingAngle",
    title: "Leading Angle",
    kind: "angle"
  },
  {
    group: "turning_insert",
    id: "insert_trailingAngle",
    json_id: "geometry.trailingAngle",
    csv_id: "tool_trailingAngle",
    title: "Trailing Angle",
    kind: "angle"
  },
  {
    group: "turning_insert",
    id: "insert_lengthNonCuttingEdge",
    json_id: "geometry.NON_CUTTING_EDGE_LENGTH",
    csv_id: "tool_lengthNonCuttingEdge",
    title: "Trailing edge length",
    kind: "length"
  },
  {
    group: "turning_insert",
    id: "insert_width",
    json_id: "geometry.tool_insertWidth",
    csv_id: "tool_insertWidth",
    title: "Width",
    kind: "length"
  },
  {
    group: "turning_insert",
    id: "insert_minimumThreadPitch",
    json_id: "geometry.minimum-thread-pitch",
    csv_id: "tool_minimumThreadPitch",
    title: "Minimum Thread Pitch",
    kind: "length"
  },
  {
    group: "turning_insert",
    id: "insert_maximumThreadPitch",
    json_id: "geometry.maximum-thread-pitch",
    csv_id: "tool_maximumThreadPitch",
    title: "Maximum Thread Pitch",
    kind: "length"
  },
  {
    group: "turning_insert",
    id: "insert_internalThread",
    json_id: "geometry.tool_internalThread",
    comment: "Turning threading",
    csv_id: "tool_internalThread",
    title: "Internal Thread",
    kind: "boolean",
    defaultValue: "false"
  },
  {
    group: "turning_insert",
    id: "insert_threadTipRadius",
    json_id: "geometry.thread-tip-radius",
    csv_id: "tool_threadTipRadius",
    title: "Thread Tip Radius",
    kind: "length"
  },
  {
    group: "turning_insert",
    id: "insert_threadTipType",
    json_id: "geometry.thread-tip-type",
    csv_id: "tool_threadTipType",
    title: "Thread Tip Type",
    kind: "enum",
    defaultValue: "round"
  },
  {
    group: "turning_insert",
    id: "insert_threadTipWidth",
    json_id: "geometry.thread-tip-width",
    csv_id: "tool_threadTipWidth",
    title: "Thread Tip Width",
    kind: "length"
  }
];
var fusion_turning_holder_fields = [
  // Turning Holder
  {
    group: "turning_holder",
    id: "holder_style",
    csv_id: "tool_holderType",
    json_id: "holder.THSC",
    title: "Style",
    kind: "enum",
    defaultValue: "L"
  },
  {
    group: "turning_holder",
    id: "holder_hand",
    comment: "Right handed",
    csv_id: "tool_hand",
    json_id: "holder.HAND",
    title: "Hand",
    kind: "enum",
    defaultValue: "R"
  },
  {
    group: "turning_holder",
    id: "holder_clamping",
    json_id: "holder.MTP",
    csv_id: "tool_clamping",
    title: "Clamping",
    kind: "enum",
    defaultValue: "D"
  },
  {
    group: "turning_holder",
    id: "holder_cuttingWidth",
    json_id: "holder.CW",
    csv_id: "tool_cuttingWidth",
    title: "Cutting Width",
    kind: "length"
  },
  {
    group: "turning_holder",
    id: "holder_headLength",
    json_id: "holder.LH",
    csv_id: "tool_holderHeadLength",
    title: "Head Length",
    kind: "length"
  },
  {
    group: "turning_holder",
    id: "holder_overallLength",
    csv_id: "tool_holderOverallLength",
    json_id: "holder.OAL",
    title: "Overall Length",
    kind: "length"
  },
  {
    group: "turning_holder",
    id: "holder_shankHeight",
    json_id: "holder.H",
    csv_id: "tool_shankHeight",
    title: "Shank Height",
    kind: "length"
  },
  {
    group: "turning_holder",
    id: "holder_shankWidth",
    json_id: "holder.W",
    csv_id: "tool_shankWidth",
    title: "Shank Width",
    kind: "length"
  },
  {
    group: "turning_holder",
    id: "holder_sideAngle",
    // json_id: // TODO
    csv_id: "tool_sideAngle",
    title: "Side Angle",
    kind: "angle"
  },
  {
    group: "turning_holder",
    id: "holder_sideCutting",
    // json_id: // TODO
    csv_id: "tool_sideCutting",
    title: "Side Cutting",
    kind: "boolean",
    defaultValue: false
  },
  {
    group: "turning_holder",
    id: "holder_roundShank",
    json_id: "holder.tool_roundShank",
    csv_id: "tool_roundShank",
    title: "Round Shank",
    kind: "boolean",
    defaultValue: false
  }
];
var fusion_turning_setup_fields = [
  // Turning Setup
  {
    group: "turning_setup",
    id: "setup_angle",
    json_id: "setup.angle",
    csv_id: "tool_angle",
    title: "Orientation",
    kind: "angle",
    defaultValue: 0
  },
  {
    group: "turning_setup",
    id: "setup_compensation",
    json_id: "setup.compensation",
    csv_id: "tool_compensation",
    title: "Compensation",
    kind: "enum"
  },
  {
    group: "turning_setup",
    id: "setup_clockwise",
    json_id: "setup.HAND",
    //csv_id: "tool_clockwise",
    //hsm_id: "motion.clockwise",
    tp_id: "clockwiseRotation",
    title: "Clockwise Spindle Rotation",
    kind: "boolean",
    defaultValue: "R"
  },
  {
    group: "turning_setup",
    id: "setup_grooveCompOppositeEdge",
    csv_id: "tool_grooveCompOppositeEdge",
    json_id: "setup.tool_grooveCompOppositeEdge",
    title: "Use Opposite Edge",
    kind: "boolean"
  }
];
var fusion_other_fields = [
  // UNKNOWN / TODO
  {
    title: "",
    comment: "unsupported",
    json_id: "holder.last_modified",
    kind: "datetime"
  },
  {
    title: "",
    comment: "unsupported",
    json_id: "holder.reference_guid",
    kind: "guid"
  },
  {
    csv_id: "tool_shaftAxisAngle",
    //id: "geometry_shaftAxisAngle",
    title: "Flip",
    kind: "angle"
  },
  {
    csv_id: "tool_endAngle",
    //id: "geometry_endAngle",
    title: "End Angle",
    kind: "angle"
  },
  {
    csv_id: "tool_endCutting",
    //id: "geometry_endCutting",
    title: "End Cutting",
    kind: "boolean"
  },
  /* From v33 (still unused though)
  {
    group: "cutter_geometry",
    //id: "geometry_chamferAngle",
    csv_id: "tool_chamferAngle",
    //json_id: "geometry.",
    title: "Chamfer Angle",
    kind: "angle",
    defaultValue: 0.0, // deg
  },
  {
    group: "cutter_geometry",
    //id: "geometry_chamferAngle",
    csv_id: "tool_chamferWidth",
    //json_id: "geometry.",
    title: "Chamfer Width",
    kind: "length",
    defaultValue: 0.0, // deg
  },
  */
  // Other
  {
    title: "Tool Index",
    csv_id: "tool_index",
    id: "index",
    kind: "integer",
    comment: "Only used for CSV output"
  },
  {
    csv_id: "tool_library_version",
    //id: "fusion.library_version",
    // __json_id: "../version",
    comment: "in JSON, not under data like everything else, so handled separately",
    title: "Tool Library Version",
    kind: "integer",
    defaultValue: fusion_library_version
  },
  {
    comment: "not in CSV",
    title: "Last Modified",
    json_id: "last_modified",
    //id: "lastModified",
    kind: "datetime"
  },
  {
    comment: "not in CSV",
    title: "Reference GUID",
    json_id: "reference_guid",
    //id: "referenceGuid",
    kind: "guid"
  }
  /*
  {
    comment:
      "Only occasionally appear in Fusion produced json. Unclear when. Does not appear to be required, but if supplied and invalid causes the imported tool to become orange.",
    title: "Grade",
    json_id: "GRADE",
    //id: "grade",
    kind: "enum",
    //defaultValue: "generic", // Or "Mill Generic"
    for_types: [],
  },
  */
];
var fusion_preset_other_fields = [
  {
    group: "preset",
    id: "materialCategory",
    comment: "Used for search UI on holder tab. For some reason Fusion saves this - AND expects it to be present on load.",
    csv_id: "tool_presetMaterialCategory",
    json_id: "start-values.presets.material.category",
    hsm_key: "tool_presetMaterialCategory",
    title: "Filter by Type",
    kind: "enum"
    // __defaultValue: "all",
    //required: true,
  },
  {
    group: "preset",
    id: "materialQuery",
    comment: "Used for search UI on holder tab. For some reason Fusion saves this - AND expects it to be present on load.",
    csv_id: "tool_presetMaterialQuery",
    json_id: "start-values.presets.material.query",
    hsm_id: "tool_presetMaterialQuery",
    title: "Filter by Search",
    kind: "enum",
    defaultValue: ""
    //required: true,
  },
  {
    group: "preset",
    //id: "materialUseHardness",
    comment: "Used for search UI on holder tab.",
    json_id: "start-values.presets.material.use-hardness",
    csv_id: "tool_presetMaterialUseHardness",
    title: "Use Hardness",
    kind: "boolean",
    defaultValue: false
  },
  {
    group: "preset",
    //id: "materialMaxHardness",
    comment: "Used for search UI on holder tab.",
    json_id: "start-values.presets.material.maximum-hardness",
    csv_id: "tool_presetMaterialMaximumHardness",
    title: "Maximum Hardness",
    kind: "hardness",
    defaultValue: 0
  },
  {
    group: "preset",
    //id: "materialMaximumHardness",
    comment: "Used for search UI on holder tab.",
    json_id: "start-values.presets.material.maximum-hardness",
    csv_id: "tool_presetMaterialMaximumHardness",
    title: "Maximum Hardness",
    kind: "hardness",
    defaultValue: 0
  },
  {
    group: "preset",
    //id: "materialMinimumHardness",
    comment: "Used for search UI on holder tab.",
    json_id: "start-values.presets.material.minimum-hardness",
    csv_id: "tool_presetMaterialMinimumHardness",
    title: "Minimum Hardness",
    kind: "hardness",
    defaultValue: 0
  }
];
var fusion_preset_fields = [
  // Preset
  {
    group: "preset",
    id: "name",
    csv_id: "preset_name",
    json_id: "start-values.presets.name",
    hsm_id: "preset.name",
    title: "Name",
    kind: "text",
    required: true,
    defaultValue: "Default preset"
  },
  {
    group: "preset",
    id: "guid",
    comment: "not in CSV",
    title: "GUID",
    json_id: "start-values.presets.guid",
    hsm_id: "preset.id",
    kind: "guid",
    hidden: true
  },
  /*
    {
      group: "preset",
      id: "description",
      comment: "not in CSV, not shown in Fusion UI",
      title: "Preset Description",
      json_id: "start-values.presets.description",
      hsm_id: "preset.description",
      kind: "text",
      hidden: true,
      for_types: ["Tool"],
    },
  */
  {
    group: "preset",
    id: "coolant",
    csv_id: "tool_coolant",
    json_id: "start-values.presets.tool-coolant",
    hsm_id: "coolant.mode",
    hsm_key: "tool_coolant",
    title: "Coolant",
    kind: "enum",
    defaultValue: "flood",
    enumValues: [
      { value: "disabled", json_value: "disabled", title: "Disabled" },
      { value: "flood", json_value: "flood", title: "Flood" },
      { value: "mist", json_value: "mist", title: "Mist" },
      { value: "tool", json_value: "tool", title: "Through Tool" },
      { value: "air", json_value: "air", title: "Air" },
      { value: "airThroughTool", json_value: "air through tool", title: "Air through tool" },
      { value: "suction", json_value: "suction", title: "Suction" },
      { value: "floodMist", json_value: "flood mist", title: "Flood and mist" },
      { value: "floodTool", json_value: "flood tool", title: "Flood and through tool" }
    ]
  },
  // Speeds
  {
    group: "preset_speeds",
    id: "useConstantSurfaceSpeed",
    csv_id: "tool_useConstantSurfaceSpeed",
    json_id: "start-values.presets.use-constant-surface-speed",
    title: "Use Constant Surface Speed",
    kind: "boolean",
    defaultValue: true
  },
  {
    group: "preset_speeds",
    id: "spindleSpeed",
    csv_id: "tool_spindleSpeed",
    json_id: "start-values.presets.n",
    hsm_id: "motion.spindle-rpm",
    hsm_key: "tool_spindleSpeed",
    title: "Spindle Speed",
    kind: "rotation_speed",
    required: true,
    defaultValue: 5e3
    // rpm
  },
  {
    group: "preset_speeds",
    id: "surfaceSpeed",
    csv_id: "tool_surfaceSpeed",
    json_id: "start-values.presets.v_c",
    // hsm_key: "tool_surfaceSpeed", // not in hsmlib format - always calculated
    title: "Surface Speed",
    kind: "surface_speed",
    comment: "Calculated from spindleSpeed on milling tools",
    defaultValue: 200
    // m/min
  },
  {
    group: "preset_speeds",
    id: "rampSpindleSpeed",
    csv_id: "tool_rampSpindleSpeed",
    json_id: "start-values.presets.n_ramp",
    hsm_id: "motion.ramp-spindle-rpm",
    hsm_key: "tool_rampSpindleSpeed",
    title: "Ramp Spindle Speed",
    kind: "rotation_speed",
    defaultValue: 5e3
    // rpm
  },
  // Feedrates
  {
    comment: "Only in hsmlib",
    group: "preset_feedrates",
    hsm_id: "motion.feed-mode",
    title: "Feed Mode",
    kind: "enum"
  },
  {
    group: "preset_feedrates",
    id: "feedCutting",
    csv_id: "tool_feedCutting",
    json_id: "start-values.presets.v_f",
    hsm_id: "motion.cutting-feedrate",
    hsm_key: "tool_feedCutting",
    title: "Cutting Feedrate",
    kind: "feedrate",
    defaultValue: 1e3
    // mm/min
  },
  {
    group: "preset_feedrates",
    id: "feedPerTooth",
    csv_id: "tool_feedPerTooth",
    json_id: "start-values.presets.f_z",
    // hsm_id: "motion.",
    hsm_key: "tool_feedPerTooth",
    title: "Feed per Tooth",
    kind: "length"
  },
  {
    group: "preset_feedrates",
    id: "feedEntry",
    csv_id: "tool_feedEntry",
    json_id: "start-values.presets.v_f_leadIn",
    hsm_id: "motion.entry-feedrate",
    hsm_key: "tool_feedEntry",
    title: "Lead-In Feedrate",
    kind: "feedrate",
    defaultValue: 1e3
    // mm/min
  },
  {
    group: "preset_feedrates",
    id: "feedExit",
    csv_id: "tool_feedExit",
    json_id: "start-values.presets.v_f_leadOut",
    hsm_id: "motion.exit-feedrate",
    hsm_key: "tool_feedExit",
    title: "Lead-Out Feedrate",
    kind: "feedrate",
    defaultValue: 1e3
    // mm/min
  },
  {
    group: "preset_feedrates",
    id: "feedTransition",
    csv_id: "tool_feedTransition",
    json_id: "start-values.presets.v_f_transition",
    hsm_id: "motion.transition-feedrate",
    hsm_key: "tool_feedTransition",
    title: "Transition Feedrate",
    kind: "feedrate",
    defaultValue: 1e3
    // mm/min
  },
  {
    group: "preset_feedrates",
    id: "feedRamp",
    csv_id: "tool_feedRamp",
    json_id: "start-values.presets.v_f_ramp",
    hsm_id: "motion.ramp-feedrate",
    hsm_key: "tool_feedRamp",
    title: "Ramp Feedrate",
    kind: "feedrate",
    defaultValue: 500
    // mm/min
  },
  /* From v33
  {
    group: "preset_feedrates",
    id: "rampAngle",
    csv_id: "tool_rampAngle",
    json_id: "start-values.presets.ramp-angle",
    //hsm_id: "motion.ramp-angle",
    hsm_key: "tool_rampAngle",
    title: "Ramp Angle",
    kind: "angle",
    defaultValue: 2.0, // deg
  },
  */
  {
    group: "preset_feedrates",
    id: "useFeedPerRevolution",
    csv_id: "tool_useFeedPerRevolution",
    json_id: "start-values.presets.use-feed-per-revolution",
    title: "Use Feed per Revolution",
    kind: "boolean",
    defaultValue: false
  },
  {
    group: "preset_feedrates",
    id: "feedEntryRel",
    csv_id: "tool_feedEntryRel",
    json_id: "start-values.presets.f_n_leadIn",
    title: "Lead-In Feed per Revolution",
    kind: "feedrate"
  },
  {
    group: "preset_feedrates",
    comment: "present in CSV, but not JSON where tool_feedPerRevolution is used",
    csv_id: "tool_feedCuttingRel",
    // __json_id: "start-values.presets.??",
    id: "feedCuttingRel",
    title: "Cutting Feed per Revolution",
    kind: "feedrate"
  },
  {
    group: "preset_feedrates",
    csv_id: "tool_feedDepositing",
    // __json_id: "start-values.presets.v_f",
    id: "feedDepositing",
    title: "Depositing Feedrate",
    kind: "feedrate",
    defaultValue: 1e3
    // mm/min
  },
  {
    group: "preset_feedrates",
    csv_id: "tool_feedExitRel",
    json_id: "start-values.presets.f_n_leadOut",
    id: "feedExitRel",
    title: "Lead-Out Feed per Revolution",
    kind: "feedrate"
  },
  {
    group: "preset_feedrates",
    csv_id: "tool_feedProbeLink",
    json_id: "start-values.presets.v_f_link",
    id: "feedProbeLink",
    title: "Link Feedrate",
    kind: "feedrate",
    defaultValue: 3e3
    // mm/min
  },
  {
    group: "preset_feedrates",
    csv_id: "tool_feedProbeMeasure",
    json_id: "start-values.presets.v_f_measure",
    id: "feedProbeMeasure",
    title: "Measure Feedrate",
    kind: "feedrate",
    defaultValue: 100
    // mm/min
  },
  // Vertical Feedrates
  {
    group: "preset_feedrates_vertical",
    id: "feedPlunge",
    csv_id: "tool_feedPlunge",
    json_id: "start-values.presets.v_f_plunge",
    hsm_id: "motion.plunge-feedrate",
    hsm_key: "tool_feedPlunge",
    title: "Plunge Feedrate",
    kind: "feedrate",
    defaultValue: 500
    // mm/min
  },
  {
    group: "preset_feedrates_vertical",
    id: "feedPerRevolution",
    csv_id: "tool_feedPerRevolution",
    json_id: "start-values.presets.f_n",
    // hsm_id: "motion.??",
    hsm_key: "tool_feedPerRevolution",
    title: "Plunge Feed per Revolution",
    kind: "feedrate"
  },
  {
    group: "preset_feedrates_vertical",
    id: "feedRetract",
    csv_id: "tool_feedRetract",
    json_id: "start-values.presets.v_f_retract",
    hsm_id: "motion.retract-feedrate",
    hsm_key: "tool_feedRetract",
    title: "Retract Feedrate",
    kind: "feedrate",
    defaultValue: 1500
    // mm/min
  },
  {
    group: "preset_feedrates_vertical",
    id: "feedRetractPerRevolution",
    csv_id: "tool_feedRetractPerRevolution",
    json_id: "start-values.presets.f_n_retract",
    // hsm_id: "motion.??",
    hsm_key: "tool_feedRetractPerRevolution",
    title: "Retract Feed per Revolution",
    kind: "length"
  },
  // Passes and linking
  {
    group: "preset_capabilities",
    id: "useStepdown",
    csv_id: "use_tool_stepdown",
    json_id: "start-values.presets.use-stepdown",
    // hsm_id: "use_tool_stepdown", // not available - derived from tool_stepdown != 0 ?
    title: "Use Stepdown",
    kind: "boolean",
    defaultValue: false
  },
  {
    group: "preset_capabilities",
    id: "stepdown",
    csv_id: "tool_stepdown",
    json_id: "start-values.presets.stepdown",
    hsm_key: "tool_stepdown",
    title: "Stepdown",
    kind: "length",
    //required: true,
    defaultValue: 6
    // mm
  },
  {
    group: "preset_capabilities",
    id: "useStepover",
    csv_id: "use_tool_stepover",
    json_id: "start-values.presets.use-stepover",
    // hsm_key: "use_tool_stepover", // not available - derived from tool_stepover != 0 ?
    title: "Use Stepover",
    kind: "boolean",
    defaultValue: false
  },
  {
    group: "preset_capabilities",
    id: "stepover",
    csv_id: "tool_stepover",
    json_id: "start-values.presets.stepover",
    hsm_key: "tool_stepover",
    title: "Stepover",
    kind: "length",
    //required: true,
    defaultValue: 1
    // mm
  },
  {
    group: "preset_capabilities",
    id: "useDepthOfCut",
    json_id: "start-values.presets.use-depth-of-cut",
    csv_id: "use_tool_depthOfCut",
    title: "Use Depth of Cut",
    kind: "boolean",
    defaultValue: false
  },
  {
    group: "preset_capabilities",
    csv_id: "tool_depthOfCut",
    id: "depthOfCut",
    json_id: "start-values.presets.depth-of-cut",
    title: "Depth of Cut",
    kind: "length",
    defaultValue: 1
    // mm
  }
];
var fusion_preset_process_fields = [
  // Cutting Process Inputs
  {
    group: "preset_process_cutting",
    id: "cutHeight",
    json_id: "start-values.presets.cutHeight",
    csv_id: "tool_cutHeight",
    title: "Cut Height",
    kind: "length"
  },
  {
    group: "preset_process_cutting",
    id: "cutPower",
    json_id: "start-values.presets.cutPower",
    csv_id: "tool_cutPower",
    title: "Cut Power",
    kind: "unitless"
  },
  {
    group: "preset_process_cutting",
    id: "assistGas",
    json_id: "start-values.presets.assistGas",
    csv_id: "tool_assistGas",
    title: "Assist Gas",
    kind: "enum",
    defaultValue: "Oxygen"
  },
  {
    group: "preset_process_cutting",
    id: "machineQualityControl",
    json_id: "start-values.presets.machineQualityControl",
    csv_id: "tool_machineQualityControl",
    title: "Quality Control",
    kind: "enum",
    defaultValue: "manual"
  },
  {
    group: "preset_process_cutting",
    id: "pierceHeight",
    json_id: "start-values.presets.pierceHeight",
    csv_id: "tool_pierceHeight",
    title: "Pierce Height",
    kind: "length",
    defaultValue: void 0
  },
  {
    group: "preset_process_cutting",
    id: "piercePower",
    json_id: "start-values.presets.piercePower",
    csv_id: "tool_piercePower",
    title: "Pierce Power",
    kind: "unitless"
  },
  {
    group: "preset_process_cutting",
    id: "pierceTime",
    json_id: "start-values.presets.pierceTime",
    csv_id: "tool_pierceTime",
    title: "Pierce Time",
    kind: "unitless"
  },
  {
    group: "preset_process_cutting",
    id: "pressure",
    json_id: "start-values.presets.pressure",
    csv_id: "tool_pressure",
    title: "Pressure",
    kind: "unitless"
  },
  {
    group: "preset_process_cutting",
    csv_id: "tool_abrasiveFlowRate",
    id: "abrasiveFlowRate",
    json_id: "start-values.presets.abrasiveFlowRate",
    title: "Abrasive Flow Rate",
    kind: "unitless",
    defaultValue: 1
  },
  {
    group: "preset_process_depositing",
    //group: "preset_deposition_geometry",
    json_id: "start-values.presets.BEAD_WIDTH",
    csv_id: "tool_beadWidth",
    id: "beadWidth",
    title: "Bead Width",
    kind: "length"
  },
  {
    group: "preset_process_depositing",
    //group: "preset_deposition_geometry",
    json_id: "start-values.presets.LAYER_THICKNESS",
    csv_id: "tool_layerThickness",
    id: "layerThickness",
    title: "Layer Thickness",
    kind: "length"
  },
  {
    group: "preset_process_depositing",
    json_id: "start-values.presets.DEPOSITING_AUXILIARY_GAS_FLOW_RATE",
    csv_id: "tool_depositingAuxiliaryGasFlowRate",
    id: "depositingAuxiliaryGasFlowRate",
    title: "Auxiliary Gas Flow Rate",
    kind: "gas_flow"
  },
  {
    group: "preset_process_depositing",
    json_id: "start-values.presets.DEPOSITING_CARRIER_GAS_FLOW_RATE",
    csv_id: "tool_depositingCarrierGasFlowRate",
    id: "depositingCarrierGasFlowRate",
    title: "Carrier Gas Flow Rate",
    kind: "gas_flow"
  },
  {
    group: "preset_process_depositing",
    json_id: "start-values.presets.DEPOSITING_CURRENT",
    csv_id: "tool_depositingCurrent",
    id: "depositingCurrent",
    title: "Current",
    kind: "current"
  },
  {
    group: "preset_process_depositing",
    json_id: "start-values.presets.DEPOSITING_POWER",
    csv_id: "tool_depositingPower",
    id: "depositingPower",
    title: "Power",
    kind: "watts"
  },
  {
    group: "preset_process_depositing",
    json_id: "start-values.presets.DEPOSITING_SHIELD_GAS_FLOW_RATE",
    csv_id: "tool_depositingShieldGasFlowRate",
    id: "depositingShieldGasFlowRate",
    title: "Shield Gas Flow Rate",
    kind: "gas_flow"
  },
  {
    group: "preset_process_depositing",
    json_id: "start-values.presets.DEPOSITING_VOLTAGE",
    csv_id: "tool_depositingVoltage",
    id: "depositingVoltage",
    title: "Voltage",
    kind: "volts"
  },
  {
    group: "preset_process_depositing",
    json_id: "start-values.presets.WIRE_FEED",
    csv_id: "tool_feedWire",
    id: "feedWire",
    title: "Wire Feedrate",
    kind: "feedrate"
  },
  {
    group: "preset_process_depositing",
    json_id: "start-values.presets.USE_PRESET_PROGRAM",
    csv_id: "tool_presetProgram",
    id: "programNumber",
    title: "Preset Program Number",
    kind: "integer"
  },
  {
    group: "preset_process_depositing",
    json_id: "start-values.presets.STAND_OFF_DISTANCE",
    csv_id: "tool_standoffDistance",
    id: "standoffDistance",
    title: "Stand-off Distance",
    kind: "length",
    defaultValue: 0
  },
  {
    group: "preset_process_depositing",
    id: "usePresetProgram",
    json_id: "start-values.presets.USE_PRESET_PROGRAM",
    csv_id: "use_tool_presetProgram",
    title: "Use Preset Program Number",
    kind: "boolean",
    defaultValue: false
  },
  {
    group: "preset_process_depositing",
    id: "powderFlowRate",
    json_id: "start-values.presets.POWDER_FLOW_RATE",
    csv_id: "tool_powderFlowRate",
    title: "Powder Flow Rate",
    kind: "unitless"
  }
];
var fusion_all_tool_fields = [
  ...fusion_base_fields,
  ...fusion_tool_fields,
  ...fusion_turning_insert_fields,
  ...fusion_turning_holder_fields,
  ...fusion_turning_setup_fields,
  ...fusion_other_fields
];
var fusion_all_preset_fields = [
  ...fusion_preset_fields,
  ...fusion_preset_process_fields,
  ...fusion_preset_other_fields
];
var fusion_fields = [...fusion_all_tool_fields, ...fusion_all_preset_fields];
function nestByKey(array, key = "id") {
  return array.reduce((acc, item) => {
    if (!(key in item)) {
      return acc;
    }
    const keys2 = item[key].split(".");
    let current = acc;
    keys2.forEach((k, index) => {
      if (index === keys2.length - 1) {
        current[k] = item;
      } else {
        current[k] = current[k] || {};
        current = current[k];
      }
    });
    return acc;
  }, {});
}
var fusion_field_by_id = {};
var fusion_field_by_json_id = {};
var fusion_field_by_csv_id = {};
var fusion_field_by_hsm_id = {};
var fusion_field_by_hsm_key = {};
var fusion_fields_nested_by_id = nestByKey(fusion_fields, "id");
fusion_fields.forEach((field, _index) => {
  if (field.id) {
    fusion_field_by_id[field.id] = field;
  }
  if (field.json_id)
    fusion_field_by_json_id[field.json_id] = field;
  if (field.csv_id)
    fusion_field_by_csv_id[field.csv_id] = field;
  if (field.hsm_id)
    fusion_field_by_hsm_id[field.hsm_id] = field;
  if (field.hsm_key)
    fusion_field_by_hsm_key[field.hsm_key] = field;
});
function expandForTypes(for_types) {
  return for_types.flatMap((ft) => groups[ft] || ft);
}
fusion_fields.forEach((field, _index) => {
  if (field.json_id) {
    if (!field.for_types)
      field.for_types = fusion_type_fields_default[field.json_id];
    if (field.for_types) {
      field.for_types_expanded = new Set(expandForTypes(field.for_types));
    } else {
    }
  }
});
function getValidFieldsForType(type) {
  return fusion_fields.filter((f) => f.for_types_expanded?.has(type));
}
function filterFieldsForTypes(fields, for_types) {
  const expanded = expandForTypes(for_types);
  return fields.filter((field) => expanded.some((type) => field.for_types_expanded?.has(type)));
}
function getFieldsByKeyForType(fields, key, type) {
  const result = {};
  for (const field of fields) {
    if (!field[key])
      continue;
    if (field.for_types_expanded?.has(type)) {
      if (result[field[key]]) {
        throw new Error(`Type "${type}" overlaps for id "${field[key]}".`);
      }
      result[field[key]] = field;
    }
  }
  return result;
}

// src/fusion/fusion_csv_columns.ts
var fusion_csv_columns = [
  "Tool Index (tool_index)",
  "Preset Name (preset_name)",
  "Type (tool_type)",
  "Description (tool_description)",
  "Diameter (tool_diameter)",
  "Number (tool_number)",
  "Unit (tool_unit)",
  "Holder Description (holder_description)",
  "Holder Product ID (holder_productId)",
  "Holder Product Link (holder_productLink)",
  "Holder Vendor (holder_vendor)",
  "Abrasive Flow Rate (tool_abrasiveFlowRate)",
  "Orientation (tool_angle)",
  "Tool Assembly Gauge Length (tool_assemblyGaugeLength)",
  "Assist Gas (tool_assistGas)",
  "Axial Distance (tool_axialDistance)",
  "Bead Width (tool_beadWidth)",
  "Body Length (tool_bodyLength)",
  "Break Control (tool_breakControl)",
  //"Chamfer Angle (tool_chamferAngle)",
  //"Chamfer Width (tool_chamferWidth)",
  "Clamping (tool_clamping)",
  "Clockwise Spindle Rotation (tool_clockwise)",
  "Comment (tool_comment)",
  "Compensation (tool_compensation)",
  "Compensation Offset (tool_compensationOffset)",
  "Coolant (tool_coolant)",
  "Coolant Support (tool_coolantSupport)",
  "Corner Radius (tool_cornerRadius)",
  "Cross Section (tool_crossSection)",
  "Cut Height (tool_cutHeight)",
  "Cut Power (tool_cutPower)",
  "Cutting Width (tool_cuttingWidth)",
  "Auxiliary Gas Flow Rate (tool_depositingAuxiliaryGasFlowRate)",
  "Carrier Gas Flow Rate (tool_depositingCarrierGasFlowRate)",
  "Current (tool_depositingCurrent)",
  "Power (tool_depositingPower)",
  "Shield Gas Flow Rate (tool_depositingShieldGasFlowRate)",
  "Voltage (tool_depositingVoltage)",
  "Depth of Cut (tool_depthOfCut)",
  "Diameter Offset (tool_diameterOffset)",
  "End Angle (tool_endAngle)",
  "End Cutting (tool_endCutting)",
  "Cutting Feedrate (tool_feedCutting)",
  "Cutting Feed per Revolution (tool_feedCuttingRel)",
  "Depositing Feedrate (tool_feedDepositing)",
  "Lead-In Feedrate (tool_feedEntry)",
  "Lead-In Feed per Revolution (tool_feedEntryRel)",
  "Lead-Out Feedrate (tool_feedExit)",
  "Lead-Out Feed per Revolution (tool_feedExitRel)",
  "Plunge Feed per Revolution (tool_feedPerRevolution)",
  "Feed per Tooth (tool_feedPerTooth)",
  "Plunge Feedrate (tool_feedPlunge)",
  "Link Feedrate (tool_feedProbeLink)",
  "Measure Feedrate (tool_feedProbeMeasure)",
  "Ramp Feedrate (tool_feedRamp)",
  "Retract Feedrate (tool_feedRetract)",
  "Retract Feed per Revolution (tool_feedRetractPerRevolution)",
  "Transition Feedrate (tool_feedTransition)",
  "Wire Feedrate (tool_feedWire)",
  "Flute Length (tool_fluteLength)",
  "Use Opposite Edge (tool_grooveCompOppositeEdge)",
  "Groove Width (tool_grooveWidth)",
  "Hand (tool_hand)",
  "Head Clearance (tool_headClearance)",
  "Head Length (tool_headLength)",
  "Tool Holder Gauge Length (tool_holderGaugeLength)",
  "Head Length (tool_holderHeadLength)",
  "Overall Length (tool_holderOverallLength)",
  "Style (tool_holderType)",
  "Angle (tool_insertAngle)",
  "Insert size (tool_insertSize)",
  "Size specified by (tool_insertSizeSpecificationMode)",
  "Shape (tool_insertType)",
  "Width (tool_insertWidth)",
  "Internal Thread (tool_internalThread)",
  "Kerf Width (tool_kerfWidth)",
  "Layer Thickness (tool_layerThickness)",
  "Leading Angle (tool_leadingAngle)",
  "Trailing edge length (tool_lengthNonCuttingEdge)",
  "Length Offset (tool_lengthOffset)",
  "Live Tool (tool_live)",
  "Lower Radius (tool_lowerRadius)",
  "Quality Control (tool_machineQualityControl)",
  "Manual Tool Change (tool_manualToolChange)",
  "Material (tool_material)",
  //"Maximum Diameter (tool_maximumCuttingDiameter)",
  "Maximum Thread Pitch (tool_maximumThreadPitch)",
  "Minimum Thread Pitch (tool_minimumThreadPitch)",
  "Nozzle Diameter (tool_nozzleDiameter)",
  "Number of Flutes (tool_numberOfFlutes)",
  "Number of Teeth (tool_numberOfTeeth)",
  "Overall Length (tool_overallLength)",
  "Pierce Height (tool_pierceHeight)",
  "Pierce Power (tool_piercePower)",
  "Pierce Time (tool_pierceTime)",
  "Powder Flow Rate (tool_powderFlowRate)",
  "Filter by Type (tool_presetMaterialCategory)",
  //"Maximum hardness (tool_presetMaterialMaximumHardness)",
  //"Minimum hardness (tool_presetMaterialMinimumHardness)",
  "Filter by Search (tool_presetMaterialQuery)",
  //"Filter by hardness (tool_presetMaterialUseHardness)",
  "Preset Program Number (tool_presetProgram)",
  "Pressure (tool_pressure)",
  "Product ID (tool_productId)",
  "Product Link (tool_productLink)",
  "Profile Radius (tool_profileRadius)",
  //"Ramp Angle (tool_rampAngle)",
  "Ramp Spindle Speed (tool_rampSpindleSpeed)",
  "Relief Angle (tool_reliefAngle)",
  "Round Shank (tool_roundShank)",
  "Flip (tool_shaftAxisAngle)",
  "Shaft Diameter (tool_shaftDiameter)",
  "Shank Height (tool_shankHeight)",
  "Shank Width (tool_shankWidth)",
  //"Shoulder Diameter (tool_shoulderDiameter)",
  "Shoulder Length (tool_shoulderLength)",
  "Side Angle (tool_sideAngle)",
  "Side Cutting (tool_sideCutting)",
  "Spindle Speed (tool_spindleSpeed)",
  "Stand-off Distance (tool_standoffDistance)",
  "Stepdown (tool_stepdown)",
  "Stepover (tool_stepover)",
  "Surface Speed (tool_surfaceSpeed)",
  "Taper Angle (tool_taperAngle)",
  "Tapered Type (tool_taperedType)",
  "Thickness (tool_thickness)",
  "Thread Pitch (tool_threadPitch)",
  "Thread Profile Angle (tool_threadProfileAngle)",
  "Thread Tip Radius (tool_threadTipRadius)",
  "Thread Tip Type (tool_threadTipType)",
  "Thread Tip Width (tool_threadTipWidth)",
  "Tip Angle (tool_tipAngle)",
  "Tip Diameter (tool_tipDiameter)",
  "Tip Length (tool_tipLength)",
  "Tip Offset (tool_tipOffset)",
  "Tolerance (tool_tolerance)",
  "Trailing Angle (tool_trailingAngle)",
  "Turret (tool_turret)",
  "Upper Radius (tool_upperRadius)",
  "Use Constant Surface Speed (tool_useConstantSurfaceSpeed)",
  "Use Feed per Revolution (tool_useFeedPerRevolution)",
  "Vendor (tool_vendor)",
  "Use Depth of Cut (use_tool_depthOfCut)",
  "Use Preset Program Number (use_tool_presetProgram)",
  "Use Stepdown (use_tool_stepdown)",
  "Use Stepover (use_tool_stepover)",
  "Shaft Segments (shaft_segments)",
  "Holder Segments (holder_segments)",
  "Tool Library Version (tool_library_version)"
];
var fusion_csv_id2index = {};
var fusion_csv_index2id = [];
fusion_csv_columns.forEach((csv_id, index) => {
  if (csv_id.indexOf(" (") != -1) {
    [, csv_id] = csv_id.split(" (");
  }
  if (csv_id.endsWith(")")) {
    csv_id = csv_id.slice(0, -1);
  }
  fusion_csv_index2id.push(csv_id);
  fusion_csv_id2index[csv_id] = index;
});

// src/util/csv.js
function splitCSV(str, useTabs = false) {
  const delimiter = useTabs ? "	" : ",";
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    if (char === '"') {
      if (inQuotes && str[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current);
  return result.map((field) => {
    if (field.startsWith('"') && field.endsWith('"')) {
      field = field.substring(1, field.length - 1);
      field = field.replace(/""/g, '"');
    }
    return field;
  });
}
function parseCSV(csv) {
  csv = csv.replace("\r\n", "\n");
  var lines = csv.split("\n");
  const headerLine = lines.shift();
  const useTabs = headerLine.indexOf("	") != -1;
  const headers = splitCSV(headerLine, useTabs);
  let rows = [];
  for (const line of lines) {
    if (line.trim() == "")
      continue;
    rows.push(splitCSV(line, useTabs));
  }
  return { headers, rows, useTabs };
}
function joinCSV(arr, useTabs = false) {
  const delimiter = useTabs ? "	" : ",";
  return arr.map((item) => {
    if (typeof item === "string") {
      const needsQuotes = item.includes(delimiter) || item.includes('"') || (useTabs ? item.includes("	") : false);
      if (item.endsWith('"') && item.startsWith('"')) {
        item = item.substring(1, item.length - 1);
      }
      let escaped = item.replace(/"/g, '""');
      if (needsQuotes) {
        return `"${escaped}"`;
      }
      return escaped;
    }
    return item;
  }).join(delimiter);
}
function diffCSV(str1, str2) {
  const { headers: headers1, rows: rows1, useTabs: useTabs1 } = parseCSV(str1);
  const { headers: headers2, rows: rows2, useTabs: useTabs2 } = parseCSV(str2);
  if (useTabs1 != useTabs2) {
    return "Mixed separators";
  }
  if (headers1.join("	") != headers2.join("	")) {
    return "Different headers";
  }
  const headers = headers1;
  const differentColumns = /* @__PURE__ */ new Set();
  const numRows = Math.max(rows1.length, rows2.length);
  for (let i = 0; i < numRows; i++) {
    const row1 = rows1[i] || {};
    const row2 = rows2[i] || {};
    for (let j = 0; j < headers.length; j++) {
      const value1 = row1[j] || "";
      const value2 = row2[j] || "";
      if (value1 !== value2) {
        differentColumns.add(j);
      }
    }
  }
  if (differentColumns.size === 0) {
    return "";
  }
  const outputHeaders = [];
  for (const j of differentColumns) {
    const header = headers[j];
    outputHeaders.push(`${header} (file1)`);
    outputHeaders.push(`${header} (file2)`);
  }
  const outputLines = [];
  outputLines.push(joinCSV(outputHeaders, useTabs1));
  for (let i = 0; i < numRows; i++) {
    const row1 = rows1[i] || {};
    const row2 = rows2[i] || {};
    let rowHasDifference = false;
    const outputFields = [];
    for (const header of differentColumns) {
      const value1 = row1[header] || "";
      const value2 = row2[header] || "";
      outputFields.push(value1);
      outputFields.push(value2);
      if (value1 !== value2) {
        rowHasDifference = true;
      }
    }
    if (rowHasDifference) {
      outputLines.push(joinCSV(outputFields, useTabs1));
    }
  }
  return outputLines.join("\n");
}

// src/assets/tool_v1.ts
var ToolBase_V1 = class extends RichType {
  constructor() {
    super(...arguments);
    //////////// general ////////////
    __publicField(this, "type", "unknown");
    __publicField(this, "unit", "millimeters");
    __publicField(this, "description", "");
    __publicField(this, "vendor", "");
    __publicField(this, "productId", "");
    __publicField(this, "productLink", "");
    __publicField(this, "guid");
    __publicField(this, "sourceSystem", "fusion");
  }
};
__publicField(ToolBase_V1, "type_spec", { "title": "Type", "kind": "enum", "defaultValue": "unknown" });
// will get replaced with UnitShorthand later, for consistency with frontend
__publicField(ToolBase_V1, "unit_spec", { "title": "Unit", "kind": "enum", "defaultValue": "millimeters" });
__publicField(ToolBase_V1, "description_spec", { "title": "Description", "kind": "text", "defaultValue": "" });
__publicField(ToolBase_V1, "vendor_spec", { "title": "Vendor", "kind": "text", "defaultValue": "" });
__publicField(ToolBase_V1, "productId_spec", { "title": "Product ID", "kind": "text", "defaultValue": "" });
__publicField(ToolBase_V1, "productLink_spec", { "title": "Product Link", "kind": "text", "defaultValue": "" });
__publicField(ToolBase_V1, "guid_spec", { "title": "GUID", "kind": "guid", "hidden": true });
__publicField(ToolBase_V1, "sourceSystem_spec", { "title": "Source System", "kind": "enum", "defaultValue": "fusion", "hidden": true });
__publicField(ToolBase_V1, "version", 1);
var Tool_V1 = class extends ToolBase_V1 {
  constructor() {
    super(...arguments);
    //////////// cutter ////////////
    __publicField(this, "geometry_clockwise");
    __publicField(this, "geometry_numberOfFlutes");
    __publicField(this, "material");
    //////////// cutter_geometry ////////////
    __publicField(this, "geometry_cuttingDiameter");
    __publicField(this, "geometry_cornerRadius");
    __publicField(this, "geometry_shaftDiameter");
    __publicField(this, "geometry_overallLength");
    __publicField(this, "geometry_protrudingLength");
    __publicField(this, "geometry_shoulderLength");
    __publicField(this, "geometry_shoulderDiameter");
    __publicField(this, "geometry_fluteLength");
    __publicField(this, "geometry_coolantSupport");
    __publicField(this, "geometry_taperedType");
    __publicField(this, "geometry_taperAngle");
    __publicField(this, "geometry_numberOfTeeth");
    __publicField(this, "geometry_threadPitch");
    __publicField(this, "geometry_threadProfileAngle");
    __publicField(this, "geometry_tipAngle");
    __publicField(this, "geometry_tipDiameter");
    __publicField(this, "geometry_tipLength");
    __publicField(this, "geometry_tipOffset");
    __publicField(this, "geometry_axialDistance");
    __publicField(this, "geometry_profileRadius");
    __publicField(this, "geometry_upperRadius");
    __publicField(this, "geometry_lowerRadius");
    //////////// cutter_assembly ////////////
    __publicField(this, "geometry_assemblyGaugeLength");
    //////////// post ////////////
    __publicField(this, "post_number");
    __publicField(this, "post_lengthOffset");
    __publicField(this, "post_diameterOffset");
    __publicField(this, "post_turret");
    __publicField(this, "post_comment");
    __publicField(this, "post_manualToolChange");
    __publicField(this, "post_live");
    __publicField(this, "post_breakControl");
    //////////// shaft ////////////
    __publicField(this, "shaft_segments");
    //////////// holder ////////////
    __publicField(this, "holder_unit");
    __publicField(this, "holder_guid");
    __publicField(this, "holder_description");
    __publicField(this, "holder_productId");
    __publicField(this, "holder_productLink");
    __publicField(this, "holder_vendor");
    __publicField(this, "holder_gaugeLength");
    __publicField(this, "holder_segments");
  }
};
// ToolMilling, ToolDrill, boring bar, counter bore, reamer, counter sink
__publicField(Tool_V1, "geometry_clockwise_spec", { "title": "Clockwise Spindle Rotation", "kind": "boolean", "defaultValue": true, "for_types": ["ToolMilling", "ToolDrill", "boring bar", "counter bore", "reamer", "counter sink"] });
// ToolMilling, ToolHoleMaking
__publicField(Tool_V1, "geometry_numberOfFlutes_spec", { "title": "Number of Flutes", "kind": "integer", "defaultValue": 3, "for_types": ["ToolMilling", "ToolHoleMaking"] });
// ToolMilling, ToolHoleMaking, ToolTurning
__publicField(Tool_V1, "material_spec", { "title": "Material", "kind": "enum", "defaultValue": "carbide", "for_types": ["ToolMilling", "ToolHoleMaking", "ToolTurning"] });
// ToolMilling, ToolHoleMaking, probe
__publicField(Tool_V1, "geometry_cuttingDiameter_spec", { "title": "Diameter", "kind": "length", "defaultValue": 6, "for_types": ["ToolMilling", "ToolHoleMaking", "probe"] });
// bull nose end mill, face mill, tapered mill, radius mill, dovetail mill, slot mill, circle segment lens, turning general, turning boring, turning grooving
__publicField(Tool_V1, "geometry_cornerRadius_spec", { "title": "Corner Radius", "kind": "length", "defaultValue": 0.75, "for_types": ["bull nose end mill", "face mill", "tapered mill", "radius mill", "dovetail mill", "slot mill", "circle segment lens", "turning general", "turning boring", "turning grooving"] });
// ToolEndMill, ToolHoleMaking, circle segment barrel, probe
__publicField(Tool_V1, "geometry_shaftDiameter_spec", { "title": "Shaft Diameter", "kind": "length", "defaultValue": 6, "for_types": ["ToolEndMill", "ToolHoleMaking", "circle segment barrel", "probe"] });
// ToolMilling, ToolHoleMaking, turning grooving, turning threading
__publicField(Tool_V1, "geometry_overallLength_spec", { "title": "Overall Length", "kind": "length", "defaultValue": 36, "for_types": ["ToolMilling", "ToolHoleMaking", "turning grooving", "turning threading"] });
// ToolMilling, ToolHoleMaking, probe
__publicField(Tool_V1, "geometry_protrudingLength_spec", { "title": "Length below holder", "kind": "length", "defaultValue": 24, "for_types": ["ToolMilling", "ToolHoleMaking", "probe"] });
// ToolEndMill, ToolHoleMaking
__publicField(Tool_V1, "geometry_shoulderLength_spec", { "title": "Shoulder Length", "kind": "length", "defaultValue": 21, "for_types": ["ToolEndMill", "ToolHoleMaking"] });
// ToolEndMill, ToolCircleSegmentMill
__publicField(Tool_V1, "geometry_shoulderDiameter_spec", { "title": "Shoulder Diameter", "kind": "length", "for_types": ["ToolEndMill", "ToolCircleSegmentMill"], "hidden": true });
// ToolMilling, ToolHoleMaking
__publicField(Tool_V1, "geometry_fluteLength_spec", { "title": "Flute Length", "kind": "length", "defaultValue": 18, "for_types": ["ToolMilling", "ToolHoleMaking"] });
// ToolMilling, ToolHoleMaking
__publicField(Tool_V1, "geometry_coolantSupport_spec", { "title": "Coolant Support", "kind": "boolean", "defaultValue": false, "for_types": ["ToolMilling", "ToolHoleMaking"] });
// tapered mill
__publicField(Tool_V1, "geometry_taperedType_spec", { "title": "Tapered Type", "kind": "enum", "defaultValue": "tapered_bull_nose", "for_types": ["tapered mill"] });
// face mill, tapered mill, chamfer mill, dovetail mill, circle segment taper, center drill
__publicField(Tool_V1, "geometry_taperAngle_spec", { "title": "Taper Angle", "kind": "angle", "defaultValue": 10, "for_types": ["face mill", "tapered mill", "chamfer mill", "dovetail mill", "circle segment taper", "center drill"] });
// thread mill
__publicField(Tool_V1, "geometry_numberOfTeeth_spec", { "title": "Number of Teeth", "kind": "integer", "defaultValue": 1, "for_types": ["thread mill"] });
// ToolTap, thread mill, turning threading
__publicField(Tool_V1, "geometry_threadPitch_spec", { "title": "Thread Pitch", "kind": "length", "defaultValue": 1, "for_types": ["ToolTap", "thread mill", "turning threading"] });
// thread mill, turning threading
__publicField(Tool_V1, "geometry_threadProfileAngle_spec", { "title": "Thread Profile Angle", "kind": "angle", "defaultValue": 60, "for_types": ["thread mill", "turning threading"] });
// ToolDrill, counter sink
__publicField(Tool_V1, "geometry_tipAngle_spec", { "title": "Tip Angle", "kind": "angle", "defaultValue": 120, "for_types": ["ToolDrill", "counter sink"] });
// chamfer mill, circle segment taper, center drill, spot drill, counter sink
__publicField(Tool_V1, "geometry_tipDiameter_spec", { "title": "Tip Diameter", "kind": "length", "defaultValue": 21, "for_types": ["chamfer mill", "circle segment taper", "center drill", "spot drill", "counter sink"] });
// radius mill, center drill
__publicField(Tool_V1, "geometry_tipLength_spec", { "title": "Tip Length", "kind": "length", "defaultValue": 2, "for_types": ["radius mill", "center drill"] });
// form mill
__publicField(Tool_V1, "geometry_tipOffset_spec", { "title": "Tip Offset", "kind": "length", "defaultValue": 0, "for_types": ["form mill"] });
// circle segment barrel
__publicField(Tool_V1, "geometry_axialDistance_spec", { "title": "Axial Distance", "kind": "length", "defaultValue": 4, "for_types": ["circle segment barrel"] });
// circle segment barrel, circle segment oval, circle segment taper
__publicField(Tool_V1, "geometry_profileRadius_spec", { "title": "Profile Radius", "kind": "length", "defaultValue": 8, "for_types": ["circle segment barrel", "circle segment oval", "circle segment taper"] });
// circle segment barrel, circle segment taper
__publicField(Tool_V1, "geometry_upperRadius_spec", { "title": "Upper Radius", "kind": "length", "defaultValue": 1, "for_types": ["circle segment barrel", "circle segment taper"] });
// ToolCircleSegmentMill
__publicField(Tool_V1, "geometry_lowerRadius_spec", { "title": "Lower Radius", "kind": "length", "defaultValue": 1, "for_types": ["ToolCircleSegmentMill"] });
// ToolMilling, ToolHoleMaking, probe
__publicField(Tool_V1, "geometry_assemblyGaugeLength_spec", { "title": "Tool Assembly Gauge Length", "kind": "length", "for_types": ["ToolMilling", "ToolHoleMaking", "probe"] });
// Tool
__publicField(Tool_V1, "post_number_spec", { "title": "Number", "kind": "integer", "defaultValue": 1, "for_types": ["Tool"] });
// ToolMilling, ToolHoleMaking, ToolCutting, ToolDepositing, probe
__publicField(Tool_V1, "post_lengthOffset_spec", { "title": "Length Offset", "kind": "integer", "defaultValue": 1, "for_types": ["ToolMilling", "ToolHoleMaking", "ToolCutting", "ToolDepositing", "probe"] });
// ToolMilling, ToolHoleMaking, ToolCutting, probe
__publicField(Tool_V1, "post_diameterOffset_spec", { "title": "Diameter Offset", "kind": "integer", "defaultValue": 1, "for_types": ["ToolMilling", "ToolHoleMaking", "ToolCutting", "probe"] });
// ToolMilling, ToolHoleMaking, ToolTurning, probe
__publicField(Tool_V1, "post_turret_spec", { "title": "Turret", "kind": "integer", "defaultValue": 0, "for_types": ["ToolMilling", "ToolHoleMaking", "ToolTurning", "probe"] });
// Tool
__publicField(Tool_V1, "post_comment_spec", { "title": "Comment", "kind": "text", "defaultValue": "", "for_types": ["Tool"] });
// ToolMilling, ToolHoleMaking, ToolTurning, ToolCutting, probe
__publicField(Tool_V1, "post_manualToolChange_spec", { "title": "Manual Tool Change", "kind": "boolean", "defaultValue": false, "for_types": ["ToolMilling", "ToolHoleMaking", "ToolTurning", "ToolCutting", "probe"] });
// ToolMilling, ToolHoleMaking, probe
__publicField(Tool_V1, "post_live_spec", { "title": "Live Tool", "kind": "boolean", "defaultValue": true, "for_types": ["ToolMilling", "ToolHoleMaking", "probe"] });
// ToolMilling, ToolHoleMaking, ToolTurning, probe
__publicField(Tool_V1, "post_breakControl_spec", { "title": "Break Control", "kind": "boolean", "defaultValue": false, "for_types": ["ToolMilling", "ToolHoleMaking", "ToolTurning", "probe"] });
// ToolMilling, ToolHoleMaking, probe
__publicField(Tool_V1, "shaft_segments_spec", { "title": "Shaft Segments", "kind": "segments", "for_types": ["ToolMilling", "ToolHoleMaking", "probe"] });
// ToolMilling, ToolHoleMaking, holder, probe
__publicField(Tool_V1, "holder_unit_spec", { "title": "Unit", "kind": "enum", "defaultValue": "millimeters", "for_types": ["ToolMilling", "ToolHoleMaking", "holder", "probe"] });
// ToolMilling, ToolHoleMaking, holder, probe
__publicField(Tool_V1, "holder_guid_spec", { "title": "Holder GUID", "kind": "guid", "for_types": ["ToolMilling", "ToolHoleMaking", "holder", "probe"], "hidden": true });
// ToolMilling, ToolHoleMaking, holder, probe
__publicField(Tool_V1, "holder_description_spec", { "title": "Holder Description", "kind": "text", "defaultValue": "", "for_types": ["ToolMilling", "ToolHoleMaking", "holder", "probe"] });
// ToolMilling, ToolHoleMaking, holder, probe
__publicField(Tool_V1, "holder_productId_spec", { "title": "Holder Product ID", "kind": "text", "defaultValue": "", "for_types": ["ToolMilling", "ToolHoleMaking", "holder", "probe"] });
// ToolMilling, ToolHoleMaking, holder, probe
__publicField(Tool_V1, "holder_productLink_spec", { "title": "Holder Product Link", "kind": "text", "defaultValue": "", "for_types": ["ToolMilling", "ToolHoleMaking", "holder", "probe"] });
// ToolMilling, ToolHoleMaking, holder, probe
__publicField(Tool_V1, "holder_vendor_spec", { "title": "Holder Vendor", "kind": "text", "defaultValue": "", "for_types": ["ToolMilling", "ToolHoleMaking", "holder", "probe"] });
// ToolMilling, ToolHoleMaking, holder, probe
__publicField(Tool_V1, "holder_gaugeLength_spec", { "title": "Holder Gauge Length", "kind": "length", "for_types": ["ToolMilling", "ToolHoleMaking", "holder", "probe"] });
// ToolMilling, ToolHoleMaking, holder, probe
__publicField(Tool_V1, "holder_segments_spec", { "title": "Holder Segments", "kind": "segments", "for_types": ["ToolMilling", "ToolHoleMaking", "holder", "probe"] });
__publicField(Tool_V1, "version", 1);

// src/util/unit.ts
function getUnitScaleFactor(fromUnit, toUnit) {
  const conversionRates = {
    mm: 1,
    // base unit
    millimeters: 1,
    // base unit
    in: 25.4,
    // 1 inch = 25.4 millimeters
    inches: 25.4
    // 1 inch = 25.4 millimeters
  };
  if (!conversionRates[fromUnit] || !conversionRates[toUnit]) {
    throw new Error("Unsupported units");
  }
  return conversionRates[fromUnit] / conversionRates[toUnit];
}

// src/fusion/fusion_tools_csv.ts
var FUSION_CSV_HEADER_LAST_COL = "CSV_TOOLS_VERSION_1";
function fusion_csv_input(csv) {
  csv = csv.replace(/\r\n/g, "\n");
  let lines = csv.split("\n");
  const header = lines.shift();
  if (header === void 0) {
    throw new Error("CSV input is empty");
  }
  const useTabs = header.indexOf("	") !== -1;
  const columns = splitCSV(header, useTabs);
  const version_id = columns.pop();
  if (version_id === void 0 || version_id !== FUSION_CSV_HEADER_LAST_COL) {
    throw new Error("Unrecognized Fusion CSV format: expected " + FUSION_CSV_HEADER_LAST_COL);
  }
  let csv_id2index = {};
  let csv_index2id = [];
  columns.forEach((csv_id, index) => {
    const match = csv_id.match(/\(([^)]+)\)$/);
    if (match) {
      csv_id = match[1];
    }
    csv_index2id.push(csv_id);
    csv_id2index[csv_id] = index;
  });
  fusion_csv_index2id.forEach((value) => {
    if (csv_id2index[value] === void 0) {
      throw new Error("Unrecognized Fusion CSV format: missing field " + value);
    }
  });
  const INDEX_COLUMN = csv_id2index["tool_index"];
  if (INDEX_COLUMN === void 0) {
    throw new Error("Unrecognized Fusion CSV format: missing tool_index");
  }
  let rows = [];
  for (const line of lines) {
    if (line.trim() === "")
      continue;
    rows.push(splitCSV(line, useTabs));
  }
  let objects = [];
  for (const row of rows) {
    let object = {};
    row.forEach((value, index) => {
      const csv_id = csv_index2id[index];
      object[csv_id] = value;
    });
    objects.push(object);
  }
  let grouped_objects = /* @__PURE__ */ new Map();
  for (const object of objects) {
    const tool_index = object["tool_index"];
    if (grouped_objects.has(tool_index)) {
      grouped_objects.get(tool_index).push(object);
    } else {
      grouped_objects.set(tool_index, [object]);
    }
  }
  let tools = [];
  for (const [key, group] of grouped_objects.entries()) {
    let first_object = group[0];
    let tool = toolFromRowData(first_object);
    for (const object of group) {
      if (tool.type != "holder") {
        addPresetFromRowData(tool, object);
      }
    }
    tools.push(tool);
  }
  return tools;
}
function parseCsvSegments(segmentsString) {
  let segments = [];
  if (typeof segmentsString !== "string")
    return segments;
  let parts = segmentsString.split(";");
  for (let part of parts) {
    part = part.trim();
    let segment = { height: 0, upperDiameter: 0, lowerDiameter: 0 };
    let subparts = part.split(" ");
    for (const subpart of subparts) {
      if (subpart.startsWith("H"))
        segment.height = parseFloat(subpart.substring(1));
      else if (subpart.startsWith("U"))
        segment.upperDiameter = parseFloat(subpart.substring(1));
      else if (subpart.startsWith("L"))
        segment.lowerDiameter = parseFloat(subpart.substring(1));
      else
        throw new Error("Error parsing segments: " + segmentsString);
    }
    segments.push(segment);
  }
  return segments;
}
function toCsvSegments(segments) {
  let parts = [];
  for (const segment of segments) {
    if (segment.height !== void 0 && segment.upperDiameter !== void 0 && segment.lowerDiameter !== void 0) {
      parts.push(
        `H${segment.height.toFixed(6)} U${segment.upperDiameter.toFixed(6)} L${segment.lowerDiameter.toFixed(6)}`
      );
    }
  }
  return parts.join("; ");
}
function parseValue(value, kind) {
  if (kind === "integer" || kind === "datetime") {
    return parseInt(value);
  }
  if ([
    "length",
    "feedrate",
    "rotation_speed",
    "surface_speed",
    "unitless",
    "angle",
    "current",
    "lpm",
    "volts",
    "watts"
  ].includes(kind)) {
    return parseFloat(value);
  }
  if (["text", "enum", "guid"].includes(kind)) {
    return String(value);
  }
  if (kind === "boolean") {
    if (typeof value === "string") {
      return ["true", "on", "yes"].includes(value.toLowerCase());
    } else if (typeof value !== "boolean") {
      console.log("Expected bool for kind", kind, "got", value);
    }
    return value;
  }
  console.log("Unsupported kind", kind);
  return value;
}
function setProp(obj, path, value, kind, defaultValue) {
  const pathArray = Array.isArray(path) ? path : path.split(".");
  const key = pathArray[0];
  if (pathArray.length === 1) {
    if (value === void 0 || value.length === 0) {
      value = defaultValue;
    }
    if (value === void 0)
      return;
    if (kind === "segments")
      value = parseCsvSegments(value);
    else
      value = parseValue(value, kind);
    if (kind === "boolean" && value === "R")
      value = true;
    obj[key] = value;
    return;
  }
  if (!obj[key] || typeof obj[key] !== "object") {
    obj[key] = {};
  }
  setProp(obj[key], pathArray.slice(1), value, kind, defaultValue);
}
function getProp(obj, path, kind, defaultValue) {
  const pathArray = Array.isArray(path) ? path : path.split(".");
  const key = pathArray[0];
  if (pathArray.length === 1) {
    let value = void 0;
    if (obj)
      value = obj[pathArray[0]];
    if (value === void 0 || value.length === 0) {
      value = defaultValue;
    }
    if (value === void 0) {
      value = "";
    }
    if (typeof value !== "string") {
      if (kind === "segments")
        return toCsvSegments(value);
      if (kind === "integer")
        return value.toString();
      if (kind === "length")
        return value.toString();
      if (kind === "unitless")
        return value.toString();
      if (kind === "angle")
        return value.toString();
      if (kind === "boolean")
        return value.toString();
    }
    return value;
  }
  return getProp(obj[key], pathArray.slice(1), kind, defaultValue);
}
function toolFromRowData(object) {
  let tool = { ...new Tool_V1(), presets: [] };
  for (const field of fusion_all_tool_fields) {
    let id = field.id;
    if (id === void 0)
      id = "csv_extra." + field.csv_id;
    if (field.csv_id) {
      const value = object[field.csv_id];
      setProp(tool, id, value, field.kind, void 0);
    }
  }
  return tool;
}
function addPresetFromRowData(tool, object) {
  let preset = new Preset();
  for (const field of fusion_all_preset_fields) {
    let id = field.id;
    if (id === void 0)
      id = "csv_extra." + field.csv_id;
    if (field.csv_id) {
      const value = object[field.csv_id];
      setProp(preset, id, value, field.kind, void 0);
    }
  }
  tool.presets.push(preset);
}
function scaleHolderUnit(tool) {
  if (tool.holder?.segments) {
    const tool_unit = tool?.unit;
    const holder_unit = tool.holder?.unit;
    if (holder_unit && tool_unit !== holder_unit) {
      const scale_factor = getUnitScaleFactor(holder_unit, tool_unit);
      tool.holder = { ...tool.holder };
      tool.holder.segments = scale_numbers(tool.holder.segments, scale_factor);
    }
  }
}
function fusion_csv_output(tools, useTabs = false) {
  let header = fusion_csv_columns;
  header.push(FUSION_CSV_HEADER_LAST_COL);
  let csv_fields = [];
  fusion_csv_columns.forEach((item) => {
    const match = item.match(/\(([^)]+)\)$/);
    if (match) {
      const csv_id = match[1];
      const field = fusion_field_by_csv_id[csv_id];
      if (!field) {
        throw new Error(`CSV field for column ${item} not found`);
      }
      csv_fields.push(field);
    }
  });
  if (!useTabs) {
    header = header.map((name) => `"${name}"`);
  }
  let output = joinCSV(header, useTabs) + "\n";
  let toolsWithPresets = [];
  let index = 1;
  for (let tool of tools) {
    if (!tool.presets || tool.presets.length === 0) {
      let toolWithPreset = { index, ...tool };
      scaleHolderUnit(toolWithPreset);
      toolsWithPresets.push(toolWithPreset);
    } else {
      for (const preset of tool.presets) {
        let toolWithPreset = { index, ...preset, ...tool };
        scaleHolderUnit(toolWithPreset);
        toolsWithPresets.push(toolWithPreset);
      }
    }
    index += 1;
  }
  for (let toolWithPreset of toolsWithPresets) {
    const row = [];
    for (let field of csv_fields) {
      let id = field.id;
      if (id === void 0)
        id = "csv_extra." + field.csv_id;
      let value = getProp(toolWithPreset, id, field.kind, void 0);
      if (field.csv_id === "tool_coolantSupport" && value !== void 0 && value !== "") {
        value = value === true ? '"yes"' : '"no"';
      }
      if (field.csv_id === "tool_hand" && value !== void 0 && value !== "") {
        value = !!value ? '"R"' : '"L"';
      }
      if (!useTabs) {
        if (["text", "enum", "segments"].includes(field.kind)) {
          if (typeof value !== "string")
            throw new Error(`Expecting type string but got ${typeof value} for: ${id} = ${value}`);
          value = value.replace(/"/g, '""');
          value = `"${value}"`;
        }
        if (value === "") {
          value = `"${value}"`;
        }
      }
      row.push(value);
    }
    output += joinCSV(row, useTabs) + "\n";
  }
  return output;
}
function scale_numbers(arr, scaleFactor) {
  return arr.map(
    (item) => Object.fromEntries(
      Object.entries(item).map(([key, value]) => [key, typeof value === "number" ? value * scaleFactor : value])
    )
  );
}

// src/fusion/fusion_tools_f3d.ts
import JSZip from "jszip";

// src/magic/make_preset.ts
import Fuse from "fuse.js";

// src/util/approx.ts
var EPSILON = 1e-9;
function isApprox(a, b, eps = EPSILON) {
  return Math.abs(a - b) <= eps;
}

// src/magic/bilinear_interpolation.ts
function bi_lin_interp(x, y, x1, y1, x2, y2, Q11, Q12, Q21, Q22) {
  if (x < x1) {
    x = x1;
  }
  if (x > x2) {
    x = x2;
  }
  if (y < y1) {
    y = y1;
  }
  if (y > y2) {
    y = y2;
  }
  if (Math.abs(x1 - x2) < 1e-8) {
    return ((y2 - y) * Q11 + (y - y1) * Q12) / (y2 - y1);
  }
  if (Math.abs(y1 - y2) < 1e-8) {
    return ((x2 - x) * Q11 + (x - x1) * Q21) / (x2 - x1);
  }
  let denom = (x2 - x1) * (y2 - y1);
  let fxy1 = ((x2 - x) * Q11 + (x - x1) * Q21) / (x2 - x1);
  let fxy2 = ((x2 - x) * Q12 + (x - x1) * Q22) / (x2 - x1);
  let fxy = ((y2 - y) * fxy1 + (y - y1) * fxy2) / (y2 - y1);
  return fxy;
}
function find_grid_cell(x, y, xArray, yArray, dataArray) {
  let x1Index = 0, x2Index = 0, searchX = true;
  let y1Index = 0, y2Index = 0, searchY = true;
  if (x < xArray[0]) {
    x1Index = 0;
    x2Index = 1;
    searchX = false;
  }
  if (x > xArray[xArray.length - 1]) {
    x1Index = xArray.length - 2;
    x2Index = xArray.length - 1;
    searchX = false;
  }
  if (y < yArray[0]) {
    y1Index = 0;
    y2Index = 1;
    searchY = false;
  }
  if (y > yArray[yArray.length - 1]) {
    y1Index = yArray.length - 2;
    y2Index = yArray.length - 1;
    searchY = false;
  }
  if (searchX) {
    for (let i = 0; i < xArray.length - 1; i++) {
      if (xArray[i] <= x && xArray[i + 1] >= x) {
        x1Index = i;
        x2Index = i + 1;
        break;
      }
    }
  }
  if (searchY) {
    for (let j = 0; j < yArray.length - 1; j++) {
      if (yArray[j] <= y && yArray[j + 1] >= y) {
        y1Index = j;
        y2Index = j + 1;
        break;
      }
    }
  }
  const x1 = xArray[x1Index];
  const x2 = xArray[x2Index];
  const y1 = yArray[y1Index];
  const y2 = yArray[y2Index];
  const Q11 = dataArray[y1Index][x1Index];
  const Q12 = dataArray[y2Index][x1Index];
  const Q21 = dataArray[y1Index][x2Index];
  const Q22 = dataArray[y2Index][x2Index];
  return {
    x1,
    y1,
    x2,
    y2,
    Q11,
    Q12,
    Q21,
    Q22
  };
}
var BilinearInterpolate = class {
  constructor(xs, ys, vals) {
    __publicField(this, "xs");
    __publicField(this, "ys");
    __publicField(this, "vals");
    this.xs = xs;
    this.ys = ys;
    this.vals = vals;
    if (xs.length != vals.length) {
      throw Error(`size mismatch: xs.length=${xs.length}, vals.length=${vals.length}`);
    }
    for (let i = 0; i < vals.length; i++) {
      if (ys.length != vals[i].length) {
        throw Error(`size mismatch: ys.length=${ys.length}, vals[${i}].length=${vals[i].length}`);
      }
    }
  }
  eval(x, y) {
    const { x1, y1, x2, y2, Q11, Q12, Q21, Q22 } = find_grid_cell(x, y, this.xs, this.ys, this.vals);
    return bi_lin_interp(x, y, x1, y1, x2, y2, Q11, Q12, Q21, Q22);
  }
};
function interp_tool_data(model, DATA, diam, AR) {
  var xArray = DATA["DIAM_VALS"];
  var yArray = DATA["AR_VALS"];
  var INTERP_DATA = {};
  INTERP_DATA["FACING_ACTIVE"] = DATA["FACING_ACTIVE"];
  INTERP_DATA["ROUGHING_ACTIVE"] = DATA["ROUGHING_ACTIVE"];
  INTERP_DATA["FINISHING_ACTIVE"] = DATA["FINISHING_ACTIVE"];
  INTERP_DATA["SLOTTING_ACTIVE"] = DATA["SLOTTING_ACTIVE"];
  INTERP_DATA["DRILLING_ACTIVE"] = DATA["DRILLING_ACTIVE"];
  INTERP_DATA["FIXED_ENDMILL_PLUNGE_ACTIVE"] = DATA["FIXED_ENDMILL_PLUNGE_ACTIVE"];
  INTERP_DATA["FIXED_ENDMILL_PLUNGE"] = DATA["FIXED_ENDMILL_PLUNGE"];
  const inp_facing = {
    purpose: 3 /* FACING */,
    numberOfFlutes: DATA.numberOfFlutes,
    isReducedShank: DATA.isReducedShank,
    cuttingDiameter: diam,
    aspectRatio: AR
  };
  INTERP_DATA["FACING_CL"] = model.eval_cl(inp_facing);
  INTERP_DATA["FACING_DOC"] = model.eval_doc(inp_facing);
  INTERP_DATA["FACING_WOC"] = model.eval_woc(inp_facing);
  INTERP_DATA["FACING_VC"] = model.eval_vc(inp_facing);
  const inp_roughing = { ...inp_facing, purpose: 1 /* ROUGHING */ };
  INTERP_DATA["ROUGHING_CL"] = model.eval_cl(inp_roughing);
  INTERP_DATA["ROUGHING_DOC"] = model.eval_doc(inp_roughing);
  INTERP_DATA["ROUGHING_WOC"] = model.eval_woc(inp_roughing);
  INTERP_DATA["ROUGHING_VC"] = model.eval_vc(inp_roughing);
  const inp_finishing = { ...inp_facing, purpose: 2 /* FINISHING */ };
  INTERP_DATA["FINISHING_CL"] = model.eval_cl(inp_finishing);
  INTERP_DATA["FINISHING_DOC"] = model.eval_doc(inp_finishing);
  INTERP_DATA["FINISHING_WOC"] = model.eval_woc(inp_finishing);
  INTERP_DATA["FINISHING_VC"] = model.eval_vc(inp_finishing);
  const inp_slotting = { ...inp_facing, purpose: 0 /* SLOTTING */ };
  INTERP_DATA["SLOTTING_CL"] = model.eval_cl(inp_slotting);
  INTERP_DATA["SLOTTING_DOC"] = model.eval_doc(inp_slotting);
  INTERP_DATA["SLOTTING_VC"] = model.eval_vc(inp_slotting);
  INTERP_DATA["DRILLING_CL"] = DATA["DRILLING_CL"];
  INTERP_DATA["DRILLING_CL"] *= 0.01;
  var dataArray = DATA["ROUGHING_RAMP"];
  var { x1, y1, x2, y2, Q11, Q12, Q21, Q22 } = find_grid_cell(diam, AR, xArray, yArray, dataArray);
  INTERP_DATA["ROUGHING_RAMP"] = bi_lin_interp(diam, AR, x1, y1, x2, y2, Q11, Q12, Q21, Q22);
  INTERP_DATA["ROUGHING_RAMP"] *= 0.01;
  return INTERP_DATA;
}

// src/magic/datasheets.ts
var default_datasheets = {
  "AluWrought-2F-FF": {
    PREFIX: "AluWrought",
    SFM: 1e3,
    DIAM_VALS: [[0.03], [0.625]],
    AR_VALS: [[3], [5]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [10, 5],
      [10, 5]
    ],
    FACING_CL: [
      [1, 1],
      [1, 1]
    ],
    FACING_ACTIVE: true,
    ROUGHING_WOC: [
      [35, 20],
      [15, 10]
    ],
    ROUGHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    ROUGHING_CL: [
      [1.3, 1.1],
      [1, 1]
    ],
    ROUGHING_RAMP: [
      [50, 50],
      [35, 35]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [3, 3],
      [2, 2]
    ],
    FINISHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    FINISHING_CL: [
      [0.75, 0.75],
      [0.65, 0.65]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [35, 35],
      [20, 20]
    ],
    SLOTTING_CL: [
      [1, 1],
      [0.75, 0.75]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "AluWrought-3F-FF": {
    PREFIX: "AluWrought",
    SFM: 1e3,
    DIAM_VALS: [[0.03], [0.625]],
    AR_VALS: [[3], [5]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [10, 5],
      [10, 5]
    ],
    FACING_CL: [
      [1, 1],
      [1, 1]
    ],
    FACING_ACTIVE: true,
    ROUGHING_WOC: [
      [35, 20],
      [15, 10]
    ],
    ROUGHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    ROUGHING_CL: [
      [1.5, 1.25],
      [1, 1]
    ],
    ROUGHING_RAMP: [
      [50, 50],
      [35, 35]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [3, 3],
      [2, 2]
    ],
    FINISHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    FINISHING_CL: [
      [0.75, 0.75],
      [0.65, 0.65]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [35, 35],
      [20, 20]
    ],
    SLOTTING_CL: [
      [1, 1],
      [0.75, 0.75]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "AluWrought-4F-FF": {
    PREFIX: "AluWrought",
    SFM: 1e3,
    DIAM_VALS: [[0.03], [0.625]],
    AR_VALS: [[3], [5]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [10, 5],
      [10, 5]
    ],
    FACING_CL: [
      [1, 1],
      [1, 1]
    ],
    FACING_ACTIVE: true,
    ROUGHING_WOC: [
      [30, 20],
      [13, 10]
    ],
    ROUGHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    ROUGHING_CL: [
      [1.5, 1.3],
      [1, 1]
    ],
    ROUGHING_RAMP: [
      [50, 50],
      [35, 35]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [3, 3],
      [2, 2]
    ],
    FINISHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    FINISHING_CL: [
      [0.75, 0.75],
      [0.65, 0.65]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [35, 35],
      [20, 20]
    ],
    SLOTTING_CL: [
      [1, 1],
      [0.75, 0.75]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "AluWrought-3F-RS": {
    PREFIX: "AluWrought",
    SFM: 1e3,
    DIAM_VALS: [[0.03], [0.5]],
    AR_VALS: [[5], [10]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [0.3, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.3, 1],
      [1, 1]
    ],
    FACING_ACTIVE: false,
    ROUGHING_WOC: [
      [30, 60],
      [25, 50]
    ],
    ROUGHING_DOC: [
      [30, 30],
      [30, 30]
    ],
    ROUGHING_CL: [
      [1.4, 1.25],
      [0.8, 0.8]
    ],
    ROUGHING_RAMP: [
      [25, 25],
      [20, 20]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [0.8, 1.5],
      [2, 2]
    ],
    FINISHING_DOC: [
      [150, 150],
      [150, 150]
    ],
    FINISHING_CL: [
      [2, 1.7],
      [0.65, 0.65]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [8, 20],
      [7, 18]
    ],
    SLOTTING_CL: [
      [1, 0.7],
      [0.7, 0.6]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "LowCSteel-4F-FF": {
    PREFIX: "LowCSteel",
    SFM: 600,
    DIAM_VALS: [[0.03], [0.625]],
    AR_VALS: [[3], [5]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [10, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.4, 0.4],
      [0.4, 0.4]
    ],
    FACING_ACTIVE: true,
    ROUGHING_WOC: [
      [15, 13],
      [13, 8]
    ],
    ROUGHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    ROUGHING_CL: [
      [0.4, 0.4],
      [0.4, 0.4]
    ],
    ROUGHING_RAMP: [
      [50, 50],
      [35, 35]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [3, 3],
      [2, 2]
    ],
    FINISHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    FINISHING_CL: [
      [0.3, 0.3],
      [0.3, 0.3]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [35, 35],
      [20, 20]
    ],
    SLOTTING_CL: [
      [0.35, 0.35],
      [0.35, 0.35]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "LowCSteel-5F-FF": {
    PREFIX: "LowCSteel",
    SFM: 600,
    DIAM_VALS: [[0.03], [0.625]],
    AR_VALS: [[3], [5]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [10, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.4, 0.4],
      [0.4, 0.4]
    ],
    FACING_ACTIVE: true,
    ROUGHING_WOC: [
      [15, 13],
      [13, 8]
    ],
    ROUGHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    ROUGHING_CL: [
      [0.3, 0.3],
      [0.3, 0.3]
    ],
    ROUGHING_RAMP: [
      [50, 50],
      [35, 35]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [1.5, 1.5],
      [1, 1]
    ],
    FINISHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    FINISHING_CL: [
      [0.25, 0.25],
      [0.25, 0.25]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [35, 35],
      [20, 20]
    ],
    SLOTTING_CL: [
      [0.2, 0.2],
      [0.15, 0.14]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "LowCSteel-6F-FF": {
    PREFIX: "LowCSteel",
    SFM: 600,
    DIAM_VALS: [[0.03], [0.625]],
    AR_VALS: [[3], [5]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [10, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.4, 0.4],
      [0.4, 0.4]
    ],
    FACING_ACTIVE: true,
    ROUGHING_WOC: [
      [13, 10],
      [10, 8]
    ],
    ROUGHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    ROUGHING_CL: [
      [0.8, 0.8],
      [0.6, 0.6]
    ],
    ROUGHING_RAMP: [
      [30, 30],
      [25, 25]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [1.5, 1.5],
      [1, 1]
    ],
    FINISHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    FINISHING_CL: [
      [0.25, 0.25],
      [0.25, 0.25]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [35, 35],
      [20, 20]
    ],
    SLOTTING_CL: [
      [0.2, 0.2],
      [0.15, 0.14]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "LowCSteel-3F-RS": {
    PREFIX: "LowCSteel",
    SFM: 600,
    DIAM_VALS: [[0.03], [0.5]],
    AR_VALS: [[5], [10]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [0.3, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.3, 1],
      [1, 1]
    ],
    FACING_ACTIVE: false,
    ROUGHING_WOC: [
      [30, 60],
      [25, 50]
    ],
    ROUGHING_DOC: [
      [30, 30],
      [30, 30]
    ],
    ROUGHING_CL: [
      [0.4, 0.4],
      [0.3, 0.3]
    ],
    ROUGHING_RAMP: [
      [25, 25],
      [20, 20]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [0.8, 1.5],
      [0.6, 1.2]
    ],
    FINISHING_DOC: [
      [150, 150],
      [150, 150]
    ],
    FINISHING_CL: [
      [0.6, 0.5],
      [0.5, 0.4]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [8, 20],
      [7, 18]
    ],
    SLOTTING_CL: [
      [0.4, 0.3],
      [0.2, 0.2]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "LowCSteel-4F-RS": {
    PREFIX: "LowCSteel",
    SFM: 600,
    DIAM_VALS: [[0.03], [0.5]],
    AR_VALS: [[5], [10]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [0.3, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.3, 1],
      [1, 1]
    ],
    FACING_ACTIVE: false,
    ROUGHING_WOC: [
      [30, 60],
      [25, 50]
    ],
    ROUGHING_DOC: [
      [30, 30],
      [30, 30]
    ],
    ROUGHING_CL: [
      [0.4, 0.4],
      [0.3, 0.3]
    ],
    ROUGHING_RAMP: [
      [25, 25],
      [20, 20]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [0.8, 1.5],
      [0.6, 1.2]
    ],
    FINISHING_DOC: [
      [150, 150],
      [150, 150]
    ],
    FINISHING_CL: [
      [0.6, 0.5],
      [0.5, 0.4]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [8, 20],
      [7, 18]
    ],
    SLOTTING_CL: [
      [0.4, 0.3],
      [0.2, 0.2]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "StainlessSteel-4F-FF": {
    PREFIX: "StainlessSteel",
    SFM: 200,
    DIAM_VALS: [[0.03], [0.625]],
    AR_VALS: [[3], [5]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [10, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.4, 0.4],
      [0.4, 0.4]
    ],
    FACING_ACTIVE: true,
    ROUGHING_WOC: [
      [15, 13],
      [13, 8]
    ],
    ROUGHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    ROUGHING_CL: [
      [0.4, 0.4],
      [0.4, 0.4]
    ],
    ROUGHING_RAMP: [
      [50, 50],
      [35, 35]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [3, 3],
      [2, 2]
    ],
    FINISHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    FINISHING_CL: [
      [0.3, 0.3],
      [0.3, 0.3]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [35, 35],
      [20, 20]
    ],
    SLOTTING_CL: [
      [0.35, 0.35],
      [0.35, 0.35]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "StainlessSteel-5F-FF": {
    PREFIX: "StainlessSteel",
    SFM: 200,
    DIAM_VALS: [[0.03], [0.625]],
    AR_VALS: [[3], [5]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [10, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.4, 0.4],
      [0.4, 0.4]
    ],
    FACING_ACTIVE: true,
    ROUGHING_WOC: [
      [15, 13],
      [13, 8]
    ],
    ROUGHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    ROUGHING_CL: [
      [0.3, 0.3],
      [0.3, 0.3]
    ],
    ROUGHING_RAMP: [
      [50, 50],
      [35, 35]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [1.5, 1.5],
      [1, 1]
    ],
    FINISHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    FINISHING_CL: [
      [0.25, 0.25],
      [0.25, 0.25]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [35, 35],
      [20, 20]
    ],
    SLOTTING_CL: [
      [0.2, 0.2],
      [0.15, 0.14]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "StainlessSteel-6F-FF": {
    PREFIX: "StainlessSteel",
    SFM: 200,
    DIAM_VALS: [[0.03], [0.625]],
    AR_VALS: [[3], [5]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [10, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.4, 0.4],
      [0.4, 0.4]
    ],
    FACING_ACTIVE: true,
    ROUGHING_WOC: [
      [13, 10],
      [10, 8]
    ],
    ROUGHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    ROUGHING_CL: [
      [0.8, 0.8],
      [0.6, 0.6]
    ],
    ROUGHING_RAMP: [
      [30, 30],
      [25, 25]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [1.5, 1.5],
      [1, 1]
    ],
    FINISHING_DOC: [
      [100, 100],
      [100, 100]
    ],
    FINISHING_CL: [
      [0.25, 0.25],
      [0.25, 0.25]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [35, 35],
      [20, 20]
    ],
    SLOTTING_CL: [
      [0.2, 0.2],
      [0.15, 0.14]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "StainlessSteel-3F-RS": {
    PREFIX: "StainlessSteel",
    SFM: 200,
    DIAM_VALS: [[0.03], [0.5]],
    AR_VALS: [[5], [10]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [0.3, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.3, 1],
      [1, 1]
    ],
    FACING_ACTIVE: false,
    ROUGHING_WOC: [
      [30, 60],
      [25, 50]
    ],
    ROUGHING_DOC: [
      [30, 30],
      [30, 30]
    ],
    ROUGHING_CL: [
      [0.4, 0.4],
      [0.3, 0.3]
    ],
    ROUGHING_RAMP: [
      [25, 25],
      [20, 20]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [0.8, 1.5],
      [0.6, 1.2]
    ],
    FINISHING_DOC: [
      [150, 150],
      [150, 150]
    ],
    FINISHING_CL: [
      [0.6, 0.5],
      [0.5, 0.4]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [8, 20],
      [7, 18]
    ],
    SLOTTING_CL: [
      [0.4, 0.3],
      [0.2, 0.2]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  },
  "StainlessSteel-4F-RS": {
    PREFIX: "StainlessSteel",
    SFM: 200,
    DIAM_VALS: [[0.03], [0.5]],
    AR_VALS: [[5], [10]],
    FACING_WOC: [
      [85, 85],
      [85, 85]
    ],
    FACING_DOC: [
      [0.3, 5],
      [10, 5]
    ],
    FACING_CL: [
      [0.3, 1],
      [1, 1]
    ],
    FACING_ACTIVE: false,
    ROUGHING_WOC: [
      [30, 60],
      [25, 50]
    ],
    ROUGHING_DOC: [
      [30, 30],
      [30, 30]
    ],
    ROUGHING_CL: [
      [0.4, 0.4],
      [0.3, 0.3]
    ],
    ROUGHING_RAMP: [
      [25, 25],
      [20, 20]
    ],
    ROUGHING_ACTIVE: true,
    FINISHING_WOC: [
      [0.8, 1.5],
      [0.6, 1.2]
    ],
    FINISHING_DOC: [
      [150, 150],
      [150, 150]
    ],
    FINISHING_CL: [
      [0.6, 0.5],
      [0.5, 0.4]
    ],
    FINISHING_ACTIVE: true,
    SLOTTING_DOC: [
      [8, 20],
      [7, 18]
    ],
    SLOTTING_CL: [
      [0.4, 0.3],
      [0.2, 0.2]
    ],
    SLOTTING_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE_ACTIVE: true,
    FIXED_ENDMILL_PLUNGE: 60
  }
};

// src/magic/drill_calcs.ts
function drillSFM(material) {
  const SFM_DEFAULT = 100;
  const SFM_DATA = {
    AluWrought: 250,
    //sheet.getRange("B4").getValue(),
    LowCSteel: 90,
    // sheet.getRange("B9").getValue(),
    StainlessSteel: 45
    // sheet.getRange("B11").getValue(),
  };
  if (material in SFM_DATA) {
    return SFM_DATA[material];
  }
  return SFM_DEFAULT;
}
function drillFeedPerRev(tool) {
  var diam = tool.geometry_cuttingDiameter;
  return diam * 0.01384269;
}

// src/magic/tap_calcs.ts
function tapSFM(material) {
  const SFM_DEFAULT = 20;
  const SFM_DATA = {
    AluWrought: 40,
    LowCSteel: 25,
    StainlessSteel: 20
  };
  if (material in SFM_DATA) {
    return SFM_DATA[material];
  }
  return SFM_DEFAULT;
}

// src/magic/tool_constants.ts
var FLAT_ENDMILL = "flat end mill";
var DRILL = "drill";
var BULL_NOSE_ENDMILL = "bull nose end mill";
var TAP_RIGHT = "tap right hand";
var TAP_LEFT = "tap left hand";
var BALL_ENDMILL = "ball end mill";
var CHAMFER_MILL = "chamfer mill";
var FACE_MILL = "face mill";
var KEYSEAT_CUTTER = "slot mill";
var SPOT_DRILL = "spot drill";
var CENTER_DRILL = "center drill";
var RADIUS_MILL = "radius mill";
var COUNTER_SINK = "counter sink";
var ENDMILL_LIKE = /* @__PURE__ */ new Set([FLAT_ENDMILL, BULL_NOSE_ENDMILL, BALL_ENDMILL]);
var DRILL_LIKE = /* @__PURE__ */ new Set([DRILL, SPOT_DRILL, CENTER_DRILL]);
var TAP_LIKE = /* @__PURE__ */ new Set([TAP_LEFT, TAP_RIGHT]);
var CAT40_ER16_HOLDER = {
  type: "holder",
  description: "CAT40 ER16",
  gaugeLength: 1.978507874,
  unit: "inches",
  segments: [
    {
      height: 0.0625,
      lowerDiameter: 0.9842519685,
      upperDiameter: 1.1023622047
    },
    {
      height: 0.6575,
      lowerDiameter: 1.1023622047,
      upperDiameter: 1.1023622047
    },
    {
      height: 0.25,
      lowerDiameter: 0.8661417323,
      upperDiameter: 0.8661417323
    },
    {
      height: 0.1423622047,
      lowerDiameter: 0.8661417323,
      upperDiameter: 1.1156338583
    },
    {
      height: 0.157480315,
      lowerDiameter: 1.811023622,
      upperDiameter: 1.811023622
    },
    {
      height: 0.09205905512,
      lowerDiameter: 1.811023622,
      upperDiameter: 1.09842519685
    },
    {
      height: 0.1308464567,
      lowerDiameter: 1.09842519685,
      upperDiameter: 1.09842519685
    },
    {
      height: 0.09205905512,
      lowerDiameter: 1.09842519685,
      upperDiameter: 1.811023622
    },
    {
      height: 0.2992125984,
      lowerDiameter: 1.811023622,
      upperDiameter: 1.811023622
    },
    {
      height: 0.0157480315,
      lowerDiameter: 1.811023622,
      upperDiameter: 1.7795275591
    },
    {
      height: 0.07874015748,
      lowerDiameter: 1.25,
      upperDiameter: 1.25
    }
  ]
};
var CAT40_ER25_HOLDER = {
  type: "holder",
  description: "CAT40 ER25",
  gaugeLength: 2.7083,
  unit: "inches",
  segments: [
    {
      height: 98e-4,
      lowerDiameter: 1.158,
      upperDiameter: 1.2178
    },
    {
      height: 59e-4,
      lowerDiameter: 1.2233,
      upperDiameter: 1.2437
    },
    {
      height: 0.0555,
      lowerDiameter: 1.2437,
      upperDiameter: 1.4361
    },
    {
      height: 0.0165,
      lowerDiameter: 1.4388,
      upperDiameter: 1.4961
    },
    {
      height: 0.0331,
      lowerDiameter: 1.4988,
      upperDiameter: 1.6133
    },
    {
      height: 0.0126,
      lowerDiameter: 1.6161,
      upperDiameter: 1.642
    },
    {
      height: 0.0189,
      lowerDiameter: 1.643,
      upperDiameter: 1.6543
    },
    {
      height: 0.5894,
      lowerDiameter: 1.6543,
      upperDiameter: 1.6543
    },
    {
      height: 0.0224,
      lowerDiameter: 1.6543,
      upperDiameter: 1.6401
    },
    {
      height: 0.0165,
      lowerDiameter: 1.6401,
      upperDiameter: 1.5844
    },
    {
      height: 0.2618,
      lowerDiameter: 1.2606,
      upperDiameter: 1.2606
    },
    {
      height: 0.039,
      lowerDiameter: 1.5885,
      upperDiameter: 1.6543
    },
    {
      height: 0.4858,
      lowerDiameter: 1.6543,
      upperDiameter: 1.6543
    },
    {
      height: 0.1177,
      lowerDiameter: 1.6543,
      upperDiameter: 1.8752
    },
    {
      height: 0.039,
      lowerDiameter: 2.4153,
      upperDiameter: 2.4811
    },
    {
      height: 0.1622,
      lowerDiameter: 2.4811,
      upperDiameter: 2.4811
    },
    {
      height: 0.0161,
      lowerDiameter: 2.4811,
      upperDiameter: 2.4628
    },
    {
      height: 0.0118,
      lowerDiameter: 2.4601,
      upperDiameter: 2.4192
    },
    {
      height: 0.0945,
      lowerDiameter: 2.4165,
      upperDiameter: 2.0892
    },
    {
      height: 0.1657,
      lowerDiameter: 2.0874,
      upperDiameter: 2.0874
    },
    {
      height: 0.0953,
      lowerDiameter: 2.0888,
      upperDiameter: 2.4189
    },
    {
      height: 0.011,
      lowerDiameter: 2.4216,
      upperDiameter: 2.4598
    },
    {
      height: 0.0165,
      lowerDiameter: 2.4624,
      upperDiameter: 2.4811
    },
    {
      height: 0.328,
      lowerDiameter: 2.4811,
      upperDiameter: 2.4811
    },
    {
      height: 71e-4,
      lowerDiameter: 2.4811,
      upperDiameter: 2.4796
    },
    {
      height: 0.0295,
      lowerDiameter: 2.4793,
      upperDiameter: 2.4234
    },
    {
      height: 0.0311,
      lowerDiameter: 1.7648,
      upperDiameter: 1.7134
    },
    {
      height: 0.0154,
      lowerDiameter: 1.7134,
      upperDiameter: 1.7134
    }
  ]
};
var CAT40_ER40_HOLDER = {
  type: "holder",
  description: "Maritool CAT40-ER40-3.0",
  gaugeLength: 3.121637999999999,
  unit: "inches",
  segments: [
    {
      height: 0.241732,
      lowerDiameter: 1.9,
      upperDiameter: 2.48
    },
    {
      height: 0.9,
      lowerDiameter: 2.48,
      upperDiameter: 2.48
    },
    {
      height: 0.499213,
      lowerDiameter: 1.9685,
      upperDiameter: 1.9685
    },
    {
      height: 0.106693,
      lowerDiameter: 1.9685,
      upperDiameter: 1.75
    },
    {
      height: 0.575,
      lowerDiameter: 1.75,
      upperDiameter: 1.75
    },
    {
      height: 0.05,
      lowerDiameter: 1.75,
      upperDiameter: 1.85
    },
    {
      height: 0.03,
      lowerDiameter: 2.442,
      upperDiameter: 2.502
    },
    {
      height: 0.145,
      lowerDiameter: 2.502,
      upperDiameter: 2.502
    },
    {
      height: 0.079,
      lowerDiameter: 2.502,
      upperDiameter: 2.215
    },
    {
      height: 0.118,
      lowerDiameter: 2.215,
      upperDiameter: 2.215
    },
    {
      height: 0.079,
      lowerDiameter: 2.215,
      upperDiameter: 2.502
    },
    {
      height: 0.143,
      lowerDiameter: 2.502,
      upperDiameter: 2.502
    },
    {
      height: 0.03,
      lowerDiameter: 2.502,
      upperDiameter: 2.442
    },
    {
      height: 0.125,
      lowerDiameter: 1.75,
      upperDiameter: 1.75
    }
  ]
};
function defaultHolder(tool) {
  var diam = tool.geometry_cuttingDiameter;
  if (tool.unit == "millimeters") {
    diam /= 25.4;
  }
  if (diam < 0.125) {
    return CAT40_ER16_HOLDER;
  }
  if (diam < 0.5) {
    return CAT40_ER25_HOLDER;
  }
  return CAT40_ER40_HOLDER;
}

// src/magic/gauge_diam_mapping.ts
function screw_thread_number(tool) {
  if (tool.unit == "millimeters") {
    return;
  }
  for (let number = 0; number <= 15; number++) {
    const formulaResult = 0.06 + number * 0.013;
    if (Math.abs(formulaResult - tool.geometry_cuttingDiameter) <= 1e-6) {
      return "#" + number.toString();
    }
  }
  return "";
}
var drillSizeMap = /* @__PURE__ */ new Map([
  [31e-4, "#104"],
  [35e-4, "#103"],
  [39e-4, "#102"],
  [43e-4, "#101"],
  [47e-4, "#100"],
  [51e-4, "#99"],
  [55e-4, "#98"],
  [59e-4, "#97"],
  [63e-4, "#96"],
  [67e-4, "#95"],
  [71e-4, "#94"],
  [75e-4, "#93"],
  [79e-4, "#92"],
  [83e-4, "#91"],
  [87e-4, "#90"],
  [91e-4, "#89"],
  [95e-4, "#88"],
  [0.01, "#87"],
  [0.0105, "#86"],
  [0.011, "#85"],
  [0.0115, "#84"],
  [0.012, "#83"],
  [0.0125, "#82"],
  [0.013, "#81"],
  [0.0135, "#80"],
  [0.0145, "#79"],
  [0.016, "#78"],
  [0.018, "#77"],
  [0.02, "#76"],
  [0.021, "#75"],
  [0.0225, "#74"],
  [0.024, "#73"],
  [0.025, "#72"],
  [0.026, "#71"],
  [0.028, "#70"],
  [0.0292, "#69"],
  [0.031, "#68"],
  [0.032, "#67"],
  [0.033, "#66"],
  [0.035, "#65"],
  [0.036, "#64"],
  [0.037, "#63"],
  [0.038, "#62"],
  [0.039, "#61"],
  [0.04, "#60"],
  [0.041, "#59"],
  [0.043, "#58"],
  [0.043, "#57"],
  [0.0465, "#56"],
  [0.052, "#55"],
  [0.053, "#54"],
  [0.0595, "#53"],
  [0.0635, "#52"],
  [0.067, "#51"],
  [0.07, "#50"],
  [0.073, "#49"],
  [0.076, "#48"],
  [0.0785, "#47"],
  [0.081, "#46"],
  [0.082, "#45"],
  [0.086, "#44"],
  [0.089, "#43"],
  [0.0935, "#42"],
  [0.096, "#41"],
  [0.098, "#40"],
  [0.0995, "#39"],
  [0.1015, "#38"],
  [0.104, "#37"],
  [0.1065, "#36"],
  [0.11, "#35"],
  [0.111, "#34"],
  [0.113, "#33"],
  [0.116, "#32"],
  [0.12, "#31"],
  [0.1285, "#30"],
  [0.136, "#29"],
  [0.1405, "#28"],
  [0.144, "#27"],
  [0.147, "#26"],
  [0.1495, "#25"],
  [0.152, "#24"],
  [0.154, "#23"],
  [0.157, "#22"],
  [0.159, "#21"],
  [0.161, "#20"],
  [0.166, "#19"],
  [0.1695, "#18"],
  [0.1673, "#17"],
  [0.177, "#16"],
  [0.18, "#15"],
  [0.181, "#14"],
  [0.185, "#13"],
  [0.189, "#12"],
  [0.191, "#11"],
  [0.1935, "#10"],
  [0.196, "#9"],
  [0.199, "#8"],
  [0.201, "#7"],
  [0.204, "#6"],
  [0.2055, "#5"],
  [0.209, "#4"],
  [0.213, "#3"],
  [0.221, "#2"],
  [0.228, "#1"],
  [0.234, "LTR A"],
  [0.238, "LTR B"],
  [0.242, "LTR C"],
  [0.246, "LTR D"],
  [0.25, "LTR E"],
  [0.257, "LTR F"],
  [0.261, "LTR G"],
  [0.266, "LTR H"],
  [0.272, "LTR I"],
  [0.277, "LTR J"],
  [0.281, "LTR K"],
  [0.29, "LTR L"],
  [0.295, "LTR M"],
  [0.302, "LTR N"],
  [0.316, "LTR O"],
  [0.323, "LTR P"],
  [0.332, "LTR Q"],
  [0.339, "LTR R"],
  [0.348, "LTR S"],
  [0.358, "LTR T"],
  [0.368, "LTR U"],
  [0.377, "LTR V"],
  [0.386, "LTR W"],
  [0.397, "LTR X"],
  [0.404, "LTR Y"],
  [0.413, "LTR Z"]
]);
function roundToDecimals(value, decimals) {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
}
function drill_gauge(tool) {
  if (tool.unit == "millimeters") {
    return "";
  }
  let diam = roundToDecimals(tool.geometry_cuttingDiameter, 4);
  let gauge = drillSizeMap.get(diam);
  if (gauge) {
    return "(" + gauge + ")";
  }
  return "";
}

// src/magic/tool_description.ts
import Fraction from "fraction.js";
function isPowerOfTwo(n) {
  return Number.isInteger(n) && n > 0 && (n & n - 1) === 0;
}
function hasValidDenominator(fractionStr) {
  const parts = fractionStr.split("/");
  if (parts.length !== 2)
    return false;
  const denom = parseInt(parts[1], 10);
  if (!Number.isInteger(denom))
    return false;
  return isPowerOfTwo(denom) && denom <= 64;
}
function fraction_string(num) {
  let frac = new Fraction(num);
  return frac.simplify(1e-3).toFraction();
}
function formatToHalf(value) {
  const half = Math.round(value * 2) / 2;
  const s = half.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}
function toolDescription(tool) {
  let type;
  let should_rename = true;
  let radius_label = "";
  let angle = "";
  let flute = tool.geometry_numberOfFlutes + "F";
  let LOC = "";
  let M_tap_prefix = "";
  let tap_pitch = "";
  if (tool.type == FLAT_ENDMILL) {
    type = "Flat";
  } else if (tool.type == BALL_ENDMILL) {
    type = "Ball";
  } else if (tool.type == BULL_NOSE_ENDMILL) {
    type = "Bull";
    let corner_rad = (tool.geometry_cornerRadius || 0).toPrecision(3);
    corner_rad = parseFloat(corner_rad).toString();
    radius_label = "R" + corner_rad;
  } else if (tool.type == DRILL) {
    type = "Drill";
    angle = formatToHalf(tool.geometry_tipAngle) + "\xB0";
  } else if (tool.type == SPOT_DRILL) {
    type = "Spot Drill";
    angle = formatToHalf(tool.geometry_tipAngle) + "\xB0";
  } else if (tool.type == CENTER_DRILL) {
    type = "Center Drill";
    angle = formatToHalf(tool.geometry_taperAngle) + "\xB0";
  } else if (tool.type == COUNTER_SINK) {
    type = "Countersink";
    angle = formatToHalf(tool.geometry_tipAngle) + "\xB0";
  } else if (tool.type == CHAMFER_MILL) {
    type = "Chamfer";
    angle = formatToHalf(tool.geometry_taperAngle) + "\xB0";
  } else if (tool.type == KEYSEAT_CUTTER) {
    type = "Keyseat";
    if ((tool.geometry_cornerRadius || 0) > 0) {
      let corner_rad = (tool.geometry_cornerRadius || 0).toPrecision(3);
      corner_rad = parseFloat(corner_rad).toString();
      radius_label = "R" + corner_rad;
    }
  } else if (tool.type == TAP_RIGHT) {
    type = "Tap RH";
  } else if (tool.type == TAP_LEFT) {
    type = "Tap LH";
  } else if (tool.type == FACE_MILL) {
    type = "Face";
  } else {
    should_rename = false;
  }
  let unit = '"';
  let diam;
  if (tool.unit == "millimeters") {
    unit = "mm";
    diam = tool.geometry_cuttingDiameter.toPrecision(2);
    diam = parseFloat(diam).toString();
    if (tool.type == TAP_LEFT || tool.type == TAP_RIGHT) {
      let pitch = tool.geometry_threadPitch.toPrecision(3);
      pitch = parseFloat(pitch).toString();
      tap_pitch = "x" + pitch;
      M_tap_prefix = "M";
      unit = "";
    }
    let LOC2 = parseFloat(tool.geometry_fluteLength.toPrecision(2)).toString() + "LOC";
  } else {
    let x = fraction_string(tool.geometry_cuttingDiameter);
    if (hasValidDenominator(x) && !drill_gauge(tool)) {
      diam = x;
    } else {
      diam = tool.geometry_cuttingDiameter.toPrecision(3);
      diam = parseFloat(diam).toString();
    }
    if (tool.type == TAP_LEFT || tool.type == TAP_RIGHT) {
      let rounded = Math.round(2 / tool.geometry_threadPitch);
      let tpi = (rounded / 2).toString();
      tap_pitch = "-" + tpi;
    }
    let LOC2 = parseFloat(tool.geometry_fluteLength.toPrecision(3)).toString() + "LOC";
  }
  if (should_rename) {
    if (tool.type == FLAT_ENDMILL || tool.type == BALL_ENDMILL || tool.type == BULL_NOSE_ENDMILL) {
      return diam + unit + " " + flute + " " + type + " " + LOC + " " + radius_label;
    } else if (tool.type == DRILL) {
      let gauge = drill_gauge(tool);
      return diam + unit + " " + gauge + " " + type + " " + LOC + " " + angle;
    } else if (tool.type == SPOT_DRILL || tool.type == CENTER_DRILL || tool.type == COUNTER_SINK) {
      return diam + unit + " " + type + " " + LOC + " " + angle;
    } else if (tool.type == CHAMFER_MILL) {
      return diam + unit + " " + flute + " " + type + " " + LOC + " " + angle;
    } else if (tool.type == KEYSEAT_CUTTER) {
      return diam + unit + " " + flute + " " + type + " " + LOC + " " + radius_label;
    } else if (tool.type == TAP_RIGHT || tool.type == TAP_LEFT) {
      let screw_num = screw_thread_number(tool);
      if (screw_num) {
        return screw_num + tap_pitch + " " + type + " (" + diam + unit + ") " + LOC;
      } else {
        return M_tap_prefix + diam + unit + tap_pitch + " " + type + " " + LOC;
      }
    } else if (tool.type == FACE_MILL) {
      return diam + unit + " " + flute + " " + type + " " + LOC + radius_label;
    }
  }
}

// src/magic/whole_library_interp.ts
function getDataSheet(datasheets, material, num_flutes, type) {
  var sheetName = material + "-" + num_flutes + "F-" + type;
  let n_f = Number(num_flutes);
  var sheet = datasheets[sheetName];
  while (!sheet && n_f > 0) {
    n_f -= 1;
    sheetName = material + "-" + n_f + "F-" + type;
    sheet = datasheets[sheetName];
  }
  while (!sheet && n_f < 8) {
    n_f += 1;
    sheetName = material + "-" + n_f + "F-" + type;
    sheet = datasheets[sheetName];
  }
  return sheetName;
}
function shaftReach(tool) {
  var segments = tool.shaft?.segments;
  var diam = tool.geometry_cuttingDiameter;
  if (segments === void 0 || segments.length <= 1) {
    if (tool.geometry_shaftDiameter >= tool.geometry_cuttingDiameter) {
      return 0;
    } else {
      return tool.geometry_protrudingLength - tool.geometry_shoulderLength;
    }
  }
  let reach = 0;
  for (const segment of segments) {
    if (segment.lowerDiameter > diam) {
      break;
    } else if (segment.upperDiameter > diam) {
      break;
    }
    reach += segment.height;
  }
  return reach;
}

// src/magic/make_preset.ts
function scaleNumbers(obj, scaleFactor) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, typeof value === "number" ? value * scaleFactor : value])
  );
}
function getExtension(filename) {
  const lastDotPosition = filename.lastIndexOf(".");
  if (lastDotPosition === -1)
    return "";
  return filename.substring(lastDotPosition + 1);
}
function stripExtension(filename) {
  const lastDotPosition = filename.lastIndexOf(".");
  if (lastDotPosition === -1)
    return filename;
  return filename.substring(0, lastDotPosition);
}
function replaceExtension(filename, newExtension) {
  return stripExtension(filename) + "." + newExtension;
}
function roundSignificant(num, numDigits = 3) {
  if (num === 0)
    return 0;
  const exponent = Math.floor(Math.log10(Math.abs(num)));
  const factor = Math.pow(10, exponent - numDigits + 1);
  return Math.round(num / factor) * factor;
}
function isPresetDefault(tool, preset) {
  const metric = tool.unit == "millimeters";
  if (preset.name?.toLowerCase() != "default preset")
    return false;
  if (preset.useStepover)
    return false;
  if (preset.useStepdown)
    return false;
  if (TAP_LIKE.has(tool.type)) {
    if (!isApprox(preset.spindleSpeed, 500))
      return false;
  } else {
    if (!isApprox(preset.spindleSpeed, 5e3))
      return false;
    if (preset.feedCutting) {
      if (!isApprox(preset.feedCutting, metric ? 1e3 : 40))
        return false;
      if (!isApprox(preset.feedEntry, metric ? 1e3 : 40))
        return false;
      if (!isApprox(preset.feedExit, metric ? 1e3 : 40))
        return false;
      if (!isApprox(preset.feedTransition, metric ? 1e3 : 40))
        return false;
    } else if (preset.feedPlunge) {
      if (!isApprox(preset.feedPlunge, metric ? 1e3 : 40))
        return false;
      if (!isApprox(preset.feedRetract, metric ? 1e3 : 40))
        return false;
    }
  }
  return true;
}
function fixUpPreset(tool, preset) {
  if (!preset.coolant)
    preset.coolant = "flood";
  if (preset.stepdown !== void 0 && preset.useStepdown === void 0)
    preset.useStepdown = true;
  if (preset.stepover !== void 0 && preset.useStepover === void 0)
    preset.useStepover = true;
  if (preset.useStepdown !== void 0 && preset.stepdown === void 0)
    preset.stepdown = 0;
  if (preset.useStepover !== void 0 && preset.stepover === void 0)
    preset.stepover = 0;
  if (tool.unit == "millimeters") {
    preset.surfaceSpeed = tool.geometry_cuttingDiameter * Math.PI * preset.spindleSpeed / 1e3;
  } else {
    preset.surfaceSpeed = tool.geometry_cuttingDiameter * Math.PI * preset.spindleSpeed / 12;
  }
  if (preset.feedCutting) {
    preset.feedPerTooth = preset.feedCutting / (preset.spindleSpeed * tool.geometry_numberOfFlutes);
    if (preset.feedRamp === void 0)
      preset.feedRamp = preset.feedCutting;
    if (preset.feedEntry === void 0)
      preset.feedEntry = preset.feedCutting;
    if (preset.feedExit === void 0)
      preset.feedExit = preset.feedCutting;
    if (preset.feedTransition === void 0)
      preset.feedTransition = preset.feedCutting;
  }
  if (preset.feedPlunge) {
    preset.feedPerRevolution = preset.feedPlunge / preset.spindleSpeed;
  }
  if (preset.feedRetract) {
    preset.feedRetractPerRevolution = preset.feedRetract / preset.spindleSpeed;
  }
  if (tool.type == COUNTER_SINK) {
    if (preset.useFeedPerRevolution === void 0)
      preset.useFeedPerRevolution = false;
  }
}
var Report = class {
  constructor() {
    __publicField(this, "toolErrors", /* @__PURE__ */ new Map());
    __publicField(this, "presetsAddedPerTool", /* @__PURE__ */ new Map());
  }
  recordToolError(tool, error) {
    this.toolErrors.set(tool, error);
  }
  recordPresetForTool(tool) {
    const prev = this.presetsAddedPerTool.get(tool) ?? 0;
    this.presetsAddedPerTool.set(tool, prev + 1);
  }
  getToolsWithPresetsAdded() {
    return this.presetsAddedPerTool.size;
  }
  getTotalAdded() {
    let total = 0;
    for (const count of this.presetsAddedPerTool.values()) {
      total += count;
    }
    return total;
  }
};
function add_or_replace_preset(tool, preset, presets, options, report = null) {
  for (let key of Object.keys(preset)) {
    let value = preset[key];
    if (typeof value === "number") {
      preset[key] = roundSignificant(value);
    }
  }
  console.assert(preset.spindleSpeed);
  if (preset.spindleSpeed > options.max_rpm) {
    preset.spindleSpeed = options.max_rpm;
  }
  if (options.empty_presets) {
    for (let key of Object.keys(preset)) {
      let value = preset[key];
      if (typeof value === "number") {
        preset[key] = 1;
      }
    }
  }
  if (preset.feedPlunge) {
    preset.feedRetract = 3 * preset.feedPlunge;
  }
  fixUpPreset(tool, preset);
  for (var i = 0; i < presets.length; i++) {
    if (presets[i].name == preset.name) {
      if (!options.overwrite_existing)
        return;
      preset.guid = presets[i].guid;
      presets[i] = preset;
      if (report)
        report.recordPresetForTool(tool);
      return;
    }
  }
  presets.push(preset);
  if (report)
    report.recordPresetForTool(tool);
  return;
}
function adjustPresetPurpose(preset, ARRAY_DATA, p) {
  let ret = Object.assign({}, preset);
  ret.name = makePresetName(ARRAY_DATA, p);
  if (ARRAY_DATA.debug) {
    ret["debug"] = ARRAY_DATA.debug;
  }
  ret.knownPurposes = [p];
  ret.origin = 2 /* GeneratedByToolpath */;
  return ret;
}
function makePresetName(ARRAY_DATA, p) {
  const ret = `${ARRAY_DATA["PREFIX"]}_${stringFromFinePurpose(p)}`;
  return ret;
}
function getAspectRatio(tool) {
  return tool.geometry_protrudingLength / tool.geometry_cuttingDiameter;
}
function push_advanced_presets(model, ARRAY_DATA, tool, options, report = null) {
  return _push_advanced_presets(tool.presets, model, ARRAY_DATA, tool, options, report);
}
function collectAdvancedPresets(model, ARRAY_DATA, tool, options, report = null) {
  let out = [];
  _push_advanced_presets(out, model, ARRAY_DATA, tool, options, report);
  return out;
}
function _push_advanced_presets(out, model, ARRAY_DATA, tool, options, report = null) {
  const tool_diam = tool.geometry_cuttingDiameter;
  const tool_flat_diam = tool_diam - 2 * (tool.geometry_cornerRadius || 0);
  var tool_aspect_ratio = getAspectRatio(tool);
  var Vc = ARRAY_DATA["SFM"];
  var rpm_from_Vc = 3.82 * Vc / tool_diam;
  var rpm = Math.min(rpm_from_Vc, options.max_rpm);
  Vc = rpm * tool_diam / 3.82;
  var Vc_drill = ARRAY_DATA["DRILLING_SFM"];
  rpm_from_Vc = 3.82 * Vc_drill / tool.geometry_cuttingDiameter;
  var rpm_drill = Math.min(rpm_from_Vc, options.max_rpm);
  Vc_drill = rpm_drill * tool.geometry_cuttingDiameter / 3.82;
  var Vc_tap = ARRAY_DATA["TAPPING_SFM"];
  rpm_from_Vc = 3.82 * Vc_tap / tool.geometry_cuttingDiameter;
  var rpm_tap = Math.min(rpm_from_Vc, options.max_rpm);
  Vc_tap = rpm_tap * tool.geometry_cuttingDiameter / 3.82;
  let tool_diam_imperial = tool_diam;
  let radius = 0;
  if (tool.type == BULL_NOSE_ENDMILL) {
    radius = tool.geometry_cornerRadius;
  } else if (tool.type == BALL_ENDMILL) {
    radius = tool_diam / 2;
  }
  const cuspSize = from_inches(tool.unit, 1e-3);
  const surface_stepover = (cuspSize * 8 * radius) ** 0.5;
  var face_chip_per_tooth = tool.unit == "millimeters" ? 0.1 : 4e-3;
  ARRAY_DATA.debug = model.name;
  if (tool.unit == "millimeters") {
    tool_diam_imperial = uconvert("inches", tool.unit, tool_diam);
    Vc = Vc * 304.8;
    rpm_from_Vc = Vc / (Math.PI * tool_diam);
    rpm = Math.min(rpm_from_Vc, options.max_rpm);
    Vc = rpm * Math.PI * tool_diam / 1e3;
    Vc_drill = Vc_drill * 304.8;
    rpm_from_Vc = Vc_drill / (Math.PI * tool.geometry_cuttingDiameter);
    rpm_drill = Math.min(rpm_from_Vc, options.max_rpm);
    Vc_drill = rpm_drill * Math.PI * tool.geometry_cuttingDiameter / 1e3;
    Vc_tap = Vc_tap * 304.8;
    rpm_from_Vc = Vc_tap / (Math.PI * tool.geometry_cuttingDiameter);
    rpm_tap = Math.min(rpm_from_Vc, options.max_rpm);
    Vc_tap = rpm_tap * Math.PI * tool.geometry_cuttingDiameter / 1e3;
  }
  var DATA = interp_tool_data(model, ARRAY_DATA, tool_diam_imperial, tool_aspect_ratio);
  var adaptive_roughing = adjustPresetPurpose(new Preset(), ARRAY_DATA, 0 /* Adaptive_Rough */);
  const rpm_finishing = Math.min(options.max_rpm, 12 * DATA["FINISHING_VC"] / (Math.PI * tool_diam_imperial));
  const rpm_facing = Math.min(options.max_rpm, 12 * DATA["FACING_VC"] / (Math.PI * tool_diam_imperial));
  const rpm_roughing = Math.min(options.max_rpm, 12 * DATA["ROUGHING_VC"] / (Math.PI * tool_diam_imperial));
  const rpm_slotting = Math.min(options.max_rpm, 12 * DATA["SLOTTING_VC"] / (Math.PI * tool_diam_imperial));
  adaptive_roughing.feedCutting = DATA["ROUGHING_CL"] * tool_diam * rpm_roughing * tool.geometry_numberOfFlutes;
  adaptive_roughing.spindleSpeed = rpm_roughing;
  adaptive_roughing.rampSpindleSpeed = adaptive_roughing.spindleSpeed;
  adaptive_roughing.stepover = DATA["ROUGHING_WOC"] * tool_diam;
  adaptive_roughing.stepdown = DATA["ROUGHING_DOC"] * tool.geometry_fluteLength;
  adaptive_roughing.useStepdown = true;
  adaptive_roughing.useStepover = true;
  if (ARRAY_DATA["FIXED_ENDMILL_PLUNGE_ACTIVE"]) {
    adaptive_roughing.feedPlunge = from_inches(tool.unit, ARRAY_DATA["FIXED_ENDMILL_PLUNGE"]) || 0;
  } else {
    adaptive_roughing.feedPlunge = 0.3 * adaptive_roughing.feedCutting;
  }
  var flat_stepover = tool_flat_diam * 0.9;
  var wall_finishing = adjustPresetPurpose(adaptive_roughing, ARRAY_DATA, 1 /* Wall_Finish */);
  wall_finishing.spindleSpeed = rpm_finishing;
  wall_finishing.rampSpindleSpeed = wall_finishing.spindleSpeed;
  wall_finishing.stepover = flat_stepover;
  wall_finishing.stepdown = DATA["FINISHING_DOC"] * tool.geometry_fluteLength;
  wall_finishing.feedCutting = DATA["FINISHING_CL"] * tool_diam * rpm_finishing * tool.geometry_numberOfFlutes;
  var face_rough = adjustPresetPurpose(adaptive_roughing, ARRAY_DATA, 2 /* Face_Rough */);
  face_rough.stepdown = 0.4 * tool.geometry_fluteLength;
  face_rough.stepover = Math.max(tool_flat_diam * 0.85, tool_diam * 0.05);
  face_rough.feedCutting = face_chip_per_tooth * rpm_roughing * tool.geometry_numberOfFlutes;
  var chamfer = adjustPresetPurpose(wall_finishing, ARRAY_DATA, 3 /* Chamfer */);
  chamfer.stepover = void 0;
  chamfer.stepdown = void 0;
  chamfer.useStepdown = false;
  chamfer.useStepover = false;
  var keyseat = adjustPresetPurpose(wall_finishing, ARRAY_DATA, 4 /* Keyseat */);
  keyseat.stepover = void 0;
  keyseat.stepdown = void 0;
  keyseat.useStepdown = false;
  keyseat.useStepover = false;
  var floor_finishing = adjustPresetPurpose(wall_finishing, ARRAY_DATA, 5 /* Floor_Finish */);
  floor_finishing.feedCutting = DATA["FINISHING_CL"] * tool_diam * rpm_finishing * tool.geometry_numberOfFlutes;
  floor_finishing.stepover = flat_stepover;
  var surface = adjustPresetPurpose(wall_finishing, ARRAY_DATA, 6 /* Surface */);
  surface.stepover = surface_stepover;
  if (tool.type == BALL_ENDMILL) {
    floor_finishing.stepover = tool_diam * 0.05;
    wall_finishing.stepover = tool_diam * 0.05;
  }
  if (tool.type == CHAMFER_MILL) {
    const angle = tool.geometry_taperAngle;
    surface.stepover = Math.tan(angle / 2 * 3.14159 / 180) * tool.geometry_fluteLength * 0.5;
  } else if (tool.type == COUNTER_SINK) {
    const angle = tool.geometry_tipAngle;
    surface.stepover = Math.tan(angle / 2 * 3.14159 / 180) * tool.geometry_fluteLength * 0.5;
  }
  surface.useStepdown = false;
  surface.useStepover = true;
  var facing = adjustPresetPurpose(new Preset(), ARRAY_DATA, 7 /* Face_Finish */);
  facing.feedCutting = DATA["FACING_CL"] * tool_diam * rpm_facing * tool.geometry_numberOfFlutes;
  facing.spindleSpeed = rpm_facing;
  facing.rampSpindleSpeed = facing.spindleSpeed;
  facing.stepover = DATA["FACING_WOC"] * tool_diam;
  facing.stepdown = DATA["FACING_DOC"] * tool.geometry_fluteLength;
  facing.useStepdown = true;
  facing.useStepover = true;
  if (tool.type == FACE_MILL) {
    facing.stepover = Math.max(tool_flat_diam * 0.85, tool_diam * 0.05);
    facing.stepdown = tool.unit == "millimeters" ? 1 : 0.03;
    facing.feedCutting = face_chip_per_tooth * rpm_facing * tool.geometry_numberOfFlutes;
  }
  if (ARRAY_DATA["FIXED_ENDMILL_PLUNGE_ACTIVE"]) {
    facing.feedPlunge = from_inches(tool.unit, ARRAY_DATA["FIXED_ENDMILL_PLUNGE"]) || 0;
  } else {
    facing.feedPlunge = 0.3 * facing.feedCutting;
  }
  var slotting = adjustPresetPurpose(adaptive_roughing, ARRAY_DATA, 8 /* Slot */);
  slotting.stepover = tool_diam;
  slotting.spindleSpeed = rpm_slotting;
  slotting.rampSpindleSpeed = rpm_slotting;
  slotting.feedCutting = DATA["SLOTTING_CL"] * tool_diam * rpm_slotting * tool.geometry_numberOfFlutes;
  slotting.stepdown = DATA["SLOTTING_DOC"] * tool_diam;
  facing.useStepdown = true;
  var trad_roughing = adjustPresetPurpose(slotting, ARRAY_DATA, 9 /* Traditional_Rough */);
  var bore_roughing = adjustPresetPurpose(trad_roughing, ARRAY_DATA, 10 /* Bore_Rough */);
  var bore_finishing = adjustPresetPurpose(trad_roughing, ARRAY_DATA, 11 /* Bore_Finish */);
  var drilling = adjustPresetPurpose(new Preset(), ARRAY_DATA, 12 /* Drill */);
  drilling.feedPlunge = DATA["DRILLING_CL"] * rpm_drill * 60;
  drilling.feedRetract = 3 * drilling.feedPlunge;
  drilling.useFeedPerRevolution = false;
  drilling.spindleSpeed = rpm_drill;
  var tapping = adjustPresetPurpose(new Preset(), ARRAY_DATA, 13 /* Tap */);
  new Preset();
  tapping.spindleSpeed = rpm_tap;
  if (tool.type == FLAT_ENDMILL || tool.type == BULL_NOSE_ENDMILL || tool.type == BALL_ENDMILL) {
    if (DATA["ROUGHING_ACTIVE"]) {
      add_or_replace_preset(tool, adaptive_roughing, out, options, report);
      add_or_replace_preset(tool, trad_roughing, out, options, report);
      add_or_replace_preset(tool, bore_roughing, out, options, report);
    }
  }
  if (tool.type == FLAT_ENDMILL || tool.type == BULL_NOSE_ENDMILL) {
    if (DATA["FINISHING_ACTIVE"]) {
      add_or_replace_preset(tool, bore_finishing, out, options, report);
      add_or_replace_preset(tool, wall_finishing, out, options, report);
      add_or_replace_preset(tool, floor_finishing, out, options, report);
      if (tool.type == BULL_NOSE_ENDMILL) {
        add_or_replace_preset(tool, surface, out, options, report);
      }
    }
    if (DATA["SLOTTING_ACTIVE"]) {
      add_or_replace_preset(tool, slotting, out, options, report);
    }
    if (DATA["FACING_ACTIVE"] && DATA["FINISHING_ACTIVE"]) {
      add_or_replace_preset(tool, facing, out, options, report);
    }
  }
  if (tool.type == FACE_MILL) {
    if (DATA["ROUGHING_ACTIVE"]) {
      add_or_replace_preset(tool, face_rough, out, options, report);
    }
    if (DATA["FINISHING_ACTIVE"] || DATA["FACING_ACTIVE"]) {
      add_or_replace_preset(tool, facing, out, options, report);
    }
  }
  if (tool.type == CHAMFER_MILL || tool.type == COUNTER_SINK) {
    if (DATA["FINISHING_ACTIVE"]) {
      add_or_replace_preset(tool, chamfer, out, options, report);
      add_or_replace_preset(tool, surface, out, options, report);
    }
  }
  if (tool.type == RADIUS_MILL) {
    if (DATA["FINISHING_ACTIVE"]) {
      add_or_replace_preset(tool, chamfer, out, options, report);
    }
  }
  if (tool.type == KEYSEAT_CUTTER) {
    if (DATA["FINISHING_ACTIVE"]) {
      add_or_replace_preset(tool, keyseat, out, options, report);
    }
  }
  if (tool.type == BALL_ENDMILL) {
    if (DATA["FINISHING_ACTIVE"]) {
      add_or_replace_preset(tool, surface, out, options, report);
      add_or_replace_preset(tool, wall_finishing, out, options, report);
    }
  }
  if (DRILL_LIKE.has(tool.type)) {
    if (DATA["DRILLING_ACTIVE"]) {
      add_or_replace_preset(tool, drilling, out, options, report);
    }
  }
  if (TAP_LIKE.has(tool.type)) {
    add_or_replace_preset(tool, tapping, out, options, report);
  }
}
var MagicOptions = class extends RichType {
  constructor() {
    super(...arguments);
    __publicField(this, "material", "AluWrought");
    __publicField(this, "max_rpm", 12e3);
    __publicField(this, "override_sfm", false);
    __publicField(this, "sfm", 0);
    __publicField(this, "keep_orig", true);
    __publicField(this, "overwrite_existing", false);
    __publicField(this, "empty_presets", false);
    __publicField(this, "roughing_active", true);
    __publicField(this, "finishing_active", true);
    __publicField(this, "rename_tools", false);
    __publicField(this, "add_default_holder", true);
    __publicField(this, "reuse_existing_presets", false);
    __publicField(this, "model_name", "GrayPaulModelExcel");
    __publicField(this, "gpmGaussianHyperparams", makeGPMGaussianHyperparams());
    __publicField(this, "keepPresetsFromF3dOps", false);
    __publicField(this, "datasheets", default_datasheets);
  }
};
__publicField(MagicOptions, "material_spec", { title: "Material", kind: "enum" });
__publicField(MagicOptions, "max_rpm_spec", { title: "Max RPM", kind: "rotation_speed" });
__publicField(MagicOptions, "override_sfm_spec", { title: "Override SFM", kind: "boolean" });
__publicField(MagicOptions, "sfm_spec", { title: "SFM", kind: "surface_speed", enabledWhen: { override_sfm: { eq: true } } });
__publicField(MagicOptions, "keep_orig_spec", { title: "Keep Existing", kind: "boolean" });
__publicField(MagicOptions, "overwrite_existing_spec", { title: "Overwrite Existing", kind: "boolean" });
__publicField(MagicOptions, "empty_presets_spec", { title: "Placeholders (all 1's)", kind: "boolean" });
__publicField(MagicOptions, "roughing_active_spec", { title: "Roughing", kind: "boolean" });
__publicField(MagicOptions, "finishing_active_spec", { title: "Finishing", kind: "boolean" });
__publicField(MagicOptions, "rename_tools_spec", { title: "Rename Tools", kind: "boolean" });
__publicField(MagicOptions, "add_default_holder_spec", { title: "Add Default Holder", kind: "boolean", hidden: true });
__publicField(MagicOptions, "reuse_existing_presets_spec", { title: "Reuse existing presets", kind: "boolean" });
__publicField(MagicOptions, "model_name_spec", { title: "Model", kind: "enum" });
__publicField(MagicOptions, "gpmGaussianHyperparams_spec", { title: "Hyperparameters", kind: "object" });
__publicField(MagicOptions, "keepPresetsFromF3dOps_spec", { title: "Keep presets from F3dOps", kind: "boolean" });
function isReducedShank(tool) {
  var shaft_reach = shaftReach(tool);
  let tool_aspect_ratio = tool.geometry_protrudingLength / tool.geometry_cuttingDiameter;
  let reach_to_flute_ratio = (shaft_reach + tool.geometry_shoulderLength) / tool.geometry_fluteLength;
  return reach_to_flute_ratio > 2 && tool_aspect_ratio > 4.95;
}
function add_to_group(dict, key, val) {
  let grp = dict.get(key);
  if (grp === void 0) {
    dict.set(key, [val]);
  } else {
    grp.push(val);
  }
}
function isPresetTpGenerated(preset) {
  return preset.origin == 2 /* GeneratedByToolpath */;
}
function isPresetFromOpF3d(preset) {
  return preset.origin == 0 /* FromOpF3d */ || preset.origin == 1 /* FromOpF3dDerived */;
}
function createPreexistingPresetDict(tool) {
  let preexising_presets = /* @__PURE__ */ new Map();
  for (const preset of tool.presets) {
    if (isPresetDefault(tool, preset)) {
      continue;
    }
    if (isPresetTpGenerated(preset)) {
      continue;
    }
    let purpose = void 0;
    if (preset.knownPurposes === void 0) {
      continue;
    }
    for (purpose of preset.knownPurposes) {
      add_to_group(preexising_presets, purpose, preset);
    }
  }
  let ret = /* @__PURE__ */ new Map();
  for (let [key, vals] of preexising_presets) {
    if (vals.length === 0) {
      throw Error("Unreachable");
    }
    let val = vals[0];
    if (val !== void 0) {
      ret.set(key, val);
    }
  }
  return ret;
}
function build_ARRAY_DATA(tool, options, report = null) {
  if (tool.geometry_cuttingDiameter === void 0 || tool.geometry_numberOfFlutes === void 0) {
    if (report)
      report.recordToolError(tool, "Non-cutting tool");
    else
      console.log("Skipping non-cutting tool:", tool.description);
    return;
  }
  var num_flutes = tool.geometry_numberOfFlutes;
  const is_red_shank = isReducedShank(tool);
  if (is_red_shank) {
    var type = "RS";
  } else {
    var type = "FF";
  }
  var data_sheet = getDataSheet(options.datasheets, options.material, num_flutes, type);
  if (data_sheet === null && type == "RS") {
    data_sheet = getDataSheet(options.datasheets, options.material, num_flutes, "FF");
  }
  var ARRAY_DATA = options.datasheets[data_sheet];
  ARRAY_DATA.numberOfFlutes = num_flutes;
  ARRAY_DATA.isReducedShank = is_red_shank;
  if (!ARRAY_DATA || ARRAY_DATA === void 0) {
    if (report)
      report.recordToolError(tool, "No matching datasheet");
    else
      console.log(`Tool #${tool.post_number}: ${num_flutes}FL ${type} type ${tool.type}: <<<< NO MATCH >>>>`);
    return void 0;
  }
  if (!report)
    console.log(`Tool #${tool.post_number}: ${num_flutes}FL ${type} type ${tool.type}: ${data_sheet}`);
  ARRAY_DATA["DRILLING_CL"] = drillFeedPerRev(tool);
  ARRAY_DATA["DRILLING_SFM"] = drillSFM(options.material);
  ARRAY_DATA["DRILLING_ACTIVE"] = true;
  ARRAY_DATA["TAPPING_SFM"] = tapSFM(options.material);
  ARRAY_DATA["TAPPING_ACTIVE"] = true;
  if (options.sfm > 0) {
    ARRAY_DATA["SFM"] = options.sfm;
  }
  ARRAY_DATA["PREFIX"] = options.material;
  ARRAY_DATA["ROUGHING_ACTIVE"] = options.roughing_active;
  ARRAY_DATA["FINISHING_ACTIVE"] = options.finishing_active;
  return ARRAY_DATA;
}
function make_magic_presets_tool(model, tool, options = new MagicOptions(), report = null) {
  const ARRAY_DATA = build_ARRAY_DATA(tool, options, report);
  if (ARRAY_DATA === void 0) {
    return;
  }
  let preexisting_preset_dict = void 0;
  if (options.reuse_existing_presets) {
    preexisting_preset_dict = createPreexistingPresetDict(tool);
  }
  if (!options.keep_orig) {
    tool.presets = [];
  }
  if (!options.keepPresetsFromF3dOps) {
    tool.presets = tool.presets.filter((preset) => !isPresetFromOpF3d(preset));
  }
  push_advanced_presets(model, ARRAY_DATA, tool, options, report);
  if (options.add_default_holder && !tool.holder?.segments) {
    tool.holder = defaultHolder(tool);
  }
  if (options.rename_tools) {
    let desc = toolDescription(tool);
    if (desc !== void 0) {
      tool.description = desc;
    }
  }
  if (preexisting_preset_dict !== void 0) {
    const options_ = { ...options, empty_presets: false, overwrite_existing: true };
    for (const [purpose, preset] of preexisting_preset_dict.entries()) {
      const name = makePresetName(ARRAY_DATA, purpose);
      const preset_ = {
        ...preset,
        name,
        debug: `preexising: ${preset.name}`
      };
      const report_ = null;
      add_or_replace_preset(tool, preset_, tool.presets, options_, report_);
    }
  }
}
function resolveGrayPaulModel(tools, options) {
  const models = GrayPaulModelExcels_from_datasheets(options.datasheets);
  const model_excel = models.get(options.material);
  if (model_excel === void 0) {
    throw Error(`Material missing in datasheets: ${options.material}`);
  }
  model_excel.maxRPM = options.max_rpm;
  switch (options.model_name) {
    case "GrayPaulModelExcel":
      return model_excel;
    case "GrayPaulModelGaussian": {
      const datapoints = collectGrayPaulPointsFromTools(tools);
      let m = new GrayPaulModelGaussian(model_excel, options.gpmGaussianHyperparams);
      m.reFit(datapoints);
      return m;
    }
  }
  throw Error(`Unknown model: ${options.model_name}`);
}
function make_magic_presets(tools, options = new MagicOptions(), report = null) {
  const model = resolveGrayPaulModel(tools, options);
  Object.entries(tools).forEach(([_index, tool]) => {
    make_magic_presets_tool(model, tool, options, report);
  });
}
function magic_tools_filter(tools) {
  let nonFerrous = Array();
  let ferrous = Array();
  let generalPurpose = Array();
  const filteredTools = {};
  Object.entries(tools).forEach(([index, tool]) => {
    if (ENDMILL_LIKE.has(tool.type)) {
      if (tool.geometry_numberOfFlutes <= 3) {
        nonFerrous.push(tool);
      } else {
        ferrous.push(tool);
      }
    } else {
      generalPurpose.push(tool);
    }
  });
  filteredTools.generalPurpose = generalPurpose;
  const fuseOptions = {
    // isCaseSensitive: false,
    // includeScore: false,
    // shouldSort: true,
    // includeMatches: false,
    // findAllMatches: false,
    // minMatchCharLength: 1,
    // location: 0,
    threshold: 0.4,
    // distance: 100,
    // useExtendedSearch: false,
    // ignoreLocation: false,
    // ignoreFieldNorm: false,
    // fieldNormWeight: 1,
    keys: ["description"]
  };
  const fuse = new Fuse(nonFerrous, fuseOptions);
  const nfRough = fuse.search("rough");
  const nfRoughIndices = new Set(nfRough.map((result) => result.refIndex));
  const nfFinish = fuse.search("fin");
  const nfFinishIndices = new Set(nfFinish.map((result) => result.refIndex));
  filteredTools.nonFerrousRough = nonFerrous.filter((_, i) => nfRoughIndices.has(i));
  filteredTools.nonFerrousFinish = nonFerrous.filter((_, i) => nfFinishIndices.has(i));
  filteredTools.nonFerrousGeneral = nonFerrous.filter((_, i) => !nfRoughIndices.has(i) && !nfFinishIndices.has(i));
  fuse.setCollection(ferrous);
  const fRough = fuse.search("rough");
  const fRoughIndices = new Set(fRough.map((result) => result.refIndex));
  const fFinish = fuse.search("fin");
  const fFinishIndices = new Set(fFinish.map((result) => result.refIndex));
  filteredTools.ferrousRough = ferrous.filter((_, i) => fRoughIndices.has(i));
  filteredTools.ferrousFinish = ferrous.filter((_, i) => fFinishIndices.has(i));
  filteredTools.ferrousGeneral = ferrous.filter((_, i) => !fRoughIndices.has(i) && !fFinishIndices.has(i));
  return filteredTools;
}
function make_magic_filtered_presets(tools, options, report = null) {
  const tool_sets = magic_tools_filter(tools);
  const local_options = { ...options };
  if (options.roughing_active) {
    local_options.roughing_active = true;
    local_options.finishing_active = false;
    local_options.material = "AluWrought";
    make_magic_presets(tool_sets.nonFerrousRough, local_options, report);
    local_options.material = "LowCSteel";
    make_magic_presets(tool_sets.ferrousRough, local_options, report);
    local_options.keep_orig = true;
    local_options.material = "StainlessSteel";
    make_magic_presets(tool_sets.ferrousRough, local_options, report);
    local_options.keep_orig = options.keep_orig;
  }
  if (options.finishing_active) {
    local_options.roughing_active = false;
    local_options.finishing_active = true;
    local_options.material = "AluWrought";
    make_magic_presets(tool_sets.nonFerrousFinish, local_options, report);
    local_options.material = "LowCSteel";
    make_magic_presets(tool_sets.ferrousFinish, local_options, report);
    local_options.keep_orig = true;
    local_options.material = "StainlessSteel";
    make_magic_presets(tool_sets.ferrousFinish, local_options, report);
    local_options.keep_orig = options.keep_orig;
  }
  local_options.roughing_active = options.roughing_active;
  local_options.finishing_active = options.finishing_active;
  local_options.material = "AluWrought";
  make_magic_presets(tool_sets.generalPurpose, local_options, report);
  make_magic_presets(tool_sets.nonFerrousGeneral, local_options, report);
  local_options.keep_orig = true;
  local_options.material = "LowCSteel";
  make_magic_presets(tool_sets.generalPurpose, local_options, report);
  make_magic_presets(tool_sets.ferrousGeneral, local_options, report);
  local_options.material = "StainlessSteel";
  make_magic_presets(tool_sets.generalPurpose, local_options, report);
  make_magic_presets(tool_sets.ferrousGeneral, local_options, report);
}
function filterDefaultToolsOnly(tools, report) {
  const result = [];
  for (const tool of tools) {
    if (tool.presets.length > 1) {
      if (report)
        report.recordToolError(tool, "More than one existing preset");
      else
        console.log("More than one existing preset: ", tool.description || `T${tool.post_number}`);
      continue;
    }
    if (tool.presets.length == 1) {
      if (!isPresetDefault(tool, tool.presets[0])) {
        if (report)
          report.recordToolError(tool, "Non-default preset");
        else
          console.log("Non-default preset: ", tool.description || `T${tool.post_number}`);
        continue;
      }
    }
    result.push(tool);
  }
  return result;
}

// src/magic/gaussian_processes.ts
import { matrix } from "vectorious";
var GaussianProcess = class {
  // inv(kernel(xtrain, xtrain) + eps * eye(len(xtrain))) * ytrain
  constructor(kernel, xtrain, ytrain, observation_noise) {
    __publicField(this, "kernel");
    __publicField(this, "observation_noise");
    __publicField(this, "xtrain");
    __publicField(this, "ytrain");
    // eps = observation_noise
    // y = kernel(x, xtrain) * inv(kernel(xtrain, xtrain) + eps * eye(len(xtrain))) * ytrain
    __publicField(this, "Kinv_ytrain");
    if (xtrain.length !== ytrain.length) {
      throw new Error("xtrain and ytrain must have the same length");
    }
    this.kernel = kernel;
    this.xtrain = xtrain;
    this.ytrain = ytrain;
    this.observation_noise = observation_noise;
    const nx = xtrain.length;
    let K = matrix(nx, nx);
    for (let i = 0; i < nx; i++) {
      for (let j = 0; j < nx; j++) {
        let val = kernel(xtrain[i], xtrain[j]);
        if (i === j) {
          val += observation_noise;
        }
        K.set(i, j, val);
      }
    }
    let y_train = matrix(nx, 1);
    for (let i = 0; i < nx; i++) {
      y_train.set(i, 0, ytrain[i]);
    }
    this.Kinv_ytrain = K.solve(y_train);
  }
  getNumTrain() {
    return this.Kinv_ytrain.shape[0];
  }
  predict(xs) {
    let ntrain = this.getNumTrain();
    let k_xxtrain = matrix(xs.length, ntrain);
    for (let i = 0; i < xs.length; i++) {
      for (let j = 0; j < ntrain; j++) {
        k_xxtrain.set(i, j, this.kernel(xs[i], this.xtrain[j]));
      }
    }
    const y = k_xxtrain.multiply(this.Kinv_ytrain);
    return y.toArray().map((yi) => yi[0]);
  }
};

// src/magic/preset_estimation.ts
var CoarsePurpose = /* @__PURE__ */ ((CoarsePurpose2) => {
  CoarsePurpose2[CoarsePurpose2["SLOTTING"] = 0] = "SLOTTING";
  CoarsePurpose2[CoarsePurpose2["ROUGHING"] = 1] = "ROUGHING";
  CoarsePurpose2[CoarsePurpose2["FINISHING"] = 2] = "FINISHING";
  CoarsePurpose2[CoarsePurpose2["FACING"] = 3] = "FACING";
  CoarsePurpose2[CoarsePurpose2["DRILL"] = 4] = "DRILL";
  return CoarsePurpose2;
})(CoarsePurpose || {});
function stringFromCoarsePurpose(c) {
  return stringFromEnum(CoarsePurpose, c);
}
function uconvert(to_unit, from_unit, val) {
  if (to_unit == "inches" && from_unit == "inches") {
    return val;
  } else if (to_unit == "inches" && from_unit == "millimeters") {
    return val / 25.4;
  } else if (to_unit == "millimeters" && from_unit == "inches") {
    return val * 25.4;
  } else if (to_unit == "millimeters" && from_unit == "millimeters") {
    return val;
  } else {
    throw Error(`Bad units: to_unit = ${to_unit} from_unit = ${from_unit}`);
  }
}
function from_inches(to_unit, val) {
  return uconvert(to_unit, "inches", val);
}
function to_inches(from_unit, val) {
  return uconvert("inches", from_unit, val);
}
function stringFromEnum(enumObj, value) {
  if (typeof value === "string") {
    return value;
  }
  return enumObj[value];
}
function stringFromFinePurpose(p) {
  return stringFromEnum(FinePurpose, p);
}
var Purpose = /* @__PURE__ */ ((Purpose2) => {
  Purpose2[Purpose2["SLOTTING"] = 0] = "SLOTTING";
  Purpose2[Purpose2["FACING"] = 1] = "FACING";
  Purpose2[Purpose2["ROUGHING"] = 2] = "ROUGHING";
  Purpose2[Purpose2["FINISHING"] = 3] = "FINISHING";
  return Purpose2;
})(Purpose || {});
function stringFromPurpose(p) {
  return stringFromEnum(Purpose, p);
}
function is_spot_tool_type(tool_type) {
  return tool_type.toLowerCase().includes("spot");
}
function is_tap_tool_type(tool_type) {
  return tool_type.toLowerCase().includes("tap");
}
function is_drill_tool_type(tool_type) {
  return tool_type.toLowerCase().includes("drill");
}
function is_keyseat_tool_type(tool_type) {
  return tool_type.toLowerCase().includes("slot mill");
}
function is_face_mill_tool_type(tool_type) {
  return tool_type.toLowerCase().includes("face");
}
function is_chamfer_tool_type(tool_type) {
  return tool_type.toLowerCase().includes("chamfer");
}
var STRATEGIES_WITH_axialStockToLeave = [
  "parallel",
  "spiral",
  "corner",
  "three_plus_two",
  "morphed_spiral",
  "contour3d",
  "face",
  "flat",
  "slot",
  "ramp",
  "morph",
  "contour2d",
  "adaptive",
  "multi_axis_morph",
  "scallop",
  "scallop_new",
  "radial",
  "flow2",
  "steep_and_shallow",
  "horizontal",
  "pencil",
  "blend",
  "rotary_finishing",
  "adaptive2d",
  "multi_axis_contour",
  "swarf",
  "flow",
  "chamfer2d",
  "trace",
  "project",
  "pocket2d",
  "pocket_new",
  "pocket_clearing",
  "inclined_walls"
];
var STRATEGIES_WITH_radialStockToLeave = [
  "parallel",
  "multiaxis_roughing",
  "spiral",
  "corner",
  "three_plus_two",
  "morphed_spiral",
  "geodesic",
  "advanced_swarf",
  "contour3d",
  "thread",
  "flat",
  "ramp",
  "morph",
  "rotary_contour",
  "contour2d",
  "adaptive",
  "multi_axis_morph",
  "bore",
  "scallop",
  "scallop_new",
  "radial",
  "flow2",
  "steep_and_shallow",
  "horizontal",
  "pencil",
  "blend",
  "profile2d",
  "rotary_finishing",
  "adaptive2d",
  "multi_axis_contour",
  "swarf",
  "circular",
  "flow",
  "rotary_pocket",
  "chamfer2d",
  "trace",
  "project",
  "pocket2d",
  "pocket_new",
  "pocket_clearing",
  "inclined_walls",
  "multiaxis_finishing"
];
var Pass = /* @__PURE__ */ ((Pass2) => {
  Pass2[Pass2["Roughing"] = 0] = "Roughing";
  Pass2[Pass2["FloorFinish"] = 1] = "FloorFinish";
  Pass2[Pass2["WallFinish"] = 2] = "WallFinish";
  Pass2[Pass2["FullFinish"] = 3] = "FullFinish";
  return Pass2;
})(Pass || {});
function stringFromPass(pass) {
  return stringFromEnum(Pass, pass);
}
function tryParseParamFloat(parameters, key) {
  let v = parameters[key];
  if (v === void 0) {
    return Error(`parameters[${key}]=${v} is undefined`);
  }
  const ret = parseFloat(v);
  if (isNaN(ret)) {
    return Error(`parameters[${key}]=${v} is not a number`);
  }
  return ret;
}
function tryParseParamBoolean(parameters, key) {
  let v = parameters[key];
  if (v === void 0) {
    return Error(`parameters[${key}]=${v} is undefined`);
  }
  if (v == "false") {
    return false;
  } else if (v == "true") {
    return true;
  } else {
    return Error(`parameters[${key}]=${v} is not a boolean`);
  }
}
function parseParamFloat(parameters, key) {
  let v = tryParseParamFloat(parameters, key);
  if (v instanceof Error) {
    throw v;
  }
  return v;
}
function tryClassifyPass(preset, unit) {
  let operationStrategy = preset.operationStrategy;
  if (operationStrategy === void 0) {
    return Error("operationStrategy undefined");
  }
  let p = preset.operationParameters;
  if (p === void 0) {
    return Error("operationParameters undefined");
  }
  const useStockToLeave = tryParseParamBoolean(p, "useStockToLeave");
  if (!useStockToLeave) {
    return 3 /* FullFinish */;
  }
  let supportsAxialStockToLeave = STRATEGIES_WITH_axialStockToLeave.includes(operationStrategy);
  let supportsRadialStockToLeave = STRATEGIES_WITH_radialStockToLeave.includes(operationStrategy);
  let axialStockToLeave = NaN;
  if (supportsAxialStockToLeave) {
    const res = tryParseParamFloat(p, "verticalStockToLeave");
    if (res instanceof Error) {
      return res;
    }
    axialStockToLeave = uconvert("inches", unit, res);
  }
  let radialStockToLeave = NaN;
  if (supportsRadialStockToLeave) {
    const res = tryParseParamFloat(p, "stockToLeave");
    if (res instanceof Error) {
      return res;
    }
    radialStockToLeave = uconvert("inches", unit, res);
  }
  const roughingStockToLeave = 2e-3;
  if (supportsRadialStockToLeave && supportsAxialStockToLeave) {
    if (axialStockToLeave > roughingStockToLeave && radialStockToLeave > roughingStockToLeave) {
      return 0 /* Roughing */;
    } else if (axialStockToLeave > 0 && radialStockToLeave <= 0) {
      return 2 /* WallFinish */;
    } else if (axialStockToLeave <= 0 && radialStockToLeave > 0) {
      return 1 /* FloorFinish */;
    } else if (axialStockToLeave <= 0 && radialStockToLeave <= 0) {
      return 3 /* FullFinish */;
    } else {
      return Error(`bad stock to leaves ${axialStockToLeave} ${radialStockToLeave}`);
    }
  } else if (supportsRadialStockToLeave && !supportsAxialStockToLeave) {
    if (radialStockToLeave > roughingStockToLeave) {
      return 0 /* Roughing */;
    } else if (radialStockToLeave <= 0) {
      return 3 /* FullFinish */;
    } else {
      return Error(`bad radialStockToLeave ${radialStockToLeave}`);
    }
  } else if (!supportsRadialStockToLeave && supportsAxialStockToLeave) {
    if (axialStockToLeave > roughingStockToLeave) {
      return 0 /* Roughing */;
    } else if (axialStockToLeave <= 0) {
      return 3 /* FullFinish */;
    } else {
      return Error(`bad axialStockToLeave ${axialStockToLeave}`);
    }
  } else {
    return Error(`${operationStrategy} does not support stockToLeave`);
  }
}
function tryClassifyPreset(tool, preset) {
  if (tool.type === void 0) {
    return Error("undefined tool type");
  } else if (is_tap_tool_type(tool.type)) {
    return [13 /* Tap */];
  } else if (is_drill_tool_type(tool.type)) {
    return [12 /* Drill */];
  } else if (is_keyseat_tool_type(tool.type)) {
    return [4 /* Keyseat */];
  } else if (is_chamfer_tool_type(tool.type)) {
    return [3 /* Chamfer */];
  }
  const pass = tryClassifyPass(preset, tool.unit);
  if (pass instanceof Error) {
    return pass;
  }
  const operationStrategy = preset.operationStrategy;
  if (operationStrategy === void 0) {
    return Error("undefined operationStrategy");
  }
  if (pass == 0 /* Roughing */) {
    if (["slot", "contour2d"].includes(operationStrategy)) {
      return [8 /* Slot */];
    } else if (operationStrategy == "face") {
      return [2 /* Face_Rough */];
    } else if (["bore", "circular"].includes(operationStrategy)) {
      return [10 /* Bore_Rough */];
    } else if (["pocket2d", "pocket_clearing", "pocket_new"].includes(operationStrategy)) {
      return [9 /* Traditional_Rough */];
    } else if (["adaptive", "adaptive2d"].includes(operationStrategy)) {
      return [0 /* Adaptive_Rough */];
    } else {
      return Error("Unknown roughing strategy " + operationStrategy);
    }
  }
  if (operationStrategy == "face") {
    return [7 /* Face_Finish */];
  } else if (["bore", "circular"].includes(operationStrategy)) {
    return [11 /* Bore_Finish */];
  }
  if (pass == 2 /* WallFinish */) {
    return [1 /* Wall_Finish */];
  }
  if (pass == 1 /* FloorFinish */) {
    return [5 /* Floor_Finish */];
  }
  if (pass !== 3 /* FullFinish */) {
    return Error(`unreachable pass = ${stringFromPass(pass)}`);
  }
  if ([
    "multiaxis_roughing",
    "three_plus_two",
    "advanced_swarf",
    "manual",
    "hole_recognition",
    "feature_construction",
    "deburr",
    "probe_geometry",
    "rotary_contour",
    "multi_axis_morph",
    "folder",
    "pencil",
    "probe",
    "inspect_surface",
    "engrave",
    "rotary_finishing",
    "multi_axis_contour",
    "swarf",
    "rotary_pocket",
    "inclined_walls",
    "multiaxis_finishing",
    "profile2d",
    "corner",
    "project"
  ].includes(operationStrategy)) {
    return Error(`unsupported strategy ${operationStrategy}`);
  } else if (["chamfer2d", "drill", "thread"].includes(operationStrategy)) {
    return Error(`strategy= ${operationStrategy} should have been handled at the tool level before getting here`);
  } else if (operationStrategy == "horizontal") {
    return [5 /* Floor_Finish */];
  } else if ([
    "parallel",
    "spiral",
    "morphed_spiral",
    "geodesic",
    "contour3d",
    "ramp",
    "morph",
    "scallop",
    "scallop_new",
    "radial",
    "flow2",
    "steep_and_shallow",
    "blend",
    "flow",
    "trace"
  ].includes(operationStrategy)) {
    return [6 /* Surface */];
  } else if (["contour2d", "pocket2d"].includes(operationStrategy)) {
    return [5 /* Floor_Finish */, 1 /* Wall_Finish */];
  } else {
    return Error(`unreachable operationStrategy ${operationStrategy}`);
  }
}
function getSurfaceFeetPerMin(tool, preset) {
  const rpm = preset.spindleSpeed;
  const diam_inch = to_inches(tool.unit, tool.geometry_cuttingDiameter);
  const diam_foot = diam_inch / 12;
  return diam_foot * Math.PI * rpm;
}
function toGrayPaulPoint(tool, preset, fp) {
  const {
    geometry_cuttingDiameter: cuttingDiameter,
    geometry_fluteLength: fluteLength,
    geometry_numberOfFlutes: numberOfFlutes
  } = tool;
  const rpm = preset.spindleSpeed;
  let ret = {
    isReducedShank: isReducedShank(tool),
    // the rest of GrayPaul space is dimensionless, but this one needs to be inches
    cuttingDiameter: to_inches(tool.unit, cuttingDiameter),
    aspectRatio: getAspectRatio(tool),
    numberOfFlutes,
    vc: getSurfaceFeetPerMin(tool, preset)
  };
  if (preset.feedCutting) {
    ret.cl = preset.feedCutting / (cuttingDiameter * rpm * numberOfFlutes);
  }
  if (fp === 0 /* Adaptive_Rough */) {
    ret.purpose = 1 /* ROUGHING */;
    if (preset.stepdown !== void 0) {
      ret.doc = preset.stepdown / fluteLength;
    }
    if (preset.stepover !== void 0) {
      ret.woc = preset.stepover / cuttingDiameter;
    }
  } else if (fp === 1 /* Wall_Finish */) {
    ret.purpose = 2 /* FINISHING */;
    if (preset.stepdown !== void 0) {
      ret.doc = preset.stepdown / fluteLength;
    }
  } else if (fp === 2 /* Face_Rough */) {
    ret.purpose = 1 /* ROUGHING */;
  } else if (fp === 3 /* Chamfer */) {
    ret.purpose = 2 /* FINISHING */;
  } else if (fp === 4 /* Keyseat */) {
    return void 0;
  } else if (fp === 5 /* Floor_Finish */) {
    ret.purpose = 2 /* FINISHING */;
  } else if (fp === 6 /* Surface */) {
    ret.purpose = 2 /* FINISHING */;
    if (preset.stepdown !== void 0) {
      ret.doc = preset.stepdown / fluteLength;
    }
  } else if (fp === 7 /* Face_Finish */) {
    ret.purpose = 3 /* FACING */;
    if (is_face_mill_tool_type(tool.type)) {
      ret.cl = void 0;
    }
    if (preset.stepdown !== void 0) {
      ret.doc = preset.stepdown / fluteLength;
    }
    if (preset.stepover !== void 0) {
      ret.woc = preset.stepover / cuttingDiameter;
    }
  } else if (fp === 8 /* Slot */) {
    ret.purpose = 0 /* SLOTTING */;
    if (preset.stepdown !== void 0) {
      ret.doc = preset.stepdown / cuttingDiameter;
    }
  } else if (fp === 9 /* Traditional_Rough */) {
    ret.purpose = 0 /* SLOTTING */;
    if (preset.stepdown !== void 0) {
      ret.doc = preset.stepdown / cuttingDiameter;
    }
  } else if (fp === 10 /* Bore_Rough */) {
    ret.purpose = 0 /* SLOTTING */;
    if (preset.stepdown !== void 0) {
      ret.doc = preset.stepdown / cuttingDiameter;
    }
  } else if (fp === 11 /* Bore_Finish */) {
    ret.purpose = 0 /* SLOTTING */;
    if (preset.stepdown !== void 0) {
      ret.doc = preset.stepdown / cuttingDiameter;
    }
  } else if (fp === 12 /* Drill */) {
    return void 0;
  } else if (fp === 13 /* Tap */) {
    return void 0;
  } else {
    throw Error(`unreachable fp ${stringFromFinePurpose(fp)}`);
  }
  return ret;
}
function collectGrayPaulPointsFromTools(tools, skipGenerated = true) {
  const ret = [];
  for (const tool of tools) {
    for (const preset of tool.presets) {
      if (skipGenerated && isPresetTpGenerated(preset)) {
        continue;
      }
      if (preset.knownPurposes === void 0) {
        continue;
      }
      for (const fp of preset.knownPurposes) {
        const pt = toGrayPaulPoint(tool, preset, fp);
        if (pt === void 0) {
          continue;
        }
        ret.push(pt);
      }
    }
  }
  return ret;
}
function parse_DataSheetTopKey(s) {
  let parts = s.split("-");
  let [prefix, nf, rs] = parts;
  let isReducedShank2 = false;
  if (rs == "RS") {
    isReducedShank2 = true;
  } else if (rs == "FF") {
    isReducedShank2 = false;
  } else {
    throw Error(`Invalid reduced shank: ${s}`);
  }
  let numberOfFlutes = parseInt(nf.replace("F", ""));
  return { prefix, isReducedShank: isReducedShank2, numberOfFlutes };
}
var GrayPaulModelConst = class {
  constructor(cl, doc, woc, vc) {
    __publicField(this, "name", "GrayPaulModelConst");
    __publicField(this, "cl");
    __publicField(this, "doc");
    __publicField(this, "woc");
    __publicField(this, "vc");
    this.cl = cl;
    this.doc = doc;
    this.woc = woc;
    this.vc = vc;
  }
  eval_cl(_) {
    return this.cl;
  }
  eval_doc(_) {
    return this.doc;
  }
  eval_woc(_) {
    return this.woc;
  }
  eval_vc(_) {
    return this.vc;
  }
};
var GrayPaulModelExcel = class {
  constructor(vc, maxRPM = Infinity) {
    __publicField(this, "name", "GrayPaulModelExcel");
    // this implements the Gray-Paul model for preset generation
    // it was invented by the two famous machinists in this excel sheet:
    // https://docs.google.com/spreadsheets/d/1B8rzcXhzIDpfs5C7q-6FoAzFO9ySgl4qDrRicvqmf8c/edit?gid=1295216691#gid=1295216691
    __publicField(this, "vc");
    // surface speed feet / min the desired surface speed
    __publicField(this, "maxRPM");
    __publicField(this, "cl_itp_by_key");
    __publicField(this, "doc_itp_by_key");
    __publicField(this, "woc_itp_by_key");
    this.vc = vc;
    this.maxRPM = maxRPM;
    this.cl_itp_by_key = /* @__PURE__ */ new Map();
    this.doc_itp_by_key = /* @__PURE__ */ new Map();
    this.woc_itp_by_key = /* @__PURE__ */ new Map();
  }
  addItps(json_key, json_data) {
    const { prefix, isReducedShank: isReducedShank2, numberOfFlutes } = parse_DataSheetTopKey(json_key);
    const ars = json_data["AR_VALS"].map((x) => {
      return Number(x[0]);
    });
    const diams = json_data["DIAM_VALS"].map((x) => {
      return Number(x[0]);
    });
    const PREFIX = json_data["PREFIX"];
    if (PREFIX !== prefix) {
      throw Error(`Inconsistend entry in datasheet: key = ${json_key} and PREFIX = ${PREFIX}`);
    }
    for (const purpose of [
      1 /* ROUGHING */,
      2 /* FINISHING */,
      3 /* FACING */,
      0 /* SLOTTING */
    ]) {
      const pname = stringFromCoarsePurpose(purpose);
      const key = this.makeItpKey(isReducedShank2, numberOfFlutes, purpose);
      const cl_key = `${pname}_CL`;
      const doc_key = `${pname}_DOC`;
      const woc_key = `${pname}_WOC`;
      if (json_data[cl_key] !== void 0) {
        this.cl_itp_by_key.set(key, new BilinearInterpolate(diams, ars, json_data[cl_key]));
      }
      if (json_data[doc_key] !== void 0) {
        this.doc_itp_by_key.set(key, new BilinearInterpolate(diams, ars, json_data[doc_key]));
      }
      if (json_data[woc_key] !== void 0) {
        this.woc_itp_by_key.set(key, new BilinearInterpolate(diams, ars, json_data[woc_key]));
      }
    }
  }
  makeItpKey(isReducedShank2, numberOfFlutes, purpose) {
    return `${isReducedShank2},${numberOfFlutes},${stringFromCoarsePurpose(purpose)}`;
  }
  findClosestItpEntry(d, isReducedShank2, numberOfFlutes, purpose) {
    let key = this.makeItpKey(isReducedShank2, numberOfFlutes, purpose);
    let itp = void 0;
    let nf = numberOfFlutes;
    while (nf > 0) {
      key = this.makeItpKey(isReducedShank2, nf, purpose);
      itp = d.get(key);
      if (itp !== void 0) {
        return { key: [isReducedShank2, nf, purpose], itp };
      }
      nf -= 1;
    }
    nf = numberOfFlutes;
    while (nf < 8) {
      key = this.makeItpKey(isReducedShank2, nf, purpose);
      itp = d.get(key);
      if (itp !== void 0) {
        return { key: [isReducedShank2, nf, purpose], itp };
      }
      nf += 1;
    }
    if (isReducedShank2) {
      return this.findClosestItpEntry(d, false, numberOfFlutes, purpose);
    }
    return void 0;
  }
  evalClosestItp(d, inp) {
    const entry = this.findClosestItpEntry(d, inp.isReducedShank, inp.numberOfFlutes, inp.purpose);
    if (entry === void 0) {
      return void 0;
    } else {
      const itp = entry["itp"];
      return itp.eval(inp.cuttingDiameter, inp.aspectRatio);
    }
  }
  eval_cl(inp) {
    const ret = this.evalClosestItp(this.cl_itp_by_key, inp);
    if (ret === void 0) {
      throw Error(`cl not found for ${JSON.stringify(inp, null, 2)}`);
    } else {
      return 0.01 * ret;
    }
  }
  eval_doc(inp) {
    const ret = this.evalClosestItp(this.doc_itp_by_key, inp);
    if (ret === void 0) {
      throw Error(`doc not found for ${JSON.stringify(inp, null, 2)}`);
    } else {
      return 0.01 * ret;
    }
  }
  eval_woc(inp) {
    const ret = this.evalClosestItp(this.woc_itp_by_key, inp);
    if (ret === void 0) {
      throw Error(`woc not found for ${JSON.stringify(inp, null, 2)}`);
    } else {
      return 0.01 * ret;
    }
  }
  eval_vc(inp) {
    const desiredRPM = 12 * this.vc / (Math.PI * inp.cuttingDiameter);
    const rpm = Math.min(this.maxRPM, desiredRPM);
    return rpm * (Math.PI * inp.cuttingDiameter) / 12;
  }
};
function GrayPaulModelExcels_from_datasheets(datasheets) {
  let ret = /* @__PURE__ */ new Map();
  for (const top_key of Object.keys(datasheets)) {
    let json_data = datasheets[top_key];
    let prefix = json_data["PREFIX"];
    if (typeof prefix != "string") {
      throw Error(`Bad PREFIX: ${prefix}`);
    }
    let model = ret.get(prefix);
    if (model === void 0) {
      const vc = parseParamFloat(json_data, "SFM");
      model = new GrayPaulModelExcel(vc);
    }
    model.addItps(top_key, json_data);
    ret.set(prefix, model);
  }
  return ret;
}
function makeGPMGaussianHyperparams() {
  return {
    s_purpose: 0.9,
    s_numberOfFlutes: 2.5,
    s_isReducedShank: 1.1,
    s_cuttingDiameter: 2,
    s_aspectRatio: 2,
    eps_cl: 0.01,
    eps_doc: 0.14,
    eps_woc: 0.14,
    eps_vc: 0.11
  };
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
var MAX_RESIDUAL = Math.log(10);
var MIN_RESIDUAL = -MAX_RESIDUAL;
function calcResidualDetailed(target, value) {
  const uncensored = Math.log(target / value);
  return {
    value: clamp(uncensored, MIN_RESIDUAL, MAX_RESIDUAL),
    uncensored,
    isCensored: uncensored < MIN_RESIDUAL || uncensored > MAX_RESIDUAL
  };
}
function calcResidual(target, value) {
  return calcResidualDetailed(target, value).value;
}
function applyResidual(residual, value) {
  const r = clamp(residual, MIN_RESIDUAL, MAX_RESIDUAL);
  return value * Math.exp(r);
}
var GrayPaulModelGaussian = class {
  constructor(base_model, hyperparams = makeGPMGaussianHyperparams()) {
    __publicField(this, "name", "GrayPaulModelGaussian");
    __publicField(this, "base_model");
    // the processes model the residuals with respect to the base_model
    __publicField(this, "process_cl");
    __publicField(this, "process_doc");
    __publicField(this, "process_woc");
    __publicField(this, "process_vc");
    __publicField(this, "datapoints");
    // mostly for debugging
    __publicField(this, "hyperparams");
    const { eps_cl, eps_doc, eps_woc, eps_vc } = hyperparams;
    this.base_model = base_model;
    this.hyperparams = hyperparams;
    const kernel = (inp1, inp2) => {
      return this.kernel(inp1, inp2);
    };
    this.process_cl = new GaussianProcess(kernel, [], [], eps_cl);
    this.process_doc = new GaussianProcess(kernel, [], [], eps_doc);
    this.process_woc = new GaussianProcess(kernel, [], [], eps_woc);
    this.process_vc = new GaussianProcess(kernel, [], [], eps_vc);
    this.datapoints = [];
  }
  reFit(datapoints) {
    this.datapoints = datapoints;
    const { eps_cl, eps_doc, eps_woc, eps_vc } = this.hyperparams;
    const kernel = (inp1, inp2) => {
      return this.kernel(inp1, inp2);
    };
    let xscl = [];
    let yscl = [];
    let xsdoc = [];
    let ysdoc = [];
    let xswoc = [];
    let yswoc = [];
    let xsvc = [];
    let ysvc = [];
    for (const datapoint of datapoints) {
      const inp = datapoint;
      let ycl = NaN;
      let ydoc = NaN;
      let ywoc = NaN;
      let yvc = NaN;
      if (datapoint.cl !== void 0) {
        const r = calcResidualDetailed(datapoint.cl, this.base_model.eval_cl(inp));
        if (r.isCensored) {
          continue;
        }
        ycl = r.value;
      }
      if (datapoint.doc !== void 0) {
        const r = calcResidualDetailed(datapoint.doc, this.base_model.eval_doc(inp));
        if (r.isCensored) {
          continue;
        }
        ydoc = r.value;
      }
      if (datapoint.woc !== void 0) {
        const r = calcResidualDetailed(datapoint.woc, this.base_model.eval_woc(inp));
        if (r.isCensored) {
          continue;
        }
        ywoc = r.value;
      }
      if (datapoint.vc !== void 0) {
        const r = calcResidualDetailed(datapoint.vc, this.base_model.eval_vc(inp));
        if (r.isCensored) {
          continue;
        }
        yvc = r.value;
      }
      if (!isNaN(ycl)) {
        xscl.push(inp);
        yscl.push(ycl);
      }
      if (!isNaN(ydoc)) {
        xsdoc.push(inp);
        ysdoc.push(ydoc);
      }
      if (!isNaN(ywoc)) {
        xswoc.push(inp);
        yswoc.push(ywoc);
      }
      if (!isNaN(yvc)) {
        xsvc.push(inp);
        ysvc.push(yvc);
      }
    }
    this.process_cl = new GaussianProcess(kernel, xscl, yscl, eps_cl ** 2);
    this.process_doc = new GaussianProcess(kernel, xsdoc, ysdoc, eps_doc ** 2);
    this.process_woc = new GaussianProcess(kernel, xswoc, yswoc, eps_woc ** 2);
    this.process_vc = new GaussianProcess(kernel, xsvc, ysvc, eps_vc ** 2);
  }
  kernel(inp1, inp2) {
    const d_purpose = inp1.purpose === inp2.purpose ? 0 : 1;
    const d_numberOfFlutes = Math.abs(inp1.numberOfFlutes - inp2.numberOfFlutes);
    const d_isReducedShank = inp1.isReducedShank === inp2.isReducedShank ? 0 : 1;
    const d_cuttingDiameter = Math.abs(Math.log(inp1.cuttingDiameter) - Math.log(inp2.cuttingDiameter));
    const d_aspectRatio = Math.abs(Math.log(inp1.aspectRatio) - Math.log(inp2.aspectRatio));
    const { s_purpose, s_numberOfFlutes, s_isReducedShank, s_cuttingDiameter, s_aspectRatio } = this.hyperparams;
    const d2 = 0 + (d_purpose / s_purpose) ** 2 + (d_numberOfFlutes / s_numberOfFlutes) ** 2 + (d_isReducedShank / s_isReducedShank) ** 2 + (d_cuttingDiameter / s_cuttingDiameter) ** 2 + (d_aspectRatio / s_aspectRatio) ** 2;
    return Math.exp(-d2 / 2);
  }
  eval_cl(inp) {
    const base = this.base_model.eval_cl(inp);
    const correction = this.process_cl.predict([inp])[0];
    return applyResidual(correction, base);
  }
  eval_doc(inp) {
    const base = this.base_model.eval_doc(inp);
    const correction = this.process_doc.predict([inp])[0];
    return applyResidual(correction, base);
  }
  eval_woc(inp) {
    const base = this.base_model.eval_woc(inp);
    const correction = this.process_woc.predict([inp])[0];
    return applyResidual(correction, base);
  }
  eval_vc(inp) {
    const base = this.base_model.eval_vc(inp);
    const correction = this.process_vc.predict([inp])[0];
    return applyResidual(correction, base);
  }
};

// src/util/object-helpers.ts
function setNestedValueImmutable(obj, path, value) {
  const keys2 = path.split(".");
  const lastKey = keys2.pop();
  let current = obj;
  for (const key of keys2) {
    if (!(key in current) || typeof current[key] !== "object") {
      current[key] = {};
    } else {
      current[key] = { ...current[key] };
    }
    current = current[key];
  }
  current[lastKey] = value;
}
function setNestedValue(obj, path, value) {
  const keys2 = path.split(".");
  let current = obj;
  keys2.forEach((key, index) => {
    if (index === keys2.length - 1) {
      current[key] = value;
    } else {
      if (!(key in current) || typeof current[key] !== "object") {
        current[key] = {};
      }
      current = current[key];
    }
  });
}
function getNestedValue(obj, path) {
  return path.split(".").reduce((current, key) => {
    if (current && typeof current === "object" && key in current) {
      return current[key];
    }
    return void 0;
  }, obj);
}
function flattenObject(obj, separator = ".", result = {}, parentKey = "") {
  for (const [key, value] of Object.entries(obj)) {
    const newKey = parentKey ? `${parentKey}${separator}${key}` : key;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      flattenObject(value, separator, result, newKey);
    } else {
      result[newKey] = value;
    }
  }
  return result;
}
function unflattenObject(flatObj, separator = ".") {
  const result = {};
  for (const flatKey in flatObj) {
    const keys2 = flatKey.split(separator);
    let current = result;
    keys2.forEach((key, index) => {
      if (index === keys2.length - 1) {
        current[key] = flatObj[flatKey];
      } else {
        if (!current[key] || typeof current[key] !== "object") {
          current[key] = {};
        }
        current = current[key];
      }
    });
  }
  return result;
}
function getObjectPaths(obj, currentPath = "") {
  const paths = [];
  Object.entries(obj).forEach(([key, value]) => {
    const newPath = currentPath ? `${currentPath}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      paths.push(...getObjectPaths(value, newPath));
    } else {
      paths.push(newPath);
    }
  });
  return paths;
}
function sortObjectKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  } else if (obj !== null && typeof obj === "object") {
    const sortedObj = {};
    Object.keys(obj).sort().forEach((key) => {
      sortedObj[key] = sortObjectKeys(obj[key]);
    });
    return sortedObj;
  }
  return obj;
}

// src/fusion/fusion_tools_hsm.ts
import { SaxesParser } from "saxes";
function makeKnownPurposesFloorWallFinish(preset, pass) {
  if (pass == 3 /* FullFinish */) {
    return [5 /* Floor_Finish */, 1 /* Wall_Finish */];
  } else if (pass == 2 /* WallFinish */) {
    return [1 /* Wall_Finish */];
  } else if (pass == 1 /* FloorFinish */) {
    return [5 /* Floor_Finish */];
  } else {
    throw Error("Unreachable");
  }
}
function tryExtractMultiplePresets(tool, preset) {
  const params = preset.operationParameters;
  const strategy = preset.operationStrategy;
  const pass = tryClassifyPass(preset, tool.unit);
  if (pass instanceof Error) {
    return pass;
  }
  if (strategy == "face") {
    if (pass == 0 /* Roughing */) {
      return Error(`multi preset ${strategy} cannot have stock to leave`);
    }
    if (tryParseParamBoolean(params, "doMultipleDepths") !== true) {
      return Error(`multi preset ${strategy} must have multiple depths`);
    }
    if (tryParseParamBoolean(params, "doFinishingStep") !== true) {
      return Error(`multi preset ${strategy} must have finishing step`);
    }
    const finishFeedrate = tryParseParamFloat(params, "finishFeedrate");
    if (finishFeedrate instanceof Error) {
      return finishFeedrate;
    }
    const finishingStepdown = tryParseParamFloat(params, "finishingStepdown");
    if (finishingStepdown instanceof Error) {
      return finishingStepdown;
    }
    let preset_rough = structuredClone(preset);
    preset_rough.knownPurposes = [2 /* Face_Rough */];
    let preset_finish = structuredClone(preset);
    preset_finish.name = `${preset.name} derived Face_Finish`;
    preset_finish.origin = 1 /* FromOpF3dDerived */;
    preset_finish.knownPurposes = [7 /* Face_Finish */];
    preset_finish.stepdown = to_inches(tool.unit, finishingStepdown);
    preset_finish.feedCutting = to_inches(tool.unit, finishFeedrate);
    fixUpPreset(tool, preset_finish);
    fixUpPreset(tool, preset_rough);
    return [preset_rough, preset_finish];
  } else if (strategy == "contour2d") {
    if (tryParseParamBoolean(params, "doRoughingPasses") !== true) {
      return Error(`multi preset ${strategy} must have doRoughingPasses`);
    }
    if (pass == 0 /* Roughing */) {
      return Error(`multi preset ${strategy} must be finishing`);
    }
    const maximumRoughStepover = tryParseParamFloat(params, "maximumStepover");
    if (maximumRoughStepover instanceof Error) {
      return maximumRoughStepover;
    }
    let preset_rough = structuredClone(preset);
    preset_rough.stepover = to_inches(tool.unit, maximumRoughStepover);
    preset_rough.name = `${preset.name} derived Traditional_Rough`;
    preset_rough.origin = 1 /* FromOpF3dDerived */;
    preset_rough.knownPurposes = [9 /* Traditional_Rough */];
    fixUpPreset(tool, preset_rough);
    let preset_finish = structuredClone(preset);
    preset_finish.knownPurposes = makeKnownPurposesFloorWallFinish(preset_finish, pass);
    fixUpPreset(tool, preset_finish);
    return [preset_rough, preset_finish];
  } else if (strategy == "pocket2d") {
    if (tryParseParamBoolean(params, "doFinishingPasses") !== true) {
      return Error(`multi preset ${strategy} must have doRoughingPasses`);
    }
    if (pass == 0 /* Roughing */) {
      return Error(`multi preset ${strategy} must be finishing`);
    }
    const finishingStepover = tryParseParamFloat(params, "finishingStepover");
    if (finishingStepover instanceof Error) {
      return finishingStepover;
    }
    const finishFeedrate = tryParseParamFloat(params, "finishFeedrate");
    if (finishFeedrate instanceof Error) {
      return finishFeedrate;
    }
    let preset_rough = structuredClone(preset);
    preset_rough.knownPurposes = [9 /* Traditional_Rough */];
    fixUpPreset(tool, preset_rough);
    let preset_finish = structuredClone(preset);
    preset_finish.name = `${preset.name} derived finish`;
    preset_finish.origin = 1 /* FromOpF3dDerived */;
    preset_finish.knownPurposes = makeKnownPurposesFloorWallFinish(preset_finish, pass);
    preset_finish.stepover = to_inches(tool.unit, finishingStepover);
    preset_finish.feedCutting = to_inches(tool.unit, finishFeedrate);
    fixUpPreset(tool, preset_finish);
    return [preset_rough, preset_finish];
  } else {
    return Error(`multi preset does not support ${strategy}`);
  }
}
function op_to_presets(tool, op) {
  const preset = new Preset();
  Object.assign(preset, {
    name: `Op_${op.displayName}`,
    coolant: "disabled",
    spindleSpeed: 0,
    // n
    surfaceSpeed: 0
    // v_c
  });
  let keyMapping = {
    // Skip duplicate stepover/stepdown parameters. These are just copied in by Fusion from the library tool.
    tool_use_stepdown: "",
    tool_use_stepover: "",
    tool_stepdown: "",
    tool_stepover: "",
    // Map op-specific step parameters back to tool stepover/stepdown
    optimalLoad: "tool_stepover",
    stepover: "tool_stepover",
    maximumStepdown: "tool_stepdown"
  };
  if (op.strategy == "contour2d") {
    keyMapping["finishingStepover"] = "tool_stepover";
  } else {
    keyMapping["maximumStepover"] = "tool_stepover";
  }
  for (let [key, value] of Object.entries(op.parameters)) {
    if (key in keyMapping) {
      key = keyMapping[key];
    }
    const field = fusion_field_by_hsm_key[key];
    if (field && field.id && value !== "" && parseFloat(value) !== 0) {
      setFieldValue(preset, field, value);
    }
  }
  preset.operationParameters = op.parameters;
  preset.operationStrategy = op.strategy;
  preset.origin = 0 /* FromOpF3d */;
  fixUpPreset(tool, op);
  const res_multi = tryExtractMultiplePresets(tool, preset);
  if (res_multi instanceof Error) {
  } else {
    return res_multi;
  }
  const res_classify = tryClassifyPreset(tool, preset);
  if (res_classify instanceof Error) {
    preset.knownPurposes = [];
  } else {
    preset.knownPurposes = res_classify;
  }
  return [preset];
}
function parseValue2(value, kind) {
  switch (kind) {
    case "integer":
    case "datetime":
      return parseInt(value);
    case "length":
    case "feedrate":
    case "rotation_speed":
    case "surface_speed":
    case "unitless":
    case "angle":
    case "current":
    case "lpm":
    case "volts":
    case "watts":
      return parseFloat(value);
    case "guid": {
      if (typeof value === "string" && value.startsWith("{")) {
        value = value.slice(1, value.length - 1);
      }
      return String(value);
    }
    case "text":
    case "enum":
      return String(value);
    case "boolean":
      if (typeof value === "string") {
        return ["true", "on", "yes"].includes(value.toLowerCase());
      } else if (typeof value !== "boolean") {
        console.log("Expected bool for kind", kind, "got", value);
      }
      return value;
    default:
      console.log("Unsupported kind", kind);
      return value;
  }
}
function convertSectionsToSegments(sections) {
  const segments = [];
  for (let i = 0; i < sections.length - 1; i++) {
    const current = sections[i];
    const next = sections[i + 1];
    if (next.length > 0) {
      segments.push({
        lowerDiameter: current.diameter,
        upperDiameter: next.diameter,
        height: next.length
      });
    }
  }
  return segments;
}
function setFieldValue(target, field, s) {
  const value = parseValue2(s, field.kind);
  setNestedValue(target, field.id, value);
}
function createFusionXmlParser() {
  const result = { tools: [], ops: [] };
  const parser = new SaxesParser();
  const elementStack = [];
  const relevantStack = [];
  const relevantNodes = ["tool", "holder", "object"];
  let currentTool = null;
  let currentOp = null;
  let currentPreset = null;
  let currentHolder = null;
  let currentShaft = null;
  let currentSections = [];
  function processFieldValue(hsm_key_or_id, value) {
    let field = fusion_field_by_hsm_key[hsm_key_or_id];
    if (!field) {
      field = fusion_field_by_hsm_id[hsm_key_or_id];
    }
    if (!field && hsm_key_or_id == "tool.description") {
      if (currentTool) {
        if (currentTool.description === "") {
          currentTool.description = value;
          return;
        }
      }
    }
    if (field && field.id) {
      if (field.group.startsWith("preset") && !currentPreset) {
        return;
      }
      setFieldValue(currentOp || currentPreset || currentTool, field, value);
    } else {
    }
  }
  parser.on("opentag", (node) => {
    elementStack.push(node.name);
    if (relevantStack.length || relevantNodes.includes(node.name)) {
      relevantStack.push(node.name);
    }
    if (relevantStack.length === 1) {
      if (relevantNodes.includes(relevantStack[0])) {
        if (relevantStack[0] === "object") {
          currentOp = {
            strategy: node.attributes.strategy || null,
            parameters: {},
            ...node.attributes
          };
        } else {
          currentTool = { ...new Tool_V1(), presets: [] };
          if (node.name === "holder")
            currentTool.type = "holder";
        }
      }
    }
    if (currentTool && relevantStack.length === 2) {
      const child = node.name;
      if (child === "holder") {
        currentHolder = {
          type: "holder",
          unit: node.attributes.unit,
          // || "millimeters", // TODO
          description: node.attributes.description || "",
          gaugeLength: parseFloat(node.attributes["gauge-length"] || "0"),
          guid: node.attributes.guid || "",
          productId: node.attributes["product-id"] || "",
          vendor: node.attributes.vendor || "",
          segments: []
        };
        currentSections = [];
      } else if (child === "shaft") {
        currentShaft = {
          segments: []
        };
        currentSections = [];
      }
    }
    if (node.name === "section") {
      const diameter = parseFloat(node.attributes.diameter || "0");
      const length = parseFloat(node.attributes.length || "0");
      currentSections.push({ diameter, length });
    }
    if (currentTool && relevantStack.length >= 3 && relevantStack[relevantStack.length - 1] === "preset") {
      currentPreset = new Preset();
    }
    if (relevantStack.length) {
      const attributes = node.attributes;
      if (attributes.key && attributes.value) {
        processFieldValue(attributes.key, attributes.value);
      } else {
        for (const [attrKey, attrValue] of Object.entries(attributes)) {
          let stack = [...relevantStack, attrKey].slice(1);
          if (stack[0] === "presets") {
            stack = stack.slice(1);
          }
          const path = stack.join(".");
          processFieldValue(path, attrValue);
        }
      }
    }
    if (currentOp && node.name === "Parameter") {
      const paramName = node.attributes.name;
      const paramValue = node.attributes.value;
      if (paramName && paramValue !== void 0) {
        currentOp.parameters[paramName] = paramValue;
      }
    }
  });
  parser.on("closetag", () => {
    const closingElement = elementStack.pop() || "";
    if (["holder", "shaft"].includes(closingElement)) {
      const segments = convertSectionsToSegments(currentSections);
      if (closingElement === "holder" && currentHolder && currentTool) {
        currentHolder.segments = segments;
        currentTool.holder = currentHolder;
        currentHolder = null;
      }
      if (closingElement === "shaft" && currentShaft && currentTool) {
        currentShaft.segments = segments;
        currentTool.shaft = currentShaft;
        currentShaft = null;
      }
      currentSections = [];
    }
    if (closingElement === "object" && currentOp) {
      result.ops.push(currentOp);
      currentOp = null;
    }
    if (relevantStack.length === 1) {
      if (["tool", "holder"].includes(relevantStack[0]) && currentTool) {
        if (currentTool.presets.length > 0) {
          for (const preset of currentTool.presets) {
            fixUpPreset(currentTool, preset);
          }
        }
        result.tools.push(currentTool);
        currentTool = null;
      }
    }
    if (currentTool && relevantStack.length >= 3 && relevantStack[relevantStack.length - 1] === "preset") {
      if (currentPreset) {
        currentTool.presets.push(currentPreset);
      }
      currentPreset = null;
    }
    if (relevantStack.length) {
      relevantStack.pop();
    }
  });
  parser.on("text", (text) => {
    const trimmed = text.trim();
    const path = elementStack.slice(2).join(".");
    processFieldValue(path, trimmed);
  });
  parser.on("error", (err) => {
    throw err;
  });
  return {
    parser,
    result,
    elementStack,
    relevantStack,
    currentTool,
    currentOp,
    currentPreset,
    currentHolder,
    currentShaft
  };
}
function fusion_hsm_input(xml) {
  const { parser, result } = createFusionXmlParser();
  parser.write(xml).close();
  for (const op of result.ops) {
    let toolId = parseInt(op.toolId);
    if (toolId > 0) {
      const tool = result.tools[toolId - 1];
      for (const preset of op_to_presets(tool, op)) {
        tool.presets.push(preset);
      }
    } else {
      console.warn(`No tool found for operation with toolId: ${toolId}`);
    }
  }
  for (const tool of result.tools) {
    const unit = tool.unit;
    if (unit == "millimeters")
      continue;
    const fields = filterFieldsForTypes(fusion_all_preset_fields, [tool.type]);
    for (const preset of tool.presets) {
      for (const field of fields) {
        if (!field.id)
          continue;
        const factor = getDefaultConversionFactor(field.kind, tool.unit == "millimeters");
        if (!factor)
          continue;
        const value = getNestedValue(preset, field.id);
        setNestedValue(preset, field.id, value * factor);
      }
      fixUpPreset(tool, preset);
    }
  }
  return result.tools;
}
function fusion_hsm_input_streaming(xmlStream) {
  return new Promise((resolve, reject) => {
    const { parser, result } = createFusionXmlParser();
    parser.on("end", () => {
      resolve(result);
    });
    parser.on("error", (err) => {
      reject(err);
    });
    xmlStream.on("data", (chunk) => {
      parser.write(chunk);
    });
    xmlStream.on("end", () => {
      parser.close();
    });
  });
}

// src/fusion/fusion_tools_f3d.ts
async function fusion_read_f3d_irondocs(fileData) {
  const zip = await JSZip.loadAsync(fileData);
  let contents = [];
  await scanZipRecursively(zip, contents);
  return contents;
}
async function scanZipRecursively(zip, contents) {
  const files = Object.keys(zip.files);
  for (const fileName of files) {
    const file = zip.files[fileName];
    if (file.dir)
      continue;
    if (fileName.endsWith("theIronDoc.irondoc")) {
      const content = await file.async("string");
      contents.push(content);
    } else if (fileName.endsWith(".f3d")) {
      const nestedZipBlob = await file.async("blob");
      const nestedZip = await JSZip.loadAsync(nestedZipBlob);
      await scanZipRecursively(nestedZip, contents);
    }
  }
}
async function fusion_f3d_input(fileData) {
  const ironDocs = await fusion_read_f3d_irondocs(fileData);
  const allTools = [];
  for (const doc of ironDocs) {
    const tools = fusion_hsm_input(doc);
    allTools.push(...tools);
  }
  return allTools;
}

// src/util/random.ts
function randomUUID() {
  return crypto.randomUUID();
}
var random_default = {
  randomUUID
};

// src/fusion/fusion_tools_json.ts
function parseJsonInput(inputData) {
  if (typeof inputData === "string") {
    return JSON.parse(inputData);
  }
  return inputData;
}
function fusion_json_input(inputData) {
  const input = parseJsonInput(inputData);
  return input.data.map((fusionToolData) => fusion_json_input_single(fusionToolData));
}
function fusion_json_input_single(data) {
  const tool_type = data["type"];
  let tool = { ...new Tool_V1(), presets: [] };
  tool.type = tool_type;
  let presetsData = [];
  if (tool_type === "holder") {
    data.holder = {
      segments: data.segments,
      gaugeLength: data.gaugeLength
    };
    delete data.segments;
    delete data.gaugeLength;
  } else {
    presetsData = data["start-values"]?.presets;
    delete data["start-values"];
  }
  const tool_fields2 = fusion_field_by_json_id;
  const preset_fields2 = fusion_field_by_json_id;
  const flat = flattenObject(data);
  delete flat["GRADE"];
  delete flat["holder.last_modified"];
  delete flat["holder.reference_guid"];
  for (const [key, value] of Object.entries(flat)) {
    setProp2(tool, key, value, tool_fields2);
  }
  if (presetsData) {
    tool.presets = presetsData.map((data2) => fusion_json_input_single_preset(data2, preset_fields2));
  }
  return tool;
}
function fusion_json_input_single_preset(data, fields) {
  const preset = new Preset();
  const flat = flattenObject(data);
  delete flat["description"];
  delete flat["material.use-hardness"];
  for (const [key, value] of Object.entries(flat)) {
    setProp2(preset, "start-values.presets." + key, value, fields);
  }
  return preset;
}
function setProp2(obj, json_path, value, fields) {
  if (value === void 0) {
    return;
  }
  if (json_path.indexOf("expressions.") > -1) {
    return;
  }
  const field = fields[json_path];
  if (field === void 0) {
    console.warn(`Skipping unrecognized field ${json_path}`);
    return;
  }
  let path = field.id ?? `fusion.${json_path}`;
  if (value === void 0 || value === null || typeof value === "string" && value.length === 0) {
    value = field.defaultValue;
  }
  if (value !== void 0 && value !== null) {
    if (field.kind === "segments")
      value = parseJsonSegments(value);
    else
      value = parseValue3(value, field.kind);
  }
  const required = field.required || field.kind === "text";
  if (value === void 0) {
    if (required)
      value = "";
    else
      return;
  }
  if (value === "" && !required)
    return;
  setNestedValue(obj, path, value);
}
function parseValue3(value, kind) {
  if (kind === "integer" || kind === "datetime") {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? void 0 : parsed;
  }
  if (isKindNumeric(kind)) {
    return parseFloat(value);
  }
  if (["text", "enum", "guid"].includes(kind)) {
    return String(value);
  }
  if (kind === "boolean") {
    if (typeof value === "string") {
      return ["true", "on", "yes"].includes(value.toLowerCase());
    } else if (typeof value !== "boolean") {
      console.warn("Expected bool for kind", kind, "got", value);
      return void 0;
    }
    return value;
  }
  console.warn("Unsupported kind", kind);
  return void 0;
}
function parseJsonSegments(fusion_segments) {
  const segments = [];
  for (const fusion_segment of fusion_segments) {
    const segment = {
      height: fusion_segment.height ?? 0,
      upperDiameter: fusion_segment["upper-diameter"] ?? 0,
      lowerDiameter: fusion_segment["lower-diameter"] ?? 0
    };
    segments.push(segment);
  }
  return segments;
}
var tool_fields = fusion_all_tool_fields.filter(
  (f) => !!f.json_id
);
var preset_fields = fusion_all_preset_fields.filter(
  (f) => !!f.json_id
);
function processFields(fields, sourceObj, targetObj) {
  for (const field of fields) {
    let { id, json_id, kind, required, defaultValue } = field;
    let value = defaultValue;
    if (id) {
      defaultValue = void 0;
      value = getProp2(sourceObj, id, kind, defaultValue);
      required = /*field.required ||*/
      kind == "text";
    } else {
      if (!required)
        value = void 0;
    }
    if (value !== void 0 || required) {
      if (value === void 0) {
        if (required)
          value = "";
        else
          continue;
      }
      if (value === "" && !required)
        continue;
      setNestedValue(targetObj, json_id || "", value);
    }
  }
}
function fusion_json_output_single(tool, sorted = true) {
  const row = {};
  const tfields = filterFieldsForTypes(tool_fields, [tool.type]);
  const pfields = filterFieldsForTypes(preset_fields, [tool.type]);
  processFields(tfields, tool, row);
  const tool_type = row["type"];
  if (row.holder && row.holder.segments === void 0) {
    delete row.holder;
  }
  if (!row.guid) {
    row.guid = random_default.randomUUID();
  }
  if (row.holder) {
    row.holder.type = "holder";
    if (!row.holder.guid) {
      row.holder.guid = random_default.randomUUID();
    }
    if (!row.holder.unit) {
      row.holder.unit = row.unit;
    }
    const unitScale = getUnitScaleFactor(row.holder.unit, row.unit);
    if (row.geometry && row.holder.gaugeLength && row.geometry.LB)
      row.geometry.assemblyGaugeLength = unitScale * row.holder.gaugeLength + row.geometry.LB;
  }
  if (row.shaft) {
    row.shaft.type = "shaft";
  }
  if (tool_type === "holder" && row.holder) {
    row.gaugeLength = row.holder.gaugeLength;
    row.segments = row.holder.segments;
    delete row.holder;
  }
  if (tool.presets && tool.presets.length > 0) {
    row["start-values"] = { presets: [] };
    for (const preset of tool.presets) {
      const preset_row = {};
      fixUpPreset(tool, preset);
      processFields(pfields, preset, preset_row);
      const json_preset = preset_row["start-values"]?.presets;
      if (!json_preset.guid) {
        json_preset.guid = random_default.randomUUID();
      }
      delete json_preset.description;
      json_preset.material = {
        category: "all",
        query: "",
        "use-hardness": false
      };
      const expressions = {};
      if (isApprox(json_preset.v_f_leadIn, json_preset.v_f))
        expressions["tool_feedEntry"] = "tool_feedCutting";
      if (isApprox(json_preset.v_f_leadOut, json_preset.v_f))
        expressions["tool_feedExit"] = "tool_feedEntry";
      if (isApprox(json_preset.v_f_transition, json_preset.v_f))
        expressions["tool_feedTransition"] = "tool_feedCutting";
      if (isApprox(json_preset.n_ramp, json_preset.n))
        expressions["tool_rampSpindleSpeed"] = "tool_spindleSpeed";
      if (Object.keys(expressions).length > 0)
        json_preset.expressions = expressions;
      row["start-values"].presets.push(json_preset);
    }
  }
  if (sorted) {
    return sortObjectKeys(row);
  }
  return row;
}
function fusion_json_output_library(tools, sorted = true) {
  const output = {
    version: fusion_library_version,
    data: []
  };
  for (const tool of tools) {
    const fusionTool = fusion_json_output_single(tool, false);
    output.data.push(fusionTool);
  }
  if (sorted) {
    output.data = sortObjectKeys(output.data);
  }
  return output;
}
function fusion_json_output(tools) {
  const output = fusion_json_output_library(tools, true);
  return JSON.stringify(output, (key, value) => typeof value === "string" ? String(value) : value, 4);
}
function toJsonSegments(segments) {
  const fusion_segments = [];
  for (const segment of segments) {
    const fusion_segment = {
      height: segment.height,
      "lower-diameter": segment.lowerDiameter,
      "upper-diameter": segment.upperDiameter
    };
    fusion_segments.push(fusion_segment);
  }
  return fusion_segments;
}
function getProp2(obj, path, kind, defaultValue) {
  let value = getNestedValue(obj, path);
  if (value === void 0 || typeof value === "string" && value.length === 0) {
    value = defaultValue;
  }
  if (value === void 0) {
    value = "";
  }
  if (typeof value === "string" && !["text", "enum", "guid"].includes(kind)) {
    return void 0;
  }
  if (kind === "segments")
    return toJsonSegments(value);
  else
    return parseValue3(value, kind);
}
function validate_preset_parameters(tool, preset) {
  const validFields = getValidFieldsForType(tool.type).filter((f) => !!f.id && f.id?.startsWith("preset."));
  let validFieldIds = validFields.map((f) => f.id || "__" + f.json_id || "");
  let requiredFields = validFields.filter((f) => f.id?.startsWith("preset."));
  let requiredFieldIds = validFields.map((f) => f.id || "__" + f.json_id || "");
  const firstPreset = { preset: tool.presets[0] };
  const fieldIds = [...getObjectPaths(tool), ...getObjectPaths(firstPreset)];
  const missingFields = requiredFieldIds.filter((fieldId) => !fieldIds.includes(fieldId));
  const invalidFields = fieldIds.filter((fieldId) => !validFieldIds.includes(fieldId));
  return { missingFields, invalidFields };
}
function validate_tool_parameters(tool) {
  const validFields = getValidFieldsForType(tool.type).filter((f) => !!f.json_id);
  let validFieldIds = validFields.map((f) => f.json_id || "");
  validFieldIds.push("__presets");
  validFieldIds.push("holder.segments");
  validFieldIds.push("holder.gaugeLength");
  validFieldIds.push("shaft.segments");
  let requiredFields = validFields.filter(
    (f) => !f.json_id?.startsWith("start-values.") && !f.json_id?.startsWith("holder.") && !f.json_id?.startsWith("shaft.")
  );
  let requiredFieldIds = requiredFields.map((f) => f.json_id || "");
  const firstPreset = { preset: tool.presets[0] };
  const fieldIds = getObjectPaths(tool);
  const fieldJsonIds = fieldIds.map((id) => fusion_field_by_id[id]?.json_id || "__" + id);
  const missingFields = requiredFieldIds.filter((fieldId) => !fieldJsonIds.includes(fieldId));
  const invalidFields = fieldJsonIds.filter((fieldId) => !validFieldIds.includes(fieldId)).filter((id) => !id.startsWith("__fusion"));
  return { missingFields, invalidFields };
}
function validate_tools_parameters(tools) {
  return tools.map((tool) => {
    return {
      number: tool.post_number,
      type: tool.type,
      ...validate_tool_parameters(tool)
    };
  }).filter((item) => item.missingFields.length > 0 || item.invalidFields.length > 0);
}

// src/fusion/validate_fusion_tool.ts
import Ajv from "ajv";

// src/fusion/types_schemas.json
var types_schemas_default = {
  "turning general": {
    type: "object",
    required: [
      "BMC",
      "guid",
      "post-process",
      "setup",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      geometry: {
        type: "object",
        required: [
          "INSD",
          "RE",
          "S",
          "SCTY"
        ],
        oneOf: [
          {
            oneOf: [
              {
                properties: {
                  SIZE_SPECIFICATION_MODE: {
                    type: "string",
                    enum: [
                      "CE"
                    ]
                  },
                  NON_CUTTING_EDGE_LENGTH: {
                    type: "number"
                  }
                },
                required: [
                  "SIZE_SPECIFICATION_MODE",
                  "NON_CUTTING_EDGE_LENGTH"
                ]
              },
              {
                properties: {
                  SIZE_SPECIFICATION_MODE: {
                    type: "string",
                    enum: [
                      "IC"
                    ]
                  }
                },
                required: [
                  "SIZE_SPECIFICATION_MODE"
                ]
              }
            ],
            required: [
              "SC",
              "EPSR"
            ],
            properties: {
              EPSR: {
                type: "number"
              },
              SC: {
                enum: [
                  "custom"
                ]
              }
            }
          },
          {
            properties: {
              SC: {
                enum: [
                  "A",
                  "B",
                  "C",
                  "D",
                  "E",
                  "K",
                  "L",
                  "M",
                  "R",
                  "S",
                  "T",
                  "V",
                  "W"
                ]
              },
              TC: {
                enum: [
                  "C",
                  "H",
                  "E",
                  "G",
                  "M",
                  "U"
                ]
              },
              RA: {
                type: "number"
              }
            },
            required: [
              "SC",
              "TC",
              "RA"
            ]
          }
        ],
        properties: {
          DRVA: {
            type: "number"
          },
          "3DGeometry": {
            type: "object",
            additionalProperties: false,
            properties: {
              id: {
                type: "string"
              }
            }
          },
          S: {
            type: "number"
          },
          SCTY: {
            enum: [
              "A",
              "B",
              "C",
              "F",
              "G",
              "H",
              "J",
              "M",
              "N",
              "Q",
              "R",
              "T",
              "U",
              "W",
              "X"
            ]
          },
          RE: {
            type: "number"
          },
          INSD: {
            type: "number"
          }
        }
      },
      type: {
        enum: [
          "turning general"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            properties: {
              THSC: {
                enum: [
                  ""
                ]
              }
            },
            required: [
              "THSC"
            ]
          },
          {
            type: "object",
            required: [
              "HAND",
              "LH",
              "MTP",
              "OAL",
              "THSC",
              "W",
              "tool_roundShank"
            ],
            if: {
              properties: {
                tool_roundShank: {
                  const: false
                }
              }
            },
            then: {
              required: [
                "H"
              ]
            },
            properties: {
              MTP: {
                enum: [
                  "C",
                  "D",
                  "M",
                  "P",
                  "S"
                ]
              },
              LH: {
                type: "number"
              },
              OAL: {
                type: "number"
              },
              W: {
                type: "number"
              },
              tool_endCutting: {
                type: "boolean"
              },
              tool_sideCutting: {
                type: "boolean"
              },
              H: {
                type: "number"
              },
              tool_roundShank: {
                type: "boolean"
              },
              CW: {
                type: "number"
              },
              HAND: {
                enum: [
                  "R",
                  "L",
                  "N"
                ]
              },
              THSC: {
                enum: [
                  "A",
                  "B",
                  "C",
                  "D",
                  "E",
                  "F",
                  "G",
                  "H",
                  "J",
                  "K",
                  "L",
                  "M",
                  "N",
                  "P",
                  "Q",
                  "T",
                  "R",
                  "S",
                  "U",
                  "V",
                  "W",
                  "Y",
                  "offset profile",
                  "straight profile"
                ]
              }
            }
          },
          {
            type: "object",
            required: [
              "HAND",
              "LH",
              "MTP",
              "OAL",
              "THSC",
              "W",
              "tool_leadingAngle",
              "tool_roundShank",
              "tool_trailingAngle"
            ],
            if: {
              properties: {
                tool_roundShank: {
                  const: false
                }
              }
            },
            then: {
              required: [
                "H"
              ]
            },
            properties: {
              MTP: {
                enum: [
                  "C",
                  "D",
                  "M",
                  "P",
                  "S"
                ]
              },
              LH: {
                type: "number"
              },
              OAL: {
                type: "number"
              },
              W: {
                type: "number"
              },
              tool_endCutting: {
                type: "boolean"
              },
              tool_sideCutting: {
                type: "boolean"
              },
              H: {
                type: "number"
              },
              tool_roundShank: {
                type: "boolean"
              },
              CW: {
                type: "number"
              },
              HAND: {
                enum: [
                  "R",
                  "L",
                  "N"
                ]
              },
              tool_trailingAngle: {
                type: "number"
              },
              tool_leadingAngle: {
                type: "number"
              },
              THSC: {
                enum: [
                  "custom"
                ]
              }
            }
          }
        ]
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "name",
                "tool-coolant",
                "use-constant-surface-speed",
                "use-depth-of-cut",
                "use-feed-per-revolution"
              ],
              if: {
                properties: {
                  "use-feed-per-revolution": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "f_n",
                  "f_n_leadIn",
                  "f_n_leadOut"
                ]
              },
              else: {
                required: [
                  "v_f",
                  "v_f_leadIn",
                  "v_f_leadOut"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                f_n: {
                  type: "number"
                },
                f_n_leadIn: {
                  type: "number"
                },
                f_n_leadOut: {
                  type: "number"
                },
                "use-feed-per-revolution": {
                  type: "boolean"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                "use-constant-surface-speed": {
                  type: "boolean"
                },
                v_c: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                "use-depth-of-cut": {
                  type: "boolean"
                },
                "depth-of-cut": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      setup: {
        type: "object",
        required: [
          "HAND",
          "compensation",
          "angle"
        ],
        properties: {
          HAND: {
            type: "boolean"
          },
          angle: {
            type: "number"
          },
          compensation: {
            enum: [
              "insert center",
              "tip",
              "tip center",
              "tip tangent"
            ]
          },
          tool_shaftAxisAngle: {
            type: "number"
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "compensation-offset",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          number: {
            type: "number"
          },
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "compensation-offset": {
            type: "number",
            minimum: 0
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          }
        }
      },
      "tool-block": {
        oneOf: [
          {
            type: "object",
            required: [
              "guid",
              "type",
              "unit"
            ],
            properties: {
              type: {
                enum: [
                  "tool block"
                ]
              },
              geometry: {
                type: "object",
                additionalProperties: false,
                properties: {
                  "3DGeometry": {
                    type: "object",
                    additionalProperties: false,
                    properties: {
                      id: {
                        type: "string"
                      }
                    }
                  }
                }
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "guid",
              "type",
              "unit"
            ],
            properties: {
              type: {
                enum: [
                  "tool block"
                ]
              },
              geometry: {
                type: "object",
                additionalProperties: false,
                properties: {
                  "3DGeometry": {
                    type: "object",
                    additionalProperties: false,
                    properties: {
                      id: {
                        type: "string"
                      }
                    }
                  }
                }
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "tool block": {
    type: "object",
    required: [
      "guid",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "tool block"
        ]
      },
      geometry: {
        type: "object",
        additionalProperties: false,
        properties: {
          "3DGeometry": {
            type: "object",
            additionalProperties: false,
            properties: {
              id: {
                type: "string"
              }
            }
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "turning boring": {
    type: "object",
    required: [
      "BMC",
      "guid",
      "post-process",
      "setup",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      geometry: {
        type: "object",
        required: [
          "INSD",
          "RE",
          "S",
          "SCTY"
        ],
        oneOf: [
          {
            oneOf: [
              {
                properties: {
                  SIZE_SPECIFICATION_MODE: {
                    type: "string",
                    enum: [
                      "CE"
                    ]
                  },
                  NON_CUTTING_EDGE_LENGTH: {
                    type: "number"
                  }
                },
                required: [
                  "SIZE_SPECIFICATION_MODE",
                  "NON_CUTTING_EDGE_LENGTH"
                ]
              },
              {
                properties: {
                  SIZE_SPECIFICATION_MODE: {
                    type: "string",
                    enum: [
                      "IC"
                    ]
                  }
                },
                required: [
                  "SIZE_SPECIFICATION_MODE"
                ]
              }
            ],
            required: [
              "SC",
              "EPSR"
            ],
            properties: {
              EPSR: {
                type: "number"
              },
              SC: {
                enum: [
                  "custom"
                ]
              }
            }
          },
          {
            properties: {
              SC: {
                enum: [
                  "A",
                  "B",
                  "C",
                  "D",
                  "E",
                  "K",
                  "L",
                  "M",
                  "R",
                  "S",
                  "T",
                  "V",
                  "W"
                ]
              },
              TC: {
                enum: [
                  "C",
                  "H",
                  "E",
                  "G",
                  "M",
                  "U"
                ]
              },
              RA: {
                type: "number"
              }
            },
            required: [
              "SC",
              "TC",
              "RA"
            ]
          }
        ],
        properties: {
          DRVA: {
            type: "number"
          },
          "3DGeometry": {
            type: "object",
            additionalProperties: false,
            properties: {
              id: {
                type: "string"
              }
            }
          },
          S: {
            type: "number"
          },
          SCTY: {
            enum: [
              "A",
              "B",
              "C",
              "F",
              "G",
              "H",
              "J",
              "M",
              "N",
              "Q",
              "R",
              "T",
              "U",
              "W",
              "X"
            ]
          },
          RE: {
            type: "number"
          },
          INSD: {
            type: "number"
          }
        }
      },
      type: {
        enum: [
          "turning boring"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            properties: {
              THSC: {
                enum: [
                  ""
                ]
              }
            },
            required: [
              "THSC"
            ]
          },
          {
            type: "object",
            required: [
              "HAND",
              "LH",
              "MTP",
              "OAL",
              "THSC",
              "W"
            ],
            if: {
              properties: {
                HAND: {
                  pattern: "[R]|[L]"
                }
              }
            },
            then: {
              required: [
                "CW"
              ]
            },
            properties: {
              MTP: {
                enum: [
                  "C",
                  "D",
                  "M",
                  "P",
                  "S"
                ]
              },
              LH: {
                type: "number"
              },
              OAL: {
                type: "number"
              },
              W: {
                type: "number"
              },
              tool_endCutting: {
                type: "boolean"
              },
              tool_sideCutting: {
                type: "boolean"
              },
              CW: {
                type: "number"
              },
              HAND: {
                enum: [
                  "R",
                  "L",
                  "N"
                ]
              },
              THSC: {
                enum: [
                  "boring bar f",
                  "boring bar g",
                  "boring bar j",
                  "boring bar k",
                  "boring bar l",
                  "boring bar p",
                  "boring bar q",
                  "boring bar u"
                ]
              }
            }
          },
          {
            type: "object",
            required: [
              "HAND",
              "LH",
              "MTP",
              "OAL",
              "THSC",
              "W",
              "tool_leadingAngle"
            ],
            if: {
              properties: {
                HAND: {
                  pattern: "[R]|[L]"
                }
              }
            },
            then: {
              required: [
                "CW"
              ]
            },
            properties: {
              MTP: {
                enum: [
                  "C",
                  "D",
                  "M",
                  "P",
                  "S"
                ]
              },
              LH: {
                type: "number"
              },
              OAL: {
                type: "number"
              },
              W: {
                type: "number"
              },
              tool_endCutting: {
                type: "boolean"
              },
              tool_sideCutting: {
                type: "boolean"
              },
              CW: {
                type: "number"
              },
              HAND: {
                enum: [
                  "R",
                  "L",
                  "N"
                ]
              },
              tool_leadingAngle: {
                type: "number"
              },
              THSC: {
                enum: [
                  "custom"
                ]
              }
            }
          }
        ]
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "name",
                "tool-coolant",
                "use-constant-surface-speed",
                "use-depth-of-cut",
                "use-feed-per-revolution"
              ],
              if: {
                properties: {
                  "use-feed-per-revolution": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "f_n",
                  "f_n_leadIn",
                  "f_n_leadOut"
                ]
              },
              else: {
                required: [
                  "v_f",
                  "v_f_leadIn",
                  "v_f_leadOut"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                f_n: {
                  type: "number"
                },
                f_n_leadIn: {
                  type: "number"
                },
                f_n_leadOut: {
                  type: "number"
                },
                "use-feed-per-revolution": {
                  type: "boolean"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                "use-constant-surface-speed": {
                  type: "boolean"
                },
                v_c: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                "use-depth-of-cut": {
                  type: "boolean"
                },
                "depth-of-cut": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      setup: {
        type: "object",
        required: [
          "HAND",
          "compensation",
          "angle"
        ],
        properties: {
          HAND: {
            type: "boolean"
          },
          angle: {
            type: "number"
          },
          compensation: {
            enum: [
              "insert center",
              "tip",
              "tip center",
              "tip tangent"
            ]
          },
          tool_shaftAxisAngle: {
            type: "number"
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "compensation-offset",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          number: {
            type: "number"
          },
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "compensation-offset": {
            type: "number",
            minimum: 0
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          }
        }
      },
      "tool-block": {
        oneOf: [
          {
            type: "object",
            required: [
              "guid",
              "type",
              "unit"
            ],
            properties: {
              type: {
                enum: [
                  "tool block"
                ]
              },
              geometry: {
                type: "object",
                additionalProperties: false,
                properties: {
                  "3DGeometry": {
                    type: "object",
                    additionalProperties: false,
                    properties: {
                      id: {
                        type: "string"
                      }
                    }
                  }
                }
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "guid",
              "type",
              "unit"
            ],
            properties: {
              type: {
                enum: [
                  "tool block"
                ]
              },
              geometry: {
                type: "object",
                additionalProperties: false,
                properties: {
                  "3DGeometry": {
                    type: "object",
                    additionalProperties: false,
                    properties: {
                      id: {
                        type: "string"
                      }
                    }
                  }
                }
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "turning threading": {
    type: "object",
    required: [
      "BMC",
      "guid",
      "post-process",
      "setup",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "turning threading"
        ]
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "name",
                "tool-coolant"
              ],
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n: {
                  type: "number"
                }
              }
            }
          }
        }
      },
      setup: {
        type: "object",
        required: [
          "HAND",
          "compensation",
          "angle"
        ],
        properties: {
          HAND: {
            type: "boolean"
          },
          angle: {
            type: "number"
          },
          compensation: {
            enum: [
              "insert center",
              "tip",
              "tip center",
              "tip tangent"
            ]
          },
          tool_shaftAxisAngle: {
            type: "number"
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "compensation-offset",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          number: {
            type: "number"
          },
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "compensation-offset": {
            type: "number",
            minimum: 0
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          }
        }
      },
      "tool-block": {
        oneOf: [
          {
            type: "object",
            required: [
              "guid",
              "type",
              "unit"
            ],
            properties: {
              type: {
                enum: [
                  "tool block"
                ]
              },
              geometry: {
                type: "object",
                additionalProperties: false,
                properties: {
                  "3DGeometry": {
                    type: "object",
                    additionalProperties: false,
                    properties: {
                      id: {
                        type: "string"
                      }
                    }
                  }
                }
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "guid",
              "type",
              "unit"
            ],
            properties: {
              type: {
                enum: [
                  "tool block"
                ]
              },
              geometry: {
                type: "object",
                additionalProperties: false,
                properties: {
                  "3DGeometry": {
                    type: "object",
                    additionalProperties: false,
                    properties: {
                      id: {
                        type: "string"
                      }
                    }
                  }
                }
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      geometry: {
        type: "object",
        required: [
          "OAL",
          "S",
          "SC",
          "SCTY",
          "TC",
          "TP",
          "maximum-thread-pitch",
          "minimum-thread-pitch",
          "thread-profile-angle",
          "thread-tip-type",
          "tool_internalThread"
        ],
        if: {
          properties: {
            SC: {
              const: "thread iso double"
            }
          }
        },
        then: {
          required: [
            "tool_insertWidth"
          ]
        },
        properties: {
          DRVA: {
            type: "number"
          },
          "3DGeometry": {
            type: "object",
            additionalProperties: false,
            properties: {
              id: {
                type: "string"
              }
            }
          },
          S: {
            type: "number"
          },
          SCTY: {
            enum: [
              "A",
              "B",
              "C",
              "F",
              "G",
              "H",
              "J",
              "M",
              "N",
              "Q",
              "R",
              "T",
              "U",
              "W",
              "X"
            ]
          },
          TC: {
            enum: [
              "C",
              "H",
              "E",
              "G",
              "M",
              "U"
            ]
          },
          tool_internalThread: {
            type: "boolean"
          },
          tool_insertWidth: {
            type: "number"
          },
          OAL: {
            type: "number"
          },
          SC: {
            enum: [
              "thread iso double",
              "thread iso triple"
            ]
          },
          TP: {
            type: "number"
          },
          "maximum-thread-pitch": {
            type: "number"
          },
          "minimum-thread-pitch": {
            type: "number"
          },
          "thread-profile-angle": {
            type: "number"
          },
          "thread-tip-radius": {
            type: "number"
          },
          "thread-tip-type": {
            enum: [
              "flat",
              "point",
              "round"
            ]
          },
          "thread-tip-width": {
            type: "number"
          }
        }
      },
      holder: {
        type: "object",
        required: [
          "HAND",
          "LH",
          "MTP",
          "OAL",
          "THSC",
          "W",
          "tool_roundShank"
        ],
        if: {
          properties: {
            tool_roundShank: {
              const: false
            }
          }
        },
        then: {
          required: [
            "H"
          ]
        },
        properties: {
          MTP: {
            enum: [
              "C",
              "D",
              "M",
              "P",
              "S"
            ]
          },
          LH: {
            type: "number"
          },
          OAL: {
            type: "number"
          },
          W: {
            type: "number"
          },
          tool_endCutting: {
            type: "boolean"
          },
          tool_sideCutting: {
            type: "boolean"
          },
          H: {
            type: "number"
          },
          tool_roundShank: {
            type: "boolean"
          },
          HAND: {
            enum: [
              "N",
              "R",
              "L"
            ]
          },
          THSC: {
            enum: [
              "thread straight"
            ]
          }
        }
      }
    }
  },
  "turning grooving": {
    type: "object",
    required: [
      "BMC",
      "guid",
      "post-process",
      "setup",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      geometry: {
        type: "object",
        required: [
          "OAL",
          "S",
          "SCTY",
          "TC",
          "tool_grooveWidth",
          "tool_insertWidth"
        ],
        oneOf: [
          {
            properties: {
              SC: {
                enum: [
                  "groove round"
                ]
              }
            },
            required: [
              "SC"
            ]
          },
          {
            properties: {
              LH: {
                type: "number"
              },
              SC: {
                enum: [
                  "groove square"
                ]
              },
              RE: {
                type: "number"
              }
            },
            required: [
              "SC",
              "LH",
              "RE"
            ]
          }
        ],
        properties: {
          DRVA: {
            type: "number"
          },
          "3DGeometry": {
            type: "object",
            additionalProperties: false,
            properties: {
              id: {
                type: "string"
              }
            }
          },
          S: {
            type: "number"
          },
          SCTY: {
            enum: [
              "A",
              "B",
              "C",
              "F",
              "G",
              "H",
              "J",
              "M",
              "N",
              "Q",
              "R",
              "T",
              "U",
              "W",
              "X"
            ]
          },
          TC: {
            enum: [
              "C",
              "H",
              "E",
              "G",
              "M",
              "U"
            ]
          },
          tool_grooveWidth: {
            type: "number"
          },
          tool_insertWidth: {
            type: "number"
          },
          OAL: {
            type: "number"
          }
        }
      },
      type: {
        enum: [
          "turning grooving"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "HAND",
              "LH",
              "MTP",
              "OAL",
              "THSC",
              "W"
            ],
            if: {
              properties: {
                HAND: {
                  pattern: "[R]|[L]"
                }
              }
            },
            then: {
              required: [
                "CW"
              ]
            },
            properties: {
              MTP: {
                enum: [
                  "C",
                  "D",
                  "M",
                  "P",
                  "S"
                ]
              },
              LH: {
                type: "number"
              },
              OAL: {
                type: "number"
              },
              W: {
                type: "number"
              },
              tool_endCutting: {
                type: "boolean"
              },
              tool_sideCutting: {
                type: "boolean"
              },
              CW: {
                type: "number"
              },
              HAND: {
                enum: [
                  "R",
                  "L",
                  "N"
                ]
              },
              THSC: {
                enum: [
                  "groove internal"
                ]
              }
            }
          },
          {
            type: "object",
            required: [
              "HAND",
              "LH",
              "MTP",
              "OAL",
              "THSC",
              "W",
              "tool_roundShank"
            ],
            if: {
              properties: {
                tool_roundShank: {
                  const: false
                }
              }
            },
            then: {
              required: [
                "H"
              ]
            },
            properties: {
              MTP: {
                enum: [
                  "C",
                  "D",
                  "M",
                  "P",
                  "S"
                ]
              },
              LH: {
                type: "number"
              },
              OAL: {
                type: "number"
              },
              W: {
                type: "number"
              },
              tool_endCutting: {
                type: "boolean"
              },
              tool_sideCutting: {
                type: "boolean"
              },
              H: {
                type: "number"
              },
              tool_roundShank: {
                type: "boolean"
              },
              THSC: {
                enum: [
                  "groove external"
                ]
              },
              HAND: {
                enum: [
                  "N",
                  "R",
                  "L"
                ]
              }
            }
          },
          {
            type: "object",
            required: [
              "HAND",
              "MTP",
              "OAL",
              "THSC",
              "W",
              "tool_roundShank"
            ],
            if: {
              properties: {
                tool_roundShank: {
                  const: false
                }
              }
            },
            then: {
              required: [
                "H"
              ]
            },
            properties: {
              MTP: {
                enum: [
                  "C",
                  "D",
                  "M",
                  "P",
                  "S"
                ]
              },
              LH: {
                type: "number"
              },
              OAL: {
                type: "number"
              },
              W: {
                type: "number"
              },
              tool_endCutting: {
                type: "boolean"
              },
              tool_sideCutting: {
                type: "boolean"
              },
              H: {
                type: "number"
              },
              tool_roundShank: {
                type: "boolean"
              },
              CW: {
                type: "number"
              },
              HAND: {
                enum: [
                  "R",
                  "L",
                  "N"
                ]
              },
              THSC: {
                enum: [
                  "groove face"
                ]
              }
            }
          },
          {
            type: "object",
            properties: {
              THSC: {
                enum: [
                  ""
                ]
              }
            },
            required: [
              "THSC"
            ]
          }
        ]
      },
      setup: {
        required: [
          "HAND",
          "angle",
          "compensation"
        ],
        if: {
          properties: {
            compensation: {
              enum: [
                "tip",
                "tip tangent"
              ]
            }
          }
        },
        then: {
          required: [
            "tool_grooveCompOppositeEdge"
          ]
        },
        type: "object",
        properties: {
          compensation: {
            enum: [
              "insert center",
              "tip",
              "tip center",
              "tip tangent"
            ]
          },
          tool_grooveCompOppositeEdge: {
            type: "boolean"
          },
          HAND: {
            type: "boolean"
          },
          angle: {
            type: "number"
          },
          tool_shaftAxisAngle: {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "name",
                "tool-coolant",
                "use-constant-surface-speed",
                "use-feed-per-revolution"
              ],
              if: {
                properties: {
                  "use-feed-per-revolution": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "f_n",
                  "f_n_leadIn",
                  "f_n_leadOut"
                ]
              },
              else: {
                required: [
                  "v_f",
                  "v_f_leadIn",
                  "v_f_leadOut"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                f_n: {
                  type: "number"
                },
                f_n_leadIn: {
                  type: "number"
                },
                f_n_leadOut: {
                  type: "number"
                },
                "use-feed-per-revolution": {
                  type: "boolean"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                "use-constant-surface-speed": {
                  type: "boolean"
                },
                v_c: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "compensation-offset",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          number: {
            type: "number"
          },
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "compensation-offset": {
            type: "number",
            minimum: 0
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          }
        }
      },
      "tool-block": {
        oneOf: [
          {
            type: "object",
            required: [
              "guid",
              "type",
              "unit"
            ],
            properties: {
              type: {
                enum: [
                  "tool block"
                ]
              },
              geometry: {
                type: "object",
                additionalProperties: false,
                properties: {
                  "3DGeometry": {
                    type: "object",
                    additionalProperties: false,
                    properties: {
                      id: {
                        type: "string"
                      }
                    }
                  }
                }
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "guid",
              "type",
              "unit"
            ],
            properties: {
              type: {
                enum: [
                  "tool block"
                ]
              },
              geometry: {
                type: "object",
                additionalProperties: false,
                properties: {
                  "3DGeometry": {
                    type: "object",
                    additionalProperties: false,
                    properties: {
                      id: {
                        type: "string"
                      }
                    }
                  }
                }
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "flat end mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "flat end mill",
          "ball end mill",
          "lollipop mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          }
        }
      }
    }
  },
  "ball end mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "flat end mill",
          "ball end mill",
          "lollipop mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          }
        }
      }
    }
  },
  "lollipop mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "flat end mill",
          "ball end mill",
          "lollipop mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          }
        }
      }
    }
  },
  holder: {
    type: "object",
    required: [
      "gaugeLength",
      "guid",
      "type",
      "unit"
    ],
    properties: {
      segments: {
        type: "array",
        items: {
          type: "object",
          required: [
            "height",
            "lower-diameter",
            "upper-diameter"
          ],
          additionalProperties: false,
          properties: {
            height: {
              type: "number"
            },
            "lower-diameter": {
              type: "number"
            },
            "upper-diameter": {
              type: "number"
            }
          }
        }
      },
      type: {
        enum: [
          "holder"
        ]
      },
      gaugeLength: {
        type: "number"
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "bull nose end mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "bull nose end mill",
          "slot mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "RE",
          "SFDM",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          RE: {
            type: "number"
          }
        }
      }
    }
  },
  "slot mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "bull nose end mill",
          "slot mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "RE",
          "SFDM",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          RE: {
            type: "number"
          }
        }
      }
    }
  },
  "dovetail mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "dovetail mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "RE",
          "SFDM",
          "TA",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          TA: {
            type: "number"
          },
          RE: {
            type: "number"
          }
        }
      }
    }
  },
  "face mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "face mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "DCX",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "RE",
          "SFDM",
          "TA",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length",
          "upper-radius"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          TA: {
            type: "number"
          },
          RE: {
            type: "number"
          },
          "upper-radius": {
            type: "number"
          }
        }
      }
    }
  },
  "chamfer mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "chamfer mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "TA",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length",
          "tip-diameter"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          TA: {
            type: "number"
          },
          "tip-diameter": {
            type: "number"
          }
        }
      }
    }
  },
  "corner chamfer end mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "corner chamfer end mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "assemblyGaugeLength",
          "chamfer-angle",
          "chamfer-width",
          "shoulder-diameter",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          "chamfer-angle": {
            type: "number"
          },
          "chamfer-width": {
            type: "number"
          }
        }
      }
    }
  },
  "form mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "tip-offset",
          "assemblyGaugeLength"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          profile: {
            type: "array"
          },
          "tip-offset": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          }
        }
      },
      type: {
        enum: [
          "form mill"
        ]
      }
    }
  },
  "circle segment barrel": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    oneOf: [
      {
        properties: {
          type: {
            enum: [
              "circle segment barrel"
            ]
          },
          geometry: {
            required: [
              "SFDM",
              "axial-distance",
              "lower-radius",
              "profile-radius",
              "upper-radius"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment lens"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "RE"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment oval"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "profile-radius"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment taper"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "profile-radius",
              "upper-radius",
              "tip-diameter",
              "TA"
            ]
          }
        }
      }
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "circle segment barrel",
          "circle segment lens",
          "circle segment oval",
          "circle segment taper"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "LB",
          "LCF",
          "OAL",
          "NOF",
          "assemblyGaugeLength",
          "HAND"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          "axial-distance": {
            type: "number"
          },
          "lower-radius": {
            type: "number"
          },
          "profile-radius": {
            type: "number"
          },
          "upper-radius": {
            type: "number"
          }
        }
      }
    }
  },
  "circle segment lens": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    oneOf: [
      {
        properties: {
          type: {
            enum: [
              "circle segment barrel"
            ]
          },
          geometry: {
            required: [
              "SFDM",
              "axial-distance",
              "lower-radius",
              "profile-radius",
              "upper-radius"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment lens"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "RE"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment oval"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "profile-radius"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment taper"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "profile-radius",
              "upper-radius",
              "tip-diameter",
              "TA"
            ]
          }
        }
      }
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "circle segment barrel",
          "circle segment lens",
          "circle segment oval",
          "circle segment taper"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "LB",
          "LCF",
          "OAL",
          "NOF",
          "assemblyGaugeLength",
          "HAND"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          "axial-distance": {
            type: "number"
          },
          "lower-radius": {
            type: "number"
          },
          "profile-radius": {
            type: "number"
          },
          "upper-radius": {
            type: "number"
          }
        }
      }
    }
  },
  "circle segment oval": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    oneOf: [
      {
        properties: {
          type: {
            enum: [
              "circle segment barrel"
            ]
          },
          geometry: {
            required: [
              "SFDM",
              "axial-distance",
              "lower-radius",
              "profile-radius",
              "upper-radius"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment lens"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "RE"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment oval"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "profile-radius"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment taper"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "profile-radius",
              "upper-radius",
              "tip-diameter",
              "TA"
            ]
          }
        }
      }
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "circle segment barrel",
          "circle segment lens",
          "circle segment oval",
          "circle segment taper"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "LB",
          "LCF",
          "OAL",
          "NOF",
          "assemblyGaugeLength",
          "HAND"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          "axial-distance": {
            type: "number"
          },
          "lower-radius": {
            type: "number"
          },
          "profile-radius": {
            type: "number"
          },
          "upper-radius": {
            type: "number"
          }
        }
      }
    }
  },
  "circle segment taper": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    oneOf: [
      {
        properties: {
          type: {
            enum: [
              "circle segment barrel"
            ]
          },
          geometry: {
            required: [
              "SFDM",
              "axial-distance",
              "lower-radius",
              "profile-radius",
              "upper-radius"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment lens"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "RE"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment oval"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "profile-radius"
            ]
          }
        }
      },
      {
        properties: {
          type: {
            enum: [
              "circle segment taper"
            ]
          },
          geometry: {
            required: [
              "lower-radius",
              "profile-radius",
              "upper-radius",
              "tip-diameter",
              "TA"
            ]
          }
        }
      }
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "circle segment barrel",
          "circle segment lens",
          "circle segment oval",
          "circle segment taper"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "LB",
          "LCF",
          "OAL",
          "NOF",
          "assemblyGaugeLength",
          "HAND"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          "axial-distance": {
            type: "number"
          },
          "lower-radius": {
            type: "number"
          },
          "profile-radius": {
            type: "number"
          },
          "upper-radius": {
            type: "number"
          }
        }
      }
    }
  },
  "thread mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "thread mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "NT",
          "OAL",
          "SFDM",
          "TP",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length",
          "thread-profile-angle"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          NT: {
            type: "number"
          },
          TP: {
            type: "number"
          },
          "thread-profile-angle": {
            type: "number"
          }
        }
      }
    }
  },
  "tapered mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "tapered-type",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "tapered mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "RE",
          "SFDM",
          "TA",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          TA: {
            type: "number"
          },
          RE: {
            type: "number"
          }
        }
      },
      "tapered-type": {
        enum: [
          "tapered_ball",
          "tapered_bull_nose"
        ]
      }
    }
  },
  "radius mill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "n_ramp",
                "name",
                "ramp-angle",
                "tool-coolant",
                "use-stepdown",
                "use-stepover",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                "use-stepdown": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                stepdown: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                },
                n_ramp: {
                  type: "number"
                },
                "ramp-angle": {
                  type: "number"
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      },
      type: {
        enum: [
          "radius mill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "RE",
          "SFDM",
          "assemblyGaugeLength",
          "shoulder-diameter",
          "shoulder-length",
          "tip-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          "tip-length": {
            type: "number"
          },
          RE: {
            type: "number"
          }
        }
      }
    }
  },
  "plasma cutter": {
    type: "object",
    required: [
      "guid",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      geometry: {
        type: "object",
        required: [
          "CW",
          "JET_HEAD_CLEARANCE",
          "JET_NOZZLE_DIAMETER"
        ],
        additionalProperties: false,
        properties: {
          CW: {
            type: "number"
          },
          JET_HEAD_CLEARANCE: {
            type: "number"
          },
          JET_NOZZLE_DIAMETER: {
            type: "number"
          },
          lensType: {
            type: "string"
          },
          nozzleStandoff: {
            type: "number"
          },
          pierceDiameter: {
            type: "number"
          }
        }
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      type: {
        enum: [
          "plasma cutter",
          "laser cutter",
          "waterjet"
        ]
      },
      "post-process": {
        type: "object",
        required: [
          "diameter-offset",
          "length-offset",
          "manual-tool-change",
          "number"
        ],
        properties: {
          number: {
            type: "number"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          "manual-tool-change": {
            type: "boolean"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "cutHeight",
                "guid",
                "machineQualityControl",
                "material",
                "name"
              ],
              if: {
                properties: {
                  machineQualityControl: {
                    const: "manual"
                  }
                }
              },
              then: {
                required: [
                  "v_f",
                  "v_f_leadIn",
                  "v_f_leadOut"
                ]
              },
              properties: {
                machineQualityControl: {
                  enum: [
                    "automatic",
                    "manual"
                  ]
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                cutHeight: {
                  type: "number"
                },
                pierceHeight: {
                  type: "number"
                },
                assistGas: {
                  type: "string"
                },
                pressure: {
                  type: "number"
                },
                abrasiveFlowRate: {
                  type: "number"
                },
                piercePower: {
                  type: "number"
                },
                pierceTime: {
                  type: "number"
                },
                cutPower: {
                  type: "number"
                },
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "laser cutter": {
    type: "object",
    required: [
      "guid",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      geometry: {
        type: "object",
        required: [
          "CW",
          "JET_HEAD_CLEARANCE",
          "JET_NOZZLE_DIAMETER"
        ],
        additionalProperties: false,
        properties: {
          CW: {
            type: "number"
          },
          JET_HEAD_CLEARANCE: {
            type: "number"
          },
          JET_NOZZLE_DIAMETER: {
            type: "number"
          },
          lensType: {
            type: "string"
          },
          nozzleStandoff: {
            type: "number"
          },
          pierceDiameter: {
            type: "number"
          }
        }
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      type: {
        enum: [
          "plasma cutter",
          "laser cutter",
          "waterjet"
        ]
      },
      "post-process": {
        type: "object",
        required: [
          "diameter-offset",
          "length-offset",
          "manual-tool-change",
          "number"
        ],
        properties: {
          number: {
            type: "number"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          "manual-tool-change": {
            type: "boolean"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "cutHeight",
                "guid",
                "machineQualityControl",
                "material",
                "name"
              ],
              if: {
                properties: {
                  machineQualityControl: {
                    const: "manual"
                  }
                }
              },
              then: {
                required: [
                  "v_f",
                  "v_f_leadIn",
                  "v_f_leadOut"
                ]
              },
              properties: {
                machineQualityControl: {
                  enum: [
                    "automatic",
                    "manual"
                  ]
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                cutHeight: {
                  type: "number"
                },
                pierceHeight: {
                  type: "number"
                },
                assistGas: {
                  type: "string"
                },
                pressure: {
                  type: "number"
                },
                abrasiveFlowRate: {
                  type: "number"
                },
                piercePower: {
                  type: "number"
                },
                pierceTime: {
                  type: "number"
                },
                cutPower: {
                  type: "number"
                },
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  waterjet: {
    type: "object",
    required: [
      "guid",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      geometry: {
        type: "object",
        required: [
          "CW",
          "JET_HEAD_CLEARANCE",
          "JET_NOZZLE_DIAMETER"
        ],
        additionalProperties: false,
        properties: {
          CW: {
            type: "number"
          },
          JET_HEAD_CLEARANCE: {
            type: "number"
          },
          JET_NOZZLE_DIAMETER: {
            type: "number"
          },
          lensType: {
            type: "string"
          },
          nozzleStandoff: {
            type: "number"
          },
          pierceDiameter: {
            type: "number"
          }
        }
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      type: {
        enum: [
          "plasma cutter",
          "laser cutter",
          "waterjet"
        ]
      },
      "post-process": {
        type: "object",
        required: [
          "diameter-offset",
          "length-offset",
          "manual-tool-change",
          "number"
        ],
        properties: {
          number: {
            type: "number"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          "manual-tool-change": {
            type: "boolean"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "cutHeight",
                "guid",
                "machineQualityControl",
                "material",
                "name"
              ],
              if: {
                properties: {
                  machineQualityControl: {
                    const: "manual"
                  }
                }
              },
              then: {
                required: [
                  "v_f",
                  "v_f_leadIn",
                  "v_f_leadOut"
                ]
              },
              properties: {
                machineQualityControl: {
                  enum: [
                    "automatic",
                    "manual"
                  ]
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                cutHeight: {
                  type: "number"
                },
                pierceHeight: {
                  type: "number"
                },
                assistGas: {
                  type: "string"
                },
                pressure: {
                  type: "number"
                },
                abrasiveFlowRate: {
                  type: "number"
                },
                piercePower: {
                  type: "number"
                },
                pierceTime: {
                  type: "number"
                },
                cutPower: {
                  type: "number"
                },
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "electric arc wire": {
    type: "object",
    required: [
      "guid",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "electric arc wire"
        ]
      },
      "post-process": {
        type: "object",
        required: [
          "length-offset",
          "number"
        ],
        properties: {
          comment: {
            type: "string"
          },
          "length-offset": {
            type: "number"
          },
          number: {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "BEAD_WIDTH",
                "DEPOSITING_CURRENT",
                "DEPOSITING_SHIELD_GAS_FLOW_RATE",
                "DEPOSITING_VOLTAGE",
                "LAYER_THICKNESS",
                "STAND_OFF_DISTANCE",
                "USE_PRESET_PROGRAM",
                "WIRE_FEED",
                "guid",
                "material",
                "name",
                "use-stepover",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge"
              ],
              if: {
                properties: {
                  USE_PRESET_PROGRAM: {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "PRESET_PROGRAM"
                ]
              },
              properties: {
                DEPOSITING_CURRENT: {
                  type: "number"
                },
                DEPOSITING_VOLTAGE: {
                  type: "number"
                },
                WIRE_FEED: {
                  type: "number"
                },
                USE_PRESET_PROGRAM: {
                  type: "boolean"
                },
                PRESET_PROGRAM: {
                  type: "number"
                },
                BEAD_WIDTH: {
                  type: "number"
                },
                LAYER_THICKNESS: {
                  type: "number"
                },
                STAND_OFF_DISTANCE: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                DEPOSITING_SHIELD_GAS_FLOW_RATE: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "laser powder": {
    type: "object",
    required: [
      "guid",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "laser powder"
        ]
      },
      "post-process": {
        type: "object",
        required: [
          "length-offset",
          "number"
        ],
        properties: {
          comment: {
            type: "string"
          },
          "length-offset": {
            type: "number"
          },
          number: {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "BEAD_WIDTH",
                "DEPOSITING_AUXILIARY_GAS_FLOW_RATE",
                "DEPOSITING_CARRIER_GAS_FLOW_RATE",
                "DEPOSITING_POWER",
                "DEPOSITING_SHIELD_GAS_FLOW_RATE",
                "LAYER_THICKNESS",
                "POWDER_FLOW_RATE",
                "STAND_OFF_DISTANCE",
                "guid",
                "material",
                "name",
                "use-stepover",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                DEPOSITING_POWER: {
                  type: "number"
                },
                DEPOSITING_CARRIER_GAS_FLOW_RATE: {
                  type: "number"
                },
                DEPOSITING_AUXILIARY_GAS_FLOW_RATE: {
                  type: "number"
                },
                POWDER_FLOW_RATE: {
                  type: "number"
                },
                BEAD_WIDTH: {
                  type: "number"
                },
                LAYER_THICKNESS: {
                  type: "number"
                },
                STAND_OFF_DISTANCE: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                DEPOSITING_SHIELD_GAS_FLOW_RATE: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "laser wire": {
    type: "object",
    required: [
      "guid",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "laser wire"
        ]
      },
      "post-process": {
        type: "object",
        required: [
          "length-offset",
          "number"
        ],
        properties: {
          comment: {
            type: "string"
          },
          "length-offset": {
            type: "number"
          },
          number: {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "BEAD_WIDTH",
                "DEPOSITING_AUXILIARY_GAS_FLOW_RATE",
                "DEPOSITING_POWER",
                "DEPOSITING_SHIELD_GAS_FLOW_RATE",
                "LAYER_THICKNESS",
                "STAND_OFF_DISTANCE",
                "WIRE_FEED",
                "guid",
                "material",
                "name",
                "use-stepover",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge"
              ],
              if: {
                properties: {
                  "use-stepover": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "stepover"
                ]
              },
              properties: {
                DEPOSITING_POWER: {
                  type: "number"
                },
                DEPOSITING_AUXILIARY_GAS_FLOW_RATE: {
                  type: "number"
                },
                WIRE_FEED: {
                  type: "number"
                },
                BEAD_WIDTH: {
                  type: "number"
                },
                LAYER_THICKNESS: {
                  type: "number"
                },
                STAND_OFF_DISTANCE: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                DEPOSITING_SHIELD_GAS_FLOW_RATE: {
                  type: "number"
                },
                "use-stepover": {
                  type: "boolean"
                },
                stepover: {
                  type: "number"
                },
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  probe: {
    type: "object",
    required: [
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      geometry: {
        type: "object",
        required: [
          "DC",
          "LB",
          "SFDM",
          "assemblyGaugeLength"
        ],
        properties: {
          DC: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          }
        }
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      type: {
        enum: [
          "probe"
        ]
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          number: {
            type: "number"
          },
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "name",
                "v_f_leadIn",
                "v_f_link",
                "v_f_measure"
              ],
              properties: {
                v_f_leadIn: {
                  type: "number",
                  minimum: 0
                },
                v_f_link: {
                  type: "number",
                  minimum: 0
                },
                v_f_measure: {
                  type: "number",
                  minimum: 0
                },
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                }
              }
            }
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "tap right hand": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "tap right hand",
          "tap left hand"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "TP",
          "assemblyGaugeLength",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          TP: {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "n",
                "name",
                "tool-coolant",
                "v_c"
              ],
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "tap left hand": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "tap right hand",
          "tap left hand"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "TP",
          "assemblyGaugeLength",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          TP: {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "n",
                "name",
                "tool-coolant",
                "v_c"
              ],
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "counter bore": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "counter bore",
          "boring bar"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "assemblyGaugeLength",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "name",
                "tool-coolant",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "boring bar": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "counter bore",
          "boring bar"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "assemblyGaugeLength",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "f_n",
                "guid",
                "material",
                "n",
                "name",
                "tool-coolant",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_plunge",
                "v_f_ramp",
                "v_f_transition"
              ],
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "spot drill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "spot drill",
          "counter sink"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "SIG",
          "assemblyGaugeLength",
          "shoulder-length",
          "tip-diameter"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          SIG: {
            type: "number"
          },
          "tip-diameter": {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "n",
                "name",
                "tool-coolant",
                "use-feed-per-revolution",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-feed-per-revolution": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "f_n_retract",
                  "f_n"
                ]
              },
              else: {
                required: [
                  "v_f_retract",
                  "v_f_plunge"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n_retract: {
                  type: "number"
                },
                "use-feed-per-revolution": {
                  type: "boolean"
                },
                v_f_retract: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "counter sink": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "spot drill",
          "counter sink"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "SIG",
          "assemblyGaugeLength",
          "shoulder-length",
          "tip-diameter"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          SIG: {
            type: "number"
          },
          "tip-diameter": {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "n",
                "name",
                "tool-coolant",
                "use-feed-per-revolution",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-feed-per-revolution": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "f_n_retract",
                  "f_n"
                ]
              },
              else: {
                required: [
                  "v_f_retract",
                  "v_f_plunge"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n_retract: {
                  type: "number"
                },
                "use-feed-per-revolution": {
                  type: "boolean"
                },
                v_f_retract: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  "center drill": {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "center drill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "SIG",
          "TA",
          "assemblyGaugeLength",
          "shoulder-length",
          "tip-diameter",
          "tip-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          TA: {
            type: "number"
          },
          "tip-diameter": {
            type: "number"
          },
          "tip-length": {
            type: "number"
          },
          SIG: {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "n",
                "name",
                "tool-coolant",
                "use-feed-per-revolution",
                "v_c",
                "v_f",
                "v_f_leadIn",
                "v_f_leadOut",
                "v_f_ramp",
                "v_f_transition"
              ],
              if: {
                properties: {
                  "use-feed-per-revolution": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "f_n_retract",
                  "f_n"
                ]
              },
              else: {
                required: [
                  "v_f_retract",
                  "v_f_plunge"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_z: {
                  type: "number"
                },
                v_f: {
                  type: "number"
                },
                v_f_leadIn: {
                  type: "number"
                },
                v_f_leadOut: {
                  type: "number"
                },
                v_f_transition: {
                  type: "number"
                },
                v_f_ramp: {
                  type: "number"
                },
                f_n_retract: {
                  type: "number"
                },
                "use-feed-per-revolution": {
                  type: "boolean"
                },
                v_f_retract: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  drill: {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "drill"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "SIG",
          "assemblyGaugeLength",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          },
          SIG: {
            type: "number"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "n",
                "name",
                "tool-coolant",
                "use-feed-per-revolution",
                "v_c"
              ],
              if: {
                properties: {
                  "use-feed-per-revolution": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "f_n_retract",
                  "f_n"
                ]
              },
              else: {
                required: [
                  "v_f_retract",
                  "v_f_plunge"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_n_retract: {
                  type: "number"
                },
                "use-feed-per-revolution": {
                  type: "boolean"
                },
                v_f_retract: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  },
  reamer: {
    type: "object",
    required: [
      "BMC",
      "geometry",
      "guid",
      "post-process",
      "start-values",
      "type",
      "unit"
    ],
    properties: {
      type: {
        enum: [
          "reamer"
        ]
      },
      geometry: {
        type: "object",
        required: [
          "CSP",
          "DC",
          "HAND",
          "LB",
          "LCF",
          "NOF",
          "OAL",
          "SFDM",
          "assemblyGaugeLength",
          "shoulder-length"
        ],
        properties: {
          CSP: {
            type: "boolean"
          },
          DC: {
            type: "number",
            exclusiveMinimum: 0
          },
          DCX: {
            type: "number"
          },
          LB: {
            type: "number"
          },
          LCF: {
            type: "number"
          },
          NOF: {
            type: "number",
            minimum: 0
          },
          OAL: {
            type: "number"
          },
          SFDM: {
            type: "number"
          },
          "shoulder-diameter": {
            type: "number"
          },
          "shoulder-length": {
            type: "number"
          },
          assemblyGaugeLength: {
            type: "number"
          },
          HAND: {
            type: "boolean"
          }
        }
      },
      "start-values": {
        type: "object",
        required: [
          "presets"
        ],
        additionalProperties: false,
        properties: {
          presets: {
            type: "array",
            minLength: 1,
            items: {
              type: "object",
              required: [
                "guid",
                "material",
                "n",
                "name",
                "tool-coolant",
                "use-feed-per-revolution",
                "v_c"
              ],
              if: {
                properties: {
                  "use-feed-per-revolution": {
                    const: true
                  }
                }
              },
              then: {
                required: [
                  "f_n_retract",
                  "f_n"
                ]
              },
              else: {
                required: [
                  "v_f_retract",
                  "v_f_plunge"
                ]
              },
              properties: {
                name: {
                  type: "string"
                },
                guid: {
                  type: "string"
                },
                expressions: {
                  type: "object",
                  patternProperties: {
                    "^[A-Za-z0-9_]+$": {
                      type: "string"
                    }
                  }
                },
                "tool-coolant": {
                  enum: [
                    "disabled",
                    "suction",
                    "flood",
                    "air",
                    "mist",
                    "tool",
                    "flood tool",
                    "flood mist",
                    "air through tool"
                  ]
                },
                n: {
                  type: "number"
                },
                v_c: {
                  type: "number"
                },
                f_n_retract: {
                  type: "number"
                },
                "use-feed-per-revolution": {
                  type: "boolean"
                },
                v_f_retract: {
                  type: "number"
                },
                f_n: {
                  type: "number"
                },
                v_f_plunge: {
                  type: "number"
                },
                material: {
                  type: "object",
                  required: [
                    "category",
                    "query",
                    "use-hardness"
                  ],
                  properties: {
                    category: {
                      enum: [
                        "all",
                        "metal",
                        "wood",
                        "plastic",
                        "glass",
                        "ceramic",
                        "stone",
                        "fabric"
                      ]
                    },
                    query: {
                      type: "string"
                    },
                    "use-hardness": {
                      type: "boolean"
                    },
                    "minimum-hardness": {
                      type: "number"
                    },
                    "maximum-hardness": {
                      type: "number"
                    }
                  }
                }
              }
            }
          }
        }
      },
      BMC: {
        type: "string",
        enum: [
          "unspecified",
          "hss",
          "ti coated",
          "carbide",
          "ceramics"
        ]
      },
      holder: {
        oneOf: [
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              GRADE: {
                enum: [
                  "Mill Generic",
                  "generic"
                ]
              },
              guid: {
                type: "string",
                pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          },
          {
            type: "object",
            required: [
              "gaugeLength",
              "guid",
              "type",
              "unit"
            ],
            properties: {
              segments: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "height",
                    "lower-diameter",
                    "upper-diameter"
                  ],
                  additionalProperties: false,
                  properties: {
                    height: {
                      type: "number"
                    },
                    "lower-diameter": {
                      type: "number"
                    },
                    "upper-diameter": {
                      type: "number"
                    }
                  }
                }
              },
              type: {
                enum: [
                  "holder"
                ]
              },
              gaugeLength: {
                type: "number"
              },
              guid: {
                type: "string",
                const: ""
              },
              errors: {
                type: "array",
                items: {
                  type: "object",
                  required: [
                    "code"
                  ],
                  additionalProperties: false,
                  properties: {
                    code: {
                      type: "integer",
                      minimum: 1e3,
                      exclusiveMaximum: 5e3
                    },
                    key: {
                      type: "string"
                    },
                    value: {
                      type: "string"
                    }
                  }
                }
              },
              unit: {
                enum: [
                  "millimeters",
                  "inches"
                ]
              },
              vendor: {
                type: "string"
              },
              "product-id": {
                type: "string"
              },
              "product-link": {
                type: "string"
              },
              _document_tool_id: {
                type: "number"
              },
              _document_tool_reference_guid: {
                type: "string"
              }
            }
          }
        ]
      },
      shaft: {
        type: "object",
        required: [
          "segments"
        ],
        additionalProperties: true,
        properties: {
          segments: {
            type: "array",
            items: {
              type: "object",
              required: [
                "height",
                "lower-diameter",
                "upper-diameter"
              ],
              additionalProperties: false,
              properties: {
                height: {
                  type: "number"
                },
                "lower-diameter": {
                  type: "number"
                },
                "upper-diameter": {
                  type: "number"
                }
              }
            }
          },
          type: {
            enum: [
              "shaft"
            ]
          }
        }
      },
      "post-process": {
        type: "object",
        required: [
          "break-control",
          "diameter-offset",
          "length-offset",
          "live",
          "manual-tool-change",
          "number",
          "turret"
        ],
        properties: {
          "break-control": {
            type: "boolean"
          },
          comment: {
            type: "string"
          },
          "diameter-offset": {
            type: "number"
          },
          "length-offset": {
            type: "number"
          },
          live: {
            type: "boolean"
          },
          "manual-tool-change": {
            type: "boolean"
          },
          turret: {
            type: "number",
            minimum: 0
          },
          number: {
            type: "number"
          }
        }
      },
      GRADE: {
        enum: [
          "Mill Generic",
          "generic"
        ]
      },
      guid: {
        type: "string",
        pattern: "(^(\\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\\}{0,1})$)|(<.*>)"
      },
      errors: {
        type: "array",
        items: {
          type: "object",
          required: [
            "code"
          ],
          additionalProperties: false,
          properties: {
            code: {
              type: "integer",
              minimum: 1e3,
              exclusiveMaximum: 5e3
            },
            key: {
              type: "string"
            },
            value: {
              type: "string"
            }
          }
        }
      },
      unit: {
        enum: [
          "millimeters",
          "inches"
        ]
      },
      vendor: {
        type: "string"
      },
      "product-id": {
        type: "string"
      },
      "product-link": {
        type: "string"
      },
      _document_tool_id: {
        type: "number"
      },
      _document_tool_reference_guid: {
        type: "string"
      }
    }
  }
};

// src/fusion/validate_fusion_tool.ts
var typeValidators = {};
var ajv = new Ajv({
  allErrors: true,
  strictTypes: false,
  strictTuples: true,
  strictNumbers: true,
  //strictRequired: true,
  strictSchema: true
});
function getValidatorForType(typeName) {
  const schema = types_schemas_default[typeName];
  if (!schema) {
    return null;
  }
  if (!typeValidators[typeName]) {
    typeValidators[typeName] = ajv.compile(schema);
  }
  return typeValidators[typeName];
}
function validateFusionTool(fusionTool) {
  const validator = getValidatorForType(fusionTool.type);
  if (!validator) {
    return [{ message: "Invalid tool type", key: "type", value: fusionTool.type }];
  }
  validator(fusionTool);
  const errors = [];
  validator.errors?.forEach((error) => {
    const key = error.instancePath || "/";
    const value = JSON.stringify(getValue(fusionTool, error.instancePath || ""));
    errors.push({ message: error.message, key, value });
  });
  return errors;
}
function getValue(obj, pointer) {
  if (!pointer || pointer === "")
    return obj;
  return pointer.split("/").slice(1).reduce((res, key) => res && res[key] !== void 0 ? res[key] : void 0, obj);
}

// src/assets/common.ts
var unitEnumValues2 = getEnumValuesFromMap({
  in: "Inches",
  mm: "Millimeters"
});
var unit_spec = {
  title: "Unit",
  kind: "enum",
  enumValues: unitEnumValues2,
  defaultValueIN: "in",
  defaultValueMM: "mm",
  required: true,
  hidden: true
};

// src/assets/preset_v1.ts
var coolantEnumValues = [
  { value: "disabled", json_value: "disabled", title: "Disabled" },
  { value: "flood", json_value: "flood", title: "Flood" },
  { value: "mist", json_value: "mist", title: "Mist" },
  { value: "tool", json_value: "tool", title: "Through Tool" },
  { value: "air", json_value: "air", title: "Air" },
  {
    value: "airThroughTool",
    json_value: "air through tool",
    title: "Air through tool"
  },
  { value: "suction", json_value: "suction", title: "Suction" },
  { value: "floodMist", json_value: "flood mist", title: "Flood and mist" },
  {
    value: "floodTool",
    json_value: "flood tool",
    title: "Flood and through tool"
  }
];
function expandForTypes2(for_types) {
  return for_types.flatMap((ft) => groups[ft] || ft);
}
function typeIn(...types) {
  return { type: { in: expandForTypes2(types) } };
}
var typeEnumValues = Object.entries(type_names).map(
  ([value, title]) => ({ value, title })
);
var showToolProps = false;
var Preset_V1 = class extends RichType {
  constructor() {
    super(...arguments);
    __publicField(this, "unit");
    __publicField(this, "type");
    __publicField(this, "geometry_cuttingDiameter");
    __publicField(this, "geometry_numberOfFlutes");
    __publicField(this, "name", "Default preset");
    __publicField(this, "guid");
    __publicField(this, "coolant");
    __publicField(this, "useConstantSurfaceSpeed");
    __publicField(this, "spindleSpeed", 5e3);
    __publicField(this, "surfaceSpeed");
    __publicField(this, "rampSpindleSpeed");
    __publicField(this, "feedCutting");
    __publicField(this, "feedPerTooth");
    __publicField(this, "feedEntry");
    __publicField(this, "feedExit");
    __publicField(this, "feedTransition");
    __publicField(this, "feedRamp");
    /*
      feedEntryRel?: number
      static feedEntryRel_spec: FieldSpec = {
        title: 'Lead-In Feed per Revolution',
        kind: 'feedrate',
        defaultValueMM: 1.0,
        defaultValueIN: 0.04,
      }
    
      feedCuttingRel?: number
      static feedCuttingRel_spec: FieldSpec = {
        title: 'Cutting Feed per Revolution',
        kind: 'feedrate_mm',
      }
    
      feedDepositing?: number
      static feedDepositing_spec: FieldSpec = {
        title: 'Depositing Feedrate',
        kind: 'feedrate',
        defaultValueMM: 1000,
      }
    */
    __publicField(this, "feedExitRel");
    __publicField(this, "feedProbeLink");
    __publicField(this, "feedProbeMeasure");
    __publicField(this, "useFeedPerRevolution");
    __publicField(this, "feedPlunge");
    __publicField(this, "feedPerRevolution");
    __publicField(this, "feedRetract");
    __publicField(this, "feedRetractPerRevolution");
    __publicField(this, "useStepdown");
    __publicField(this, "stepdown");
    __publicField(this, "useStepover");
    __publicField(this, "stepover");
    __publicField(this, "useDepthOfCut");
    __publicField(this, "depthOfCut");
  }
};
/////////// context - tool parameters needed in compute functions ////////////
// TODO: Move to __parent
//__parent?: Tool
__publicField(Preset_V1, "unit_spec", { ...unit_spec, hidden: !showToolProps });
__publicField(Preset_V1, "type_spec", {
  title: "Tool Type (not shown)",
  kind: "enum",
  hidden: !showToolProps,
  enumValues: typeEnumValues,
  defaultValue: typeEnumValues[0].value
});
__publicField(Preset_V1, "geometry_cuttingDiameter_spec", {
  title: "Tool Dia (not shown)",
  kind: "length",
  hidden: !showToolProps,
  defaultValueMM: 10,
  defaultValueIN: 0.4
});
__publicField(Preset_V1, "geometry_numberOfFlutes_spec", {
  title: "Tool #flutes (not shown)",
  kind: "integer",
  hidden: !showToolProps,
  defaultValue: 3,
  enabledWhen: typeIn("ToolMilling", "ToolHoleMaking")
});
//////////// preset ////////////
__publicField(Preset_V1, "name_spec", {
  title: "Name",
  kind: "text",
  noLabel: true,
  required: false,
  hidden: false
});
// Tool
__publicField(Preset_V1, "guid_spec", {
  title: "GUID",
  kind: "guid",
  hidden: true
  //required: true,
});
/*Enum*/
__publicField(Preset_V1, "coolant_spec", {
  title: "Coolant",
  kind: "enum",
  enumValues: coolantEnumValues,
  defaultValue: "flood",
  enabledWhen: typeIn("ToolMilling", "ToolHoleMaking", "ToolTurning"),
  required: true
});
//////////// preset_speeds ////////////
__publicField(Preset_V1, "groupSpeed_spec", {
  title: "Speed",
  kind: "group"
});
__publicField(Preset_V1, "useConstantSurfaceSpeed_spec", {
  title: "Use Constant Surface Speed",
  kind: "boolean",
  defaultValue: true,
  enabledWhen: typeIn("turning general", "turning boring", "turning grooving"),
  required: true
});
__publicField(Preset_V1, "spindleSpeed_spec", {
  title: "Spindle Speed",
  kind: "rotation_speed",
  defaultValue: 5e3,
  enabledWhen: typeIn("ToolMilling", "ToolHoleMaking", "turning general", "turning boring", "turning threading"),
  gt: 0,
  required: true
  /*
  // Calculated onChange for surfaceSpeed instead
  dependencies: keys('surfaceSpeed', 'geometry_cuttingDiameter', 'unit'),
  compute: (values: PresetData) => {
    const metric = values?.unit !== 'in'
    const v = values?.surfaceSpeed // m/min or ft/min
    const d = values?.geometry_cuttingDiameter // inches or mm
    return metric
      ? (v * 1000) / (Math.PI * d) // V (m/min), d (mm) → N (rpm)
      : (v * 12) / (Math.PI * d) // V (ft/min), d (in) → N (rpm)
  },
  computeAlways: true,
  */
});
__publicField(Preset_V1, "surfaceSpeed_spec", {
  title: "Surface Speed",
  kind: "surface_speed",
  defaultValue: void 0,
  enabledWhen: typeIn("ToolMilling", "ToolHoleMaking", "turning general", "turning boring", "turning grooving"),
  gt: 0,
  required: false,
  // always calculated
  onChange(values, oldValue, newValue, setFieldValue2) {
    const metric = values?.unit !== "in";
    const v = newValue;
    const d = values?.geometry_cuttingDiameter ?? NaN;
    const spindleSpeed = metric ? v * 1e3 / (Math.PI * d) : v * 12 / (Math.PI * d);
    setFieldValue2("spindleSpeed", spindleSpeed);
  },
  dependencies: keys("spindleSpeed", "geometry_cuttingDiameter", "unit"),
  compute: (values) => {
    const metric = values?.unit !== "in";
    const n = values?.spindleSpeed;
    const d = values?.geometry_cuttingDiameter ?? NaN;
    return metric ? d * Math.PI * n / 1e3 : d * Math.PI * n / 12;
  },
  computeAlways: true
});
__publicField(Preset_V1, "rampSpindleSpeed_spec", {
  title: "Ramp Spindle Speed",
  kind: "rotation_speed",
  defaultValue: void 0,
  enabledWhen: typeIn("ToolMilling"),
  gt: 0,
  required: false,
  dependencies: keys("spindleSpeed"),
  compute: (values) => values?.spindleSpeed
});
//////////// preset_feedrates ////////////
__publicField(Preset_V1, "groupFeedrates_spec", {
  title: "Feedrates",
  kind: "group"
});
__publicField(Preset_V1, "feedCutting_spec", {
  title: "Cutting Feedrate",
  kind: "feedrate",
  defaultValueMM: 1e3,
  defaultValueIN: 40,
  inGroup: true,
  enabledWhen: typeIn(
    "ToolMilling",
    "ToolCutting",
    "ToolDepositing",
    "boring bar",
    "counter bore",
    "center drill",
    "spot drill",
    "counter sink",
    "turning general",
    "turning boring"
  ),
  gt: 0,
  required: true
  /*
  dependencies: keys('feedPerTooth', 'spindleSpeed', 'geometry_numberOfFlutes'),
  compute: (values: PresetData) => {
    const feedPerTooth = values?.feedPerTooth
    const spindleSpeed = values?.spindleSpeed
    const numberOfFlutes = values?.geometry_numberOfFlutes
    return feedPerTooth * spindleSpeed * numberOfFlutes
  },
  */
});
__publicField(Preset_V1, "feedPerTooth_spec", {
  title: "Feed per Tooth",
  kind: "length",
  defaultValue: void 0,
  inGroup: true,
  enabledWhen: typeIn("ToolMilling", "boring bar", "counter bore", "center drill", "spot drill", "counter sink"),
  gt: 0,
  required: false,
  // always calculated
  dependencies: keys("feedCutting", "spindleSpeed", "geometry_numberOfFlutes"),
  compute: (values) => {
    const feedCutting = values?.feedCutting ?? NaN;
    const spindleSpeed = values?.spindleSpeed;
    const numberOfFlutes = values?.geometry_numberOfFlutes ?? NaN;
    return feedCutting / (spindleSpeed * numberOfFlutes);
  },
  computeAlways: true,
  onChange(values, oldValue, newValue, setFieldValue2) {
    const spindleSpeed = values?.spindleSpeed;
    const numberOfFlutes = values?.geometry_numberOfFlutes ?? NaN;
    const feedCutting = newValue * spindleSpeed * numberOfFlutes;
    setFieldValue2("feedCutting", feedCutting);
  }
});
__publicField(Preset_V1, "feedEntry_spec", {
  title: "Lead-In Feedrate",
  kind: "feedrate",
  defaultValueMM: 1e3,
  defaultValueIN: 40,
  enabledWhen: typeIn(
    "ToolMilling",
    "ToolCutting",
    "ToolDepositing",
    "boring bar",
    "counter bore",
    "center drill",
    "spot drill",
    "counter sink",
    "turning general",
    "turning boring",
    "probe"
  ),
  gt: 0,
  required: true,
  dependencies: keys("feedCutting"),
  compute: (values) => values?.feedCutting
});
__publicField(Preset_V1, "feedExit_spec", {
  title: "Lead-Out Feedrate",
  kind: "feedrate",
  defaultValueMM: 1e3,
  defaultValueIN: 40,
  enabledWhen: typeIn(
    "ToolMilling",
    "ToolCutting",
    "ToolDepositing",
    "boring bar",
    "counter bore",
    "center drill",
    "spot drill",
    "counter sink",
    "turning general",
    "turning boring"
  ),
  gt: 0,
  required: true,
  dependencies: keys("feedCutting"),
  compute: (values) => values?.feedCutting
});
__publicField(Preset_V1, "feedTransition_spec", {
  title: "Transition Feedrate",
  kind: "feedrate",
  defaultValueMM: 1e3,
  defaultValueIN: 40,
  enabledWhen: typeIn("ToolMilling", "boring bar", "counter bore", "center drill", "spot drill", "counter sink"),
  gt: 0,
  required: true,
  dependencies: keys("feedCutting"),
  compute: (values) => values?.feedCutting
});
__publicField(Preset_V1, "feedRamp_spec", {
  title: "Ramp Feedrate",
  kind: "feedrate",
  defaultValueMM: 500,
  defaultValueIN: 20,
  enabledWhen: typeIn("ToolMilling", "boring bar", "counter bore", "center drill", "spot drill", "counter sink"),
  gt: 0,
  required: true,
  dependencies: keys("feedCutting"),
  compute: (values) => values?.feedCutting ?? NaN / 2
});
__publicField(Preset_V1, "feedExitRel_spec", {
  title: "Lead-Out Feed per Revolution",
  kind: "feedrate",
  defaultValueMM: 1,
  defaultValueIN: 0.04,
  enabledWhen: typeIn("turning general", "turning boring", "turning grooving"),
  gt: 0,
  required: true
});
__publicField(Preset_V1, "feedProbeLink_spec", {
  title: "Link Feedrate",
  kind: "feedrate",
  defaultValueMM: 3e3,
  defaultValueIN: 120,
  enabledWhen: typeIn("probe"),
  gt: 0,
  required: true
});
__publicField(Preset_V1, "feedProbeMeasure_spec", {
  title: "Measure Feedrate",
  kind: "feedrate",
  defaultValueMM: 100,
  defaultValueIN: 4,
  enabledWhen: typeIn("probe"),
  gt: 0,
  required: true
});
//////////// preset_feedrates_vertical ////////////
__publicField(Preset_V1, "groupVerticalFeedrates_spec", {
  title: "Vertical Feedrates",
  kind: "group"
});
__publicField(Preset_V1, "useFeedPerRevolution_spec", {
  title: "Use Feed per Revolution",
  kind: "boolean",
  defaultValue: false,
  enabledWhen: typeIn("ToolDrill", "reamer", "counter sink", "turning general", "turning boring", "turning grooving"),
  required: true
});
__publicField(Preset_V1, "feedPlunge_spec", {
  title: "Plunge Feedrate",
  kind: "feedrate",
  defaultValueMM: 500,
  defaultValueIN: 20,
  enabledWhen: typeIn(
    "ToolMilling",
    "ToolDrill",
    "ToolDepositing",
    "boring bar",
    "counter bore",
    "reamer",
    "counter sink"
  ),
  gt: 0,
  required: true
});
__publicField(Preset_V1, "feedPerRevolution_spec", {
  title: "Plunge Feed per Revolution",
  kind: "length",
  defaultValue: void 0,
  enabledWhen: typeIn(
    "ToolMilling",
    "ToolDrill",
    "boring bar",
    "counter bore",
    "reamer",
    "counter sink",
    "turning general",
    "turning boring",
    "turning grooving"
  ),
  gt: 0,
  required: false,
  computeAlways: true,
  dependencies: keys("spindleSpeed", "feedPlunge"),
  compute(values) {
    return values?.feedPlunge ?? NaN / values?.spindleSpeed;
  },
  onChange(values, oldValue, newValue, setFieldValue2) {
    setFieldValue2("feedPlunge", newValue * values?.spindleSpeed);
  }
});
__publicField(Preset_V1, "feedRetract_spec", {
  title: "Retract Feedrate",
  kind: "feedrate",
  defaultValueMM: 1500,
  defaultValueIN: 60,
  enabledWhen: typeIn("ToolDrill", "reamer", "counter sink"),
  gt: 0,
  required: true
});
__publicField(Preset_V1, "feedRetractPerRevolution_spec", {
  title: "Retract Feed per Revolution",
  kind: "length",
  defaultValueMM: 1,
  defaultValueIN: 0.04,
  enabledWhen: typeIn("ToolDrill", "reamer", "counter sink"),
  gt: 0,
  required: true
});
//////////// preset_capabilities ////////////
__publicField(Preset_V1, "groupCapabilities_spec", {
  title: "Passes and Linking",
  kind: "group"
});
__publicField(Preset_V1, "useStepdown_spec", {
  title: "Use Stepdown",
  kind: "boolean",
  defaultValue: false,
  enabledWhen: typeIn("ToolMilling"),
  required: true
});
__publicField(Preset_V1, "stepdown_spec", {
  title: "Stepdown",
  kind: "length",
  defaultValueMM: 6,
  defaultValueIN: 0.5,
  //enabledWhen: typeIn('ToolMilling'),
  enabledWhen: { useStepdown: { eq: true } },
  gt: 0,
  required: true
});
__publicField(Preset_V1, "useStepover_spec", {
  title: "Use Stepover",
  kind: "boolean",
  defaultValue: false,
  enabledWhen: typeIn("ToolMilling", "ToolDepositing"),
  required: true
});
__publicField(Preset_V1, "stepover_spec", {
  title: "Stepover",
  kind: "length",
  defaultValueMM: 1,
  defaultValueIN: 0.04,
  //enabledWhen: typeIn('ToolMilling', 'ToolDepositing'),
  enabledWhen: { useStepover: { eq: true } },
  gt: 0,
  required: true
});
__publicField(Preset_V1, "useDepthOfCut_spec", {
  title: "Use Depth of Cut",
  kind: "boolean",
  defaultValue: false,
  enabledWhen: typeIn("turning general", "turning boring"),
  required: true
});
__publicField(Preset_V1, "depthOfCut_spec", {
  title: "Depth of Cut",
  kind: "length",
  defaultValueMM: 1,
  defaultValueIN: 0.04,
  enabledWhen: typeIn("turning general", "turning boring"),
  gt: 0,
  required: true
});
__publicField(Preset_V1, "version", 1);
function keys(...args) {
  return args;
}

// src/assets/preset.ts
var FinePurpose = /* @__PURE__ */ ((FinePurpose2) => {
  FinePurpose2[FinePurpose2["Adaptive_Rough"] = 0] = "Adaptive_Rough";
  FinePurpose2[FinePurpose2["Wall_Finish"] = 1] = "Wall_Finish";
  FinePurpose2[FinePurpose2["Face_Rough"] = 2] = "Face_Rough";
  FinePurpose2[FinePurpose2["Chamfer"] = 3] = "Chamfer";
  FinePurpose2[FinePurpose2["Keyseat"] = 4] = "Keyseat";
  FinePurpose2[FinePurpose2["Floor_Finish"] = 5] = "Floor_Finish";
  FinePurpose2[FinePurpose2["Surface"] = 6] = "Surface";
  FinePurpose2[FinePurpose2["Face_Finish"] = 7] = "Face_Finish";
  FinePurpose2[FinePurpose2["Slot"] = 8] = "Slot";
  FinePurpose2[FinePurpose2["Traditional_Rough"] = 9] = "Traditional_Rough";
  FinePurpose2[FinePurpose2["Bore_Rough"] = 10] = "Bore_Rough";
  FinePurpose2[FinePurpose2["Bore_Finish"] = 11] = "Bore_Finish";
  FinePurpose2[FinePurpose2["Drill"] = 12] = "Drill";
  FinePurpose2[FinePurpose2["Tap"] = 13] = "Tap";
  return FinePurpose2;
})(FinePurpose || {});
var PresetOrigin = /* @__PURE__ */ ((PresetOrigin2) => {
  PresetOrigin2[PresetOrigin2["FromOpF3d"] = 0] = "FromOpF3d";
  PresetOrigin2[PresetOrigin2["FromOpF3dDerived"] = 1] = "FromOpF3dDerived";
  PresetOrigin2[PresetOrigin2["GeneratedByToolpath"] = 2] = "GeneratedByToolpath";
  PresetOrigin2[PresetOrigin2["Unknown"] = 3] = "Unknown";
  return PresetOrigin2;
})(PresetOrigin || {});
var Preset = class extends Preset_V1 {
  constructor() {
    super(...arguments);
    // If available, this preset came from a (Fusion) operation, and these are all the parameters
    __publicField(this, "operationParameters");
    // if this preset belongs to a fusion op, this is the strategy used
    __publicField(this, "operationStrategy");
    // we know that the preset is suitable for these purposes, because it was used in a fusion op
    __publicField(this, "knownPurposes");
    __publicField(this, "origin", 3 /* Unknown */);
  }
};

// src/assets/tool-library.ts
var ToolLibrary = class {
  constructor() {
    __publicField(this, "assetData");
    __publicField(this, "tools", []);
  }
};

// src/magic/dedup.ts
var Lens = class {
  // string for property names, number for array indices
  constructor(components) {
    __publicField(this, "components");
    this.components = components;
  }
  get(obj) {
    for (const c of this.components) {
      obj = obj[c];
    }
    return obj;
  }
  prettyString() {
    let pieces = [];
    pieces.push("_");
    for (const c of this.components) {
      if (typeof c === "string") {
        pieces.push(`.${c}`);
      } else if (typeof c === "number") {
        pieces.push(`[${c}]`);
      } else {
      }
    }
    return pieces.join("");
  }
};
function findFirstDiff(a, b, ignore_tree) {
  let rev = _findFirstDiff(a, b, ignore_tree);
  if (rev == void 0) {
    return void 0;
  }
  return new Lens(rev.reverse());
}
function _findFirstDiff(a, b, ignore_tree) {
  if (a === b)
    return void 0;
  if (a == null || b == null) {
    return void 0;
  }
  if (typeof a !== typeof b) {
    return [];
  }
  if (a instanceof Date && b instanceof Date) {
    if (a.getTime() === b.getTime()) {
      return void 0;
    } else {
      return [];
    }
  }
  if (a instanceof RegExp && b instanceof RegExp) {
    if (a.toString() === b.toString()) {
      return void 0;
    } else {
      return [];
    }
  }
  if (typeof a === "number" && typeof b === "number") {
    if (Number.isNaN(a) && Number.isNaN(b)) {
      return void 0;
    }
  }
  if (typeof a !== "object") {
    return [];
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length > b.length) {
      return [a.length - 1];
    } else if (a.length < b.length) {
      return [b.length - 1];
    }
    for (let i = 0; i < a.length; i++) {
      const diff = _findFirstDiff(a[i], b[i], ignore_tree);
      if (diff != void 0) {
        diff.push(i);
        return diff;
      }
    }
    return void 0;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return [];
  }
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  for (const keys2 of [keysA, keysB]) {
    for (const key of keys2) {
      if (ignore_tree[key] === null) {
        continue;
      } else {
        let child_tree = ignore_tree[key];
        if (child_tree === void 0) {
          child_tree = {};
        }
        let diff = _findFirstDiff(a[key], b[key], child_tree);
        if (diff != void 0) {
          diff.push(key);
          return diff;
        }
      }
    }
  }
  return void 0;
}
function isToolDup(tool1, tool2) {
  if (tool1.description !== tool2.description) {
    return false;
  }
  if (tool1.type !== tool2.type) {
    return false;
  }
  const ignore_tree = {
    presets: null,
    // post: null,
    guid: null,
    fusion: {
      last_modified: null
    }
  };
  let lens = findFirstDiff(tool1, tool2, ignore_tree);
  if (lens != void 0) {
  }
  return lens === void 0;
}
function groupDupTools(tools) {
  let ret = /* @__PURE__ */ new Map();
  for (const tool of tools) {
    let seen = false;
    for (const [tool_repr, ts] of ret) {
      if (isToolDup(tool, tool_repr)) {
        ts.push(tool);
        seen = true;
        break;
      }
    }
    if (!seen) {
      ret.set(tool, [tool]);
    }
  }
  return ret;
}
function isApproxOrUndef(a, b) {
  if (a !== void 0 && b !== void 0) {
    if (isNaN(a) && isNaN(b)) {
      return true;
    } else {
      return isApprox(a, b);
    }
  } else {
    return true;
  }
}
function findFirstDiffPresets(preset1, preset2) {
  for (const property of [
    "spindleSpeed",
    "rampSpindleSpeed",
    "feedCutting",
    "feedEntry",
    "feedExit",
    "feedTransition",
    "feedRamp",
    "feedProbeLink",
    "feedProbeMeasure",
    "feedPlunge",
    "feedRetract",
    "useStepdown",
    "stepdown",
    "useStepover",
    "stepover",
    "useDepthOfCut",
    "depthOfCut"
  ]) {
    const lens = new Lens([property]);
    if (!isApproxOrUndef(lens.get(preset1), lens.get(preset2))) {
      return lens;
    }
  }
  return void 0;
}
function isDupPreset(preset1, preset2) {
  const lens = findFirstDiffPresets(preset1, preset2);
  return lens === void 0;
}
function createDupGroups(isSame, items) {
  let ret = [];
  for (const item of items) {
    let need_new_group = true;
    for (let group of ret) {
      if (isSame(item, group[0])) {
        group.push(item);
        need_new_group = false;
        break;
      }
    }
    if (need_new_group) {
      ret.push([item]);
    }
  }
  return ret;
}
function pickPresetAmongDups(tool, candidates) {
  if (candidates.length === 0) {
    throw Error("No candidate presets to pick from");
  }
  if (candidates.length === 1) {
    return candidates[0];
  }
  let purposes = candidates.reduce((acc, preset) => {
    if (preset.knownPurposes == void 0) {
      return acc;
    } else {
      return [...acc, ...preset.knownPurposes];
    }
  }, []);
  purposes = dedup((p1, p2) => p1 === p2, purposes);
  const candidates_non_default = candidates.filter((p) => !isPresetDefault(tool, p));
  if (candidates_non_default.length > 0) {
    candidates = candidates_non_default;
  }
  const candidates_not_generated = candidates.filter((p) => !isPresetTpGenerated(p));
  if (candidates_not_generated.length > 0) {
    candidates = candidates_not_generated;
  }
  const candidates_not_from_op = candidates.filter((p) => !isPresetFromOpF3d(p));
  if (candidates_not_from_op.length > 0) {
    candidates = candidates_not_from_op;
  }
  for (let fieldname of [
    "operationStrategy",
    "operationParameters",
    "spindleSpeed",
    "rampSpindleSpeed",
    "feedCutting",
    "feedEntry",
    "feedExit",
    "feedTransition",
    "feedRamp",
    "feedProbeLink",
    "feedProbeMeasure",
    "feedPlunge",
    "feedRetract",
    "useStepdown",
    "stepdown",
    "useStepover",
    "stepover",
    "useDepthOfCut",
    "depthOfCut"
  ]) {
    let candidates_with_field = candidates.filter((p) => p[fieldname] != void 0);
    if (candidates_with_field.length > 0) {
      candidates = candidates_with_field;
      if (candidates.length === 1) {
        break;
      }
    }
  }
  let ret = structuredClone(candidates[0]);
  ret.knownPurposes = purposes;
  return ret;
}
function dedup(issame, items) {
  return createDupGroups(issame, items).map((group) => group[0]);
}
function dedupPresets(tool, presets) {
  const groups2 = createDupGroups(isDupPreset, presets);
  let ret = [];
  for (const group of groups2) {
    ret.push(pickPresetAmongDups(tool, group));
  }
  return ret;
}
function dedupTools(tools) {
  let ret = [];
  const group_dict = groupDupTools(tools);
  for (const [tool_repr, ts] of group_dict) {
    const allPresets = ts.reduce((acc, tool2) => {
      return acc.concat(tool2.presets);
    }, []);
    const presets = dedupPresets(tool_repr, allPresets);
    let tool = structuredClone(tool_repr);
    tool.presets = presets;
    ret.push(tool);
  }
  return ret;
}

// src/util/sort-by-dependencies.ts
function sortByDependencies(items) {
  const adj = /* @__PURE__ */ new Map();
  const indegree = /* @__PURE__ */ new Map();
  items.forEach(({ id }) => {
    adj.set(id, /* @__PURE__ */ new Set());
    indegree.set(id, 0);
  });
  items.forEach(({ id, dependencies }) => {
    dependencies?.forEach((dep) => {
      if (!adj.has(dep)) {
        throw new Error(`Item '${id}' depends on unknown id '${dep}'.`);
      }
      const neighbors = adj.get(dep);
      if (!neighbors.has(id)) {
        neighbors.add(id);
        indegree.set(id, indegree.get(id) + 1);
      }
    });
  });
  const queue = [];
  indegree.forEach((deg, id) => {
    if (deg === 0)
      queue.push(id);
  });
  const sortedIds = [];
  while (queue.length > 0) {
    const current = queue.shift();
    sortedIds.push(current);
    adj.get(current).forEach((neighbor) => {
      const newDeg = indegree.get(neighbor) - 1;
      indegree.set(neighbor, newDeg);
      if (newDeg === 0)
        queue.push(neighbor);
    });
  }
  if (sortedIds.length !== items.length) {
    const cycleIds = items.filter(({ id }) => (indegree.get(id) ?? 0) > 0).map(({ id }) => id);
    throw new Error(`Circular dependency detected among: ${cycleIds.join(", ")}`);
  }
  const idToItem = new Map(items.map((item) => [item.id, item]));
  return sortedIds.map((id) => idToItem.get(id));
}
export {
  BALL_ENDMILL,
  BULL_NOSE_ENDMILL,
  BilinearInterpolate,
  CENTER_DRILL,
  CHAMFER_MILL,
  COUNTER_SINK,
  CoarsePurpose,
  DRILL,
  DRILL_LIKE,
  ENDMILL_LIKE,
  EPSILON,
  FACE_MILL,
  FLAT_ENDMILL,
  FinePurpose,
  GaussianProcess,
  GrayPaulModelConst,
  GrayPaulModelExcel,
  GrayPaulModelExcels_from_datasheets,
  GrayPaulModelGaussian,
  KEYSEAT_CUTTER,
  KindDefinitions,
  Lens,
  MagicOptions,
  OtherMills,
  OtherTypes,
  Pass,
  Preset,
  PresetOrigin,
  Purpose,
  RADIUS_MILL,
  Report,
  RichType,
  SPOT_DRILL,
  TAP_LEFT,
  TAP_LIKE,
  TAP_RIGHT,
  ToolLibrary,
  applyResidual,
  buildUsedByMap,
  build_ARRAY_DATA,
  calcResidual,
  calcResidualDetailed,
  collectAdvancedPresets,
  collectGrayPaulPointsFromTools,
  convertToUnitSystem,
  createPreexistingPresetDict,
  dedupTools,
  defaultHolder,
  default_datasheets,
  diffCSV,
  drillFeedPerRev,
  drillSFM,
  drill_gauge,
  filterDefaultToolsOnly,
  filterEnabledFieldSpecs,
  filterFieldsForTypes,
  findFirstDiff,
  findFirstDiffPresets,
  fixUpPreset,
  flattenObject,
  fraction_string,
  from_inches,
  fusion_all_preset_fields,
  fusion_all_tool_fields,
  fusion_base_fields,
  fusion_csv_input,
  fusion_csv_output,
  fusion_f3d_input,
  fusion_field_by_csv_id,
  fusion_field_by_hsm_id,
  fusion_field_by_hsm_key,
  fusion_field_by_id,
  fusion_field_by_json_id,
  fusion_fields,
  fusion_fields_nested_by_id,
  fusion_hsm_input,
  fusion_hsm_input_streaming,
  fusion_json_input,
  fusion_json_input_single,
  fusion_json_input_single_preset,
  fusion_json_output,
  fusion_json_output_library,
  fusion_json_output_single,
  fusion_library_version,
  fusion_other_fields,
  fusion_preset_fields,
  fusion_preset_other_fields,
  fusion_preset_process_fields,
  fusion_read_f3d_irondocs,
  fusion_tool_fields,
  fusion_turning_holder_fields,
  fusion_turning_insert_fields,
  fusion_turning_setup_fields,
  getAspectRatio,
  getDataSheet,
  getDefaultConversionFactor,
  getDefaultUnit,
  getDefaultValue,
  getDefaults,
  getEnumTitle,
  getEnumValuesFromMap,
  getExtension,
  getFieldsByKeyForType,
  getKindAlign,
  getKindDataType,
  getNestedValue,
  getObjectPaths,
  getUnitConversionFactor,
  getUnitOptions,
  getUnitScaleFactor,
  getValidFieldsForType,
  groupDupTools,
  groupFieldSpecs,
  groups,
  interp_tool_data,
  isApprox,
  isFieldEnabled,
  isKindNumeric,
  isPresetDefault,
  isPresetFromOpF3d,
  isPresetTpGenerated,
  isReducedShank,
  isToolDup,
  is_chamfer_tool_type,
  is_drill_tool_type,
  is_face_mill_tool_type,
  is_keyseat_tool_type,
  is_spot_tool_type,
  is_tap_tool_type,
  joinCSV,
  kindEqual,
  magic_tools_filter,
  main_groups,
  makeFieldSpecMap,
  makeGPMGaussianHyperparams,
  make_magic_filtered_presets,
  make_magic_presets,
  make_magic_presets_tool,
  nestByKey,
  parseCSV,
  parseJsonInput,
  parseParamFloat,
  prepareAssetData,
  replaceExtension,
  roundSignificant,
  scaleNumbers,
  scale_numbers,
  screw_thread_number,
  setNestedValue,
  setNestedValueImmutable,
  shaftReach,
  sortByDependencies,
  sortObjectKeys,
  splitCSV,
  stringFromFinePurpose,
  stringFromPurpose,
  stripExtension,
  tapSFM,
  toGrayPaulPoint,
  to_inches,
  toolDescription,
  tryClassifyPass,
  tryClassifyPreset,
  tryParseParamBoolean,
  tryParseParamFloat,
  type_names,
  uconvert,
  unflattenObject,
  unitEnumValues,
  validateFusionTool,
  validate_preset_parameters,
  validate_tool_parameters,
  validate_tools_parameters
};
//# sourceMappingURL=index.js.map